import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './AdminDashboard.css';
import { Form, Input, Button, Steps, ConfigProvider, Select, AutoComplete, Checkbox, message, notification, Modal, Image } from 'antd';
import {
    UserOutlined, TagOutlined, ShopOutlined,
    DashboardOutlined, BgColorsOutlined, ArrowRightOutlined, ArrowLeftOutlined,
    PlusOutlined, EditOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
    OrderedListOutlined, AppstoreOutlined, VideoCameraOutlined, TeamOutlined, MessageOutlined,
    ToolOutlined, ApiOutlined, InboxOutlined, AuditOutlined, BarChartOutlined, CheckCircleOutlined, PrinterOutlined
} from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import racquetModels from '../data/racquetModels';
import { filterOrders, paginateOrders, ORDER_PAGE_SIZE } from '../utils/adminOrderTable.mjs';
import ItemsTab from './admin/ItemsTab';
import CustomersTab from './admin/CustomersTab';
import InventoryTab from './admin/InventoryTab';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
import takumiLogo from '../../../images/logo_remove_background.png';

const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

const ORDER_STATUSES = ['Pending to String', 'Ready to Collect', 'Collected'];
const RACQUET_BRANDS = ['Yonex', 'Victor', 'Li-Ning', 'Felet', 'Toalson', 'Others'];

// Preset brand buttons for the New String Order wizard's Racquet Details step.
// Logo files are supplied separately under client/public/brand-logos/.
const RACQUET_BRAND_LOGOS = [
    { name: 'Yonex', logo: '/brand-logos/yonex.jpeg' },
    { name: 'Li-Ning', logo: '/brand-logos/lining.jpeg' },
    { name: 'Victor', logo: '/brand-logos/victor.jpeg' },
    { name: 'Felet', logo: '/brand-logos/felet.jpeg' },
    { name: 'Mizuno', logo: '/brand-logos/mizuno.jpeg' },
    { name: 'Toalson', logo: '/brand-logos/toalson.jpeg' },
    { name: 'Kumpoo', logo: '/brand-logos/kumpoo.jpeg' },
];

const STATUS_EMOJI = {
    'Pending to String': '⏳',
    'Ready to Collect': '✅',
    'Collected': '📦',
};

// An order row can be one-tap marked "Collected" once it's ready, paid, and all
// the stringing details required for "Ready to Collect" (see handleUpdateSubmit's
// statusRequiresFull check) are actually filled in.
const canMarkOrderCollected = (o) => (
    o.status === 'Ready to Collect' &&
    !!o.paid &&
    !!String(o.tension || '').trim() &&
    !!String(o.string_pattern || '').trim() &&
    !!String(o.stringer_name || '').trim() &&
    !!String(o.dt || '').trim() &&
    !!String(o.string_machine || '').trim() &&
    (!!o.bring_own_string || !!String(o.string_brand || '').trim())
);

const buildWhatsAppUrl = (groupOrders) => {
    const o = groupOrders[0];
    const phone = (o.contact_number || '').replace(/\D/g, '');
    const collectionDate = o.collection_date
        ? new Date(o.collection_date).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
    const multi = groupOrders.length > 1;

    const racquetLines = groupOrders.flatMap((ord, i) => {
        const statusEmoji = STATUS_EMOJI[ord.status] || '📋';
        const racquet = [ord.racquet_brand, ord.racquet_model].filter(Boolean).join(' ');
        const mainString = [ord.string_brand, ord.string_model].filter(Boolean).join(' ');
        const stringLine = ord.cross_string_brand ? `${mainString} (mains) / ${ord.cross_string_brand} (crosses)` : mainString;
        const tensionStr = ord.tension ? `${ord.tension}/${ord.cross_tension || ord.tension} lbs` : null;
        return [
            multi ? `🏸 Racquet ${i + 1}: ${racquet || '—'}` : `🏸 Racquet: ${racquet || '—'}`,
            stringLine ? `🪡 String: ${stringLine}${tensionStr ? ` | 💪 ${tensionStr}` : ''}` : tensionStr ? `💪 Tension: ${tensionStr}` : null,
            `*${statusEmoji} Status: ${ord.status}*`,
            '',
        ].filter(l => l !== null);
    });

    const lines = [
        `Hi ${o.customer_name} 👋`,
        '',
        multi ? `Here's an update on your stringing orders:` : `Here's an update on your stringing order:`,
        '',
        `📋 Order Ref: ${o.order_group_ref || o.order_ref}`,
        '',
        ...racquetLines,
        collectionDate ? `📅 Collection Date: ${collectionDate}` : null,
        '',
        `Thank you for choosing Takumi Tension! 🏸`,
    ].filter(l => l !== null).join('\n');

    return `https://wa.me/${phone}?text=${encodeURIComponent(lines)}`;
};

const ANALYTICS_FIELDS = [
    { key: 'stringer', label: 'Stringer', dbKey: 'stringer_name' },
    { key: 'machine', label: 'Machine', dbKey: 'string_machine' },
    { key: 'stringItem', label: 'String Item', dbKey: 'string_brand', multiSelect: true },
    { key: 'stringPattern', label: 'String Pattern', dbKey: 'string_pattern' },
    { key: 'tension', label: 'Tension', dbKey: 'tension' },
    { key: 'racquetModel', label: 'Racquet Model', dbKey: 'racquet_model' },
    { key: 'preStretch', label: 'Pre-Stretch', dbKey: 'pre_stretch' },
];
const ANALYTICS_INIT_CHECKED = Object.fromEntries(ANALYTICS_FIELDS.map(f => [f.key, false]));
const ANALYTICS_INIT_VALUES = Object.fromEntries(ANALYTICS_FIELDS.map(f => [f.key, f.multiSelect ? [] : '']));

const emptyRacquet = () => ({
    racquetBrandSelect: 'Yonex', racquetBrandOther: '',
    racquetModel: '', racquetModelOther: '',
    collapsed: false,
    expressService: false,
    string: { zohoItemId: '', zohoItemName: '', tension: '', crossTension: '', preStretch: 'None', stringPattern: 'Haribito 2 knots', bringOwnString: false, ownStringType: '', hybrid: false, crossZohoItemId: '', crossZohoItemName: '' },
});

// Price of stringing one racquet, from the item list: the string's own rate
// (which includes stringing), the average of the two rates for 2 different
// strings, or the "Stringing Service only" rate for bring-own-string. The
// server charges the same (racquetPriceFromItems in server/routes/admin.js).
const STRINGING_SERVICE_ITEM = 'Stringing Service only';
const orderRacquetPrice = (str, items) => {
    const rateOf = (it) => (it && it.rate != null ? Number(it.rate) : null);
    const byId = (id) => rateOf((items || []).find(it => it.item_id === id));
    if (str.bringOwnString) {
        const service = (items || []).find(it => (it.name || '').trim() === STRINGING_SERVICE_ITEM);
        return { price: rateOf(service) ?? 0, missing: rateOf(service) == null, label: STRINGING_SERVICE_ITEM };
    }
    const main = byId(str.zohoItemId);
    if (!isHybridString(str)) return { price: main ?? 0, missing: main == null, label: str.zohoItemName };
    const cross = byId(str.crossZohoItemId);
    return {
        price: Math.round((((main ?? 0) + (cross ?? 0)) / 2) * 100) / 100,
        missing: main == null || cross == null,
        label: `${str.zohoItemName} / ${str.crossZohoItemName} (average)`,
    };
};

// A racquet strung with 2 different strings (mains / crosses) uses half a set of each.
const isHybridString = (str) => !str.bringOwnString && !!str.hybrid && !!str.crossZohoItemId;
const stringSelectionLabel = (str) => {
    if (str.bringOwnString) return str.ownStringType ? `Own string: ${str.ownStringType}` : 'Bring own string';
    return isHybridString(str) ? `${str.zohoItemName} / ${str.crossZohoItemName}` : str.zohoItemName;
};

const emptyOrderForm = {
    custName: '', phone: '65',
    racquets: [emptyRacquet()],
    expectedCollectionDate: '',
    discount: '', paid: false
};

const emptyVideoForm = {
    title: '', embedUrl: '', description: '', category: 'guide',
    date: '', enabled: true
};

const emptyStringForm = {
    brand: '', model: '', type: '', description: '',
    price: '', gauge: '', coreMaterial: '', outerMaterial: '', coating: '', images: [],
    racquet_type: 'Badminton', colorGroupsText: ''
};

const emptyProductForm = {
    name: '', brand: '', category: 'accessories', description: '',
    price: '', images: [], inStock: true, specifications: '', rentalFee: '', condition: '', costing: '',
    swingWeight: '', weightType: '', weight: '', balancePoint: '', grommetSystem: '',
    outForRent: false, stringType: '', gauge: ''
};

const ADMIN_NAV_ITEMS = [
    { key: 'orders', label: 'String Order', icon: <OrderedListOutlined /> },
    { key: 'customers', label: 'Customer Info', icon: <TeamOutlined /> },
    { key: 'invoicing', label: 'Invoicing', icon: <AuditOutlined /> },
    { key: 'items', label: 'Items', icon: <TagOutlined /> },
    { key: 'inventory', label: 'Inventory', icon: <InboxOutlined /> },
    { key: 'products', label: 'Shop Products', icon: <ShopOutlined /> },
    { key: 'stringers', label: 'Static Info', icon: <ToolOutlined /> },
    { key: 'statistics', label: 'Statistics', icon: <BarChartOutlined /> },
    { key: 'strings', label: 'String Brands', icon: <AppstoreOutlined /> },
    { key: 'videos', label: 'TikTok Video', icon: <VideoCameraOutlined /> },
    { key: 'zoho', label: 'Zoho Integration', icon: <ApiOutlined /> },

];

const parseColorGroupsInput = (value) => {
    if (!value) return [];

    return Array.from(
        new Map(
            value
                .split(/[\n,]+/)
                .map(item => item.trim())
                .filter(Boolean)
                .map(item => [item.toLowerCase(), item])
        ).values()
    );
};

const readColorGroups = (record) => {
    if (Array.isArray(record?.colorGroups)) return record.colorGroups;
    if (!record?.color_groups) return [];

    try {
        const parsed = JSON.parse(record.color_groups);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return parseColorGroupsInput(record.color_groups);
    }
};

const getModelsByBrand = (strings, brand) =>
    strings
        .filter(item => item.brand === brand)
        .map(item => item.model)
        .filter((model, index, arr) => model && arr.indexOf(model) === index);

const getColorOptions = (strings, brand, model) => {
    const match = strings.find(item => item.brand === brand && item.model === model);
    return readColorGroups(match);
};

const defaultInvoiceDateFrom = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2);
    d.setDate(1);
    return d.toISOString().slice(0, 10);
};
const defaultInvoiceDateTo = () => new Date().toISOString().slice(0, 10);
const todayDateStr = () => new Date().toISOString().slice(0, 10);

const SHOP_DETAILS = {
    name: 'Takumi Tension',
    address: '60 Eu Tong Sen St, #01-17 Furama City Center, Singapore 059804',
    phone: '+65 9068 8780',
    email: 'takumitension@gmail.com',
};

const PAYMENT_METHOD_LABELS = { cash: 'Cash', paynow: 'PayNow', card: 'Card', bank_transfer: 'Bank Transfer', other: 'Other' };

const invoiceStatusLabel = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'sent' || s === 'draft') return 'unpaid';
    return s.replace(/_/g, ' ') || '—';
};

// The invoice's discount is its own negative line named "Discount" (not linked to an order).
const isDiscountLine = (li) => !li.order_id && li.name === 'Discount' && Number(li.item_total) < 0;
const invoiceDiscount = (inv) => (inv.line_items || []).filter(isDiscountLine).reduce((sum, li) => sum - Number(li.item_total), 0);

// Printable invoice (the browser's print dialog can also "Save as PDF").
const buildInvoiceHtml = (inv, customerContact) => {
    const money = (v) => `SGD ${Number(v || 0).toFixed(2)}`;
    const total = Number(inv.total || 0);
    const balance = Number(inv.balance || 0);
    const rows = (inv.line_items || []).map(li => `
        <tr>
          <td><div class="item">${escapeHtml(li.name || li.description)}</div>${li.description && li.description !== li.name ? `<div class="muted">${escapeHtml(li.description)}</div>` : ''}</td>
          <td class="num">${Number(li.quantity || 0)}</td>
          <td class="num">${Number(li.rate || 0).toFixed(2)}</td>
          <td class="num">${Number(li.item_total || 0).toFixed(2)}</td>
        </tr>`).join('');
    const payments = (inv.payments || []).filter(p => Number(p.amount) !== 0).map(p => `
        <tr><td>${escapeHtml(p.payment_date)}</td><td>${escapeHtml(PAYMENT_METHOD_LABELS[p.method] || (Number(p.amount) < 0 ? 'Reversal' : 'Payment'))}${p.note ? ` <span class="muted">· ${escapeHtml(p.note)}</span>` : ''}</td><td class="num">${Number(p.amount).toFixed(2)}</td></tr>`).join('');
    const isVoid = (inv.status || '').toLowerCase() === 'void';
    return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(inv.invoice_number)} - ${escapeHtml(SHOP_DETAILS.name)}</title>
<style>
  * { box-sizing: border-box; } body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #222; margin: 0; padding: 32px; font-size: 13px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #c41e3a; padding-bottom: 16px; margin-bottom: 20px; }
  .shop h1 { margin: 0 0 4px; font-size: 22px; color: #c41e3a; } .shop div { color: #555; line-height: 1.5; }
  .title { text-align: right; } .title h2 { margin: 0 0 6px; font-size: 26px; letter-spacing: 2px; } .title div { line-height: 1.6; }
  .stamp { display: inline-block; margin-top: 6px; padding: 2px 10px; border: 2px solid #999; color: #999; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; }
  .stamp.paid { border-color: #28a745; color: #28a745; } .stamp.void { border-color: #c41e3a; color: #c41e3a; }
  .billto { margin-bottom: 20px; } .label { font-size: 11px; text-transform: uppercase; color: #888; letter-spacing: 1px; margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; } th { text-align: left; font-size: 11px; text-transform: uppercase; color: #666; border-bottom: 1px solid #ccc; padding: 8px 6px; }
  td { padding: 8px 6px; border-bottom: 1px solid #eee; vertical-align: top; } .num { text-align: right; white-space: nowrap; } .item { font-weight: 600; } .muted { color: #888; font-size: 12px; }
  .totals { margin-left: auto; width: 280px; margin-top: 12px; } .totals div { display: flex; justify-content: space-between; padding: 4px 6px; }
  .totals .due { border-top: 2px solid #222; margin-top: 4px; padding-top: 8px; font-size: 16px; font-weight: 700; }
  h3 { font-size: 13px; margin: 28px 0 6px; } .foot { margin-top: 36px; text-align: center; color: #888; }
  @media print { body { padding: 0; } @page { margin: 16mm; } }
</style></head><body>
  <div class="head">
    <div class="shop"><h1>${escapeHtml(SHOP_DETAILS.name)}</h1><div>${escapeHtml(SHOP_DETAILS.address)}</div><div>${escapeHtml(SHOP_DETAILS.phone)} · ${escapeHtml(SHOP_DETAILS.email)}</div></div>
    <div class="title"><h2>INVOICE</h2>
      <div><strong>${escapeHtml(inv.invoice_number)}</strong></div>
      <div>Date: ${escapeHtml(inv.date || '')}</div>
      ${inv.reference_number ? `<div>Ref: ${escapeHtml(inv.reference_number)}</div>` : ''}
      ${isVoid ? '<div class="stamp void">Void</div>' : balance <= 0 && total > 0 ? '<div class="stamp paid">Paid</div>' : ''}
    </div>
  </div>
  <div class="billto"><div class="label">Bill to</div><div class="item">${escapeHtml(inv.customer_name || '')}</div>${customerContact ? `<div>${escapeHtml(customerContact)}</div>` : ''}</div>
  <table><thead><tr><th>Item</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Amount</th></tr></thead><tbody>${rows || '<tr><td colspan="4" class="muted">No items</td></tr>'}</tbody></table>
  <div class="totals">
    <div><span>Total</span><span>${money(total)}</span></div>
    <div><span>Paid</span><span>${money(total - balance)}</span></div>
    <div class="due"><span>Balance due</span><span>${money(balance)}</span></div>
  </div>
  ${payments ? `<h3>Payments</h3><table><tbody>${payments}</tbody></table>` : ''}
  <div class="foot">Thank you for choosing ${escapeHtml(SHOP_DETAILS.name)}!</div>
  <script>window.onload = function () { window.print(); };</script>
</body></html>`;
};

// Plain-data version of the invoice for the tablet app's thermal printer
// (window.AndroidPrinter.printInvoice). Amounts are pre-formatted; the app only lays them out.
const buildInvoicePrintData = (inv, customerContact) => {
    const amount = (v) => Number(v || 0).toFixed(2);
    const total = Number(inv.total || 0);
    const balance = Number(inv.balance || 0);
    const isVoid = (inv.status || '').toLowerCase() === 'void';
    return {
        shop: SHOP_DETAILS,
        invoice_number: inv.invoice_number || '',
        date: inv.date || '',
        reference_number: inv.reference_number || '',
        stamp: isVoid ? 'Void' : balance <= 0 && total > 0 ? 'Paid' : '',
        customer_name: inv.customer_name || '',
        customer_contact: customerContact || '',
        line_items: (inv.line_items || []).map(li => ({
            name: li.name || li.description || '',
            description: li.description && li.description !== li.name ? li.description : '',
            quantity: Number(li.quantity || 0),
            rate: amount(li.rate),
            amount: amount(li.item_total),
        })),
        total: `SGD ${amount(total)}`,
        paid: `SGD ${amount(total - balance)}`,
        balance: `SGD ${amount(balance)}`,
        payments: (inv.payments || []).filter(p => Number(p.amount) !== 0).map(p => ({
            date: p.payment_date || '',
            method: PAYMENT_METHOD_LABELS[p.method] || (Number(p.amount) < 0 ? 'Reversal' : 'Payment'),
            note: p.note || '',
            amount: amount(p.amount),
        })),
    };
};

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('orders');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Video state
    const [videos, setVideos] = useState([]);
    const [videoLoading, setVideoLoading] = useState(true);
    const [showVideoForm, setShowVideoForm] = useState(false);
    const [editingVideoId, setEditingVideoId] = useState(null);
    const [videoForm, setVideoForm] = useState(emptyVideoForm);

    // String state
    const [strings, setStrings] = useState([]);
    const [stringLoading, setStringLoading] = useState(true);
    const [showStringForm, setShowStringForm] = useState(false);
    const [editingStringId, setEditingStringId] = useState(null);
    const [stringForm, setStringForm] = useState(emptyStringForm);

    // Product state
    const [products, setProducts] = useState([]);
    const [productLoading, setProductLoading] = useState(true);
    const [showProductForm, setShowProductForm] = useState(false);
    const [editingProductId, setEditingProductId] = useState(null);
    const [productForm, setProductForm] = useState(emptyProductForm);

    // New Arrivals state
    const [newArrivalsAdmin, setNewArrivalsAdmin] = useState([]);
    const [newArrivalsLoading, setNewArrivalsLoading] = useState(false);
    const [showArrivalForm, setShowArrivalForm] = useState(false);
    const [editingArrivalId, setEditingArrivalId] = useState(null);
    const [arrivalForm, setArrivalForm] = useState({ name: '', category: 'racquets', label: 'New Arrival', description: '', image: '' });
    const [uploadingArrivalImage, setUploadingArrivalImage] = useState(false);

    // Stringing Orders state
    const [orders, setOrders] = useState([]);
    const [orderLoading, setOrderLoading] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    // New String Order wizard: 'phone' -> ('newCustomer' if not found) -> 'racquet' -> 'confirm'
    const [orderWizardStep, setOrderWizardStep] = useState('phone');
    const [orderPhoneLookupStatus, setOrderPhoneLookupStatus] = useState(null); // null | 'found' | 'not_found'
    const [orderPhoneLookupLoading, setOrderPhoneLookupLoading] = useState(false);
    const [orderCustomerId, setOrderCustomerId] = useState(null);
    const [orderForm, setOrderForm] = useState(emptyOrderForm);
    const [racquetModelsData, setRacquetModelsData] = useState(racquetModels);
    const [orderFilter, setOrderFilter] = useState('All');
    const [orderSearch, setOrderSearch] = useState('');
    const [orderPage, setOrderPage] = useState(1);
    const [orderSort, setOrderSort] = useState({ key: 'collection_date', dir: 'asc' });
    const [orderSaving, setOrderSaving] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({});
    const [updateSaving, setUpdateSaving] = useState(false);
    const [updateModalEditing, setUpdateModalEditing] = useState(true);
    const [updateItemPickerOpen, setUpdateItemPickerOpen] = useState(false);
    const [updateItemPickerSearch, setUpdateItemPickerSearch] = useState('');
    const [updateItemPickerExpandedGroups, setUpdateItemPickerExpandedGroups] = useState(new Set());
    const [orderDetailPhotos, setOrderDetailPhotos] = useState([]);
    const [photoCarouselVisible, setPhotoCarouselVisible] = useState(false);
    const [photoCarouselIndex, setPhotoCarouselIndex] = useState(0);

    // Customer state (full list kept here for the invoice tab's customer picker; the
    // Customer Info tab UI itself lives in its own CustomersTab component)
    const [customers, setCustomers] = useState([]);

    // CSV import state
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvResult, setCsvResult] = useState(null);

    // Stringer state
    const [stringers, setStringers] = useState([]);
    const [stringerLoading, setStringerLoading] = useState(false);
    const [newStringerName, setNewStringerName] = useState('');
    const [stringerSaving, setStringerSaving] = useState(false);
    const [stringerPage, setStringerPage] = useState(1);

    // Stringing machine state
    const [stringMachines, setStringMachines] = useState([]);
    const [machineLoading, setMachineLoading] = useState(false);
    const [newMachineName, setNewMachineName] = useState('');
    const [machineSaving, setMachineSaving] = useState(false);
    const [machinePage, setMachinePage] = useState(1);

    // Stringing pattern state
    const [stringPatterns, setStringPatterns] = useState([]);
    const [patternLoading, setPatternLoading] = useState(false);
    const [newPatternName, setNewPatternName] = useState('');
    const [patternSaving, setPatternSaving] = useState(false);
    const [patternPage, setPatternPage] = useState(1);

    // Telegram state
    const [telegramSettings, setTelegramSettings] = useState(null);
    const [telegramForm, setTelegramForm] = useState({ bot_token: '', chat_id: '', group_name: '' });
    const [telegramSaving, setTelegramSaving] = useState(false);
    const [telegramFinding, setTelegramFinding] = useState(false);
    const [telegramFoundChats, setTelegramFoundChats] = useState(null);
    const [telegramMessage, setTelegramMessage] = useState('');
    const [telegramSending, setTelegramSending] = useState(false);
    const [telegramSendResult, setTelegramSendResult] = useState(null);

    // Zoho state
    const [zohoCredentials, setZohoCredentials] = useState(null);
    const [zohoForm, setZohoForm] = useState({ client_id: '', client_secret: '', refresh_token: '', organization_id: '' });
    const [zohoSaving, setZohoSaving] = useState(false);
    const [zohoTesting, setZohoTesting] = useState(false);
    const [zohoTestResult, setZohoTestResult] = useState(null);

    // Invoice state
    const [invoices, setInvoices] = useState([]);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [invoiceSyncing, setInvoiceSyncing] = useState(false);
    const [invoiceSyncResult, setInvoiceSyncResult] = useState(null);
    const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
    const [discountInvoice, setDiscountInvoice] = useState(null);
    const [discountAmount, setDiscountAmount] = useState('');
    const [discountSubmitting, setDiscountSubmitting] = useState(false);
    const [discountError, setDiscountError] = useState('');
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceDateFrom, setInvoiceDateFrom] = useState(defaultInvoiceDateFrom);
    const [invoiceDateTo, setInvoiceDateTo] = useState(defaultInvoiceDateTo);

    // Items state (full list kept here for order-form pickers; the Items tab UI itself
    // — search, pagination, sync, add/edit — lives in its own ItemsTab component)
    const [zohoItems, setZohoItems] = useState([]);

    // Add Invoice modal state
    const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
    const [invoiceModalOrders, setInvoiceModalOrders] = useState([]);
    const [invoiceModalLoading, setInvoiceModalLoading] = useState(false);
    const [invoiceOrderSearch, setInvoiceOrderSearch] = useState('');
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [invoiceNewDate, setInvoiceNewDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [invoiceNewBalance, setInvoiceNewBalance] = useState('');
    const [invoiceNewDiscount, setInvoiceNewDiscount] = useState('');
    const [expandedInvoiceGroups, setExpandedInvoiceGroups] = useState(new Set());
    const [addInvoiceSubmitting, setAddInvoiceSubmitting] = useState(false);
    const [addInvoiceError, setAddInvoiceError] = useState('');
    const [addInvoiceResult, setAddInvoiceResult] = useState(null);
    const [invoiceOtherItems, setInvoiceOtherItems] = useState([]);
    const [invoiceSelectedCustomer, setInvoiceSelectedCustomer] = useState(null);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    // Add-items-to-existing-invoice modal state
    const [addItemsInvoice, setAddItemsInvoice] = useState(null);
    const [addItemsRows, setAddItemsRows] = useState([]);
    const [addItemsPaid, setAddItemsPaid] = useState(false);
    const [addItemsSubmitting, setAddItemsSubmitting] = useState(false);
    const [addItemsError, setAddItemsError] = useState('');
    // Record-payment modal state
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'cash', payment_date: '', note: '' });
    const [paymentSubmitting, setPaymentSubmitting] = useState(false);
    const [paymentError, setPaymentError] = useState('');

    // Statistics state
    const today = new Date();
    const statsDefaultFrom = new Date(today.getFullYear(), today.getMonth() - 2, 1).toISOString().split('T')[0];
    const statsDefaultTo = today.toISOString().split('T')[0];
    const [statsType, setStatsType] = useState('string-usage');
    const [statsFrom, setStatsFrom] = useState(statsDefaultFrom);
    const [statsTo, setStatsTo] = useState(statsDefaultTo);
    const [statsLimit, setStatsLimit] = useState('top10');
    const [statsData, setStatsData] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);

    // Analytics state
    const [analyticsFrom, setAnalyticsFrom] = useState(statsDefaultFrom);
    const [analyticsTo, setAnalyticsTo] = useState(statsDefaultTo);
    const [analyticsOrders, setAnalyticsOrders] = useState(null); // null = not yet filtered
    const [analyticsFiltering, setAnalyticsFiltering] = useState(false);
    const [constChecked, setConstChecked] = useState(ANALYTICS_INIT_CHECKED);
    const [constValues, setConstValues] = useState(ANALYTICS_INIT_VALUES);
    const [manipChecked, setManipChecked] = useState(ANALYTICS_INIT_CHECKED);
    const [respEnabled, setRespEnabled] = useState(false);
    const [respDtChecked, setRespDtChecked] = useState(true);
    const [analyticsResult, setAnalyticsResult] = useState(null);

    // Shared state
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const dragItem = useRef(null);

    // Row drag state (for table row reordering)
    const productRowDragIdx = useRef(null);
    const stringRowDragIdx = useRef(null);
    const [productRowDragOver, setProductRowDragOver] = useState(null);
    const [stringRowDragOver, setStringRowDragOver] = useState(null);

    const navigate = useNavigate();

    const token = localStorage.getItem('adminToken');

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    }), [token]);

    const handleAuthError = useCallback((res) => {
        if (res.status === 401) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUsername');
            navigate('/admin');
            return true;
        }
        return false;
    }, [navigate]);

    // ========== IMAGE UPLOAD ==========
    const handleImageUpload = async (file, setFormFunc) => {
        if (!file) return;
        setUploadingImage(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`${API_BASE_URL}/admin/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Upload failed');
            }
            const data = await res.json();
            setFormFunc(prev => ({ ...prev, image: data.url }));
        } catch (err) {
            setError('Image upload failed: ' + err.message);
        } finally {
            setUploadingImage(false);
        }
    };

    // ========== VIDEO FUNCTIONS ==========
    const fetchVideos = useCallback(async () => {
        try {
            setVideoLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/videos`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setVideos(data.videos);
        } catch (err) {
            setError('Failed to load videos');
        } finally {
            setVideoLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const openAddVideoForm = () => {
        setVideoForm(emptyVideoForm);
        setEditingVideoId(null);
        setShowVideoForm(true);
    };

    const toggleVideoEnabled = async (video) => {
        try {
            await fetch(`${API_BASE_URL}/admin/videos/${video.id}`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ enabled: !video.enabled })
            });
            fetchVideos();
        } catch (err) {
            setError('Failed to update video status');
        }
    };

    const openEditVideoForm = (video) => {
        setVideoForm({
            title: video.title, embedUrl: video.embedUrl,
            description: video.description || '', category: video.category || 'guide',
            date: video.date || '',
            enabled: video.enabled
        });
        setEditingVideoId(video.id);
        setShowVideoForm(true);
    };

    const handleVideoFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setVideoForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleVideoSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const url = editingVideoId ? `${API_BASE_URL}/admin/videos/${editingVideoId}` : `${API_BASE_URL}/admin/videos`;
            const res = await fetch(url, { method: editingVideoId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(videoForm) });
            if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Save failed'); }
            setShowVideoForm(false);
            setEditingVideoId(null);
            fetchVideos();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleVideoDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this video?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/videos/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchVideos();
        } catch (err) {
            setError('Failed to delete video');
        }
    };

    // ========== STRING FUNCTIONS ==========
    const fetchStrings = useCallback(async () => {
        try {
            setStringLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/strings`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setStrings(data.strings);
        } catch (err) {
            setError('Failed to load strings');
        } finally {
            setStringLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const openAddStringForm = () => {
        setStringForm(emptyStringForm);
        setEditingStringId(null);
        setShowStringForm(true);
    };

    const openEditStringForm = (s) => {
        const imgs = s.images ? (Array.isArray(s.images) ? s.images : JSON.parse(s.images)) : [];
        setStringForm({
            brand: s.brand, model: s.model, type: s.type || '',
            description: s.description || '', price: s.price,
            gauge: s.gauge || '', coreMaterial: s.coreMaterial || '',
            outerMaterial: s.outerMaterial || '', coating: s.coating || '', images: imgs,
            racquet_type: s.racquet_type || 'Badminton',
            colorGroupsText: readColorGroups(s).join(', ')
        });
        setEditingStringId(s.id);
        setShowStringForm(true);
    };

    const handleStringFormChange = (e) => {
        const { name, value } = e.target;
        setStringForm(prev => ({ ...prev, [name]: value }));
    };

    const handleStringSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...stringForm,
                price: parseFloat(stringForm.price),
                colorGroups: parseColorGroupsInput(stringForm.colorGroupsText)
            };
            const url = editingStringId ? `${API_BASE_URL}/admin/strings/${editingStringId}` : `${API_BASE_URL}/admin/strings`;
            const res = await fetch(url, { method: editingStringId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
            if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Save failed'); }
            setShowStringForm(false);
            setEditingStringId(null);
            fetchStrings();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleStringDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this string?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/strings/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchStrings();
        } catch (err) {
            setError('Failed to delete string');
        }
    };

    // ========== PRODUCT FUNCTIONS ==========
    const fetchProducts = useCallback(async () => {
        try {
            setProductLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/products`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setProducts(data.products);
        } catch (err) {
            setError('Failed to load products');
        } finally {
            setProductLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const openAddProductForm = () => {
        setProductForm(emptyProductForm);
        setEditingProductId(null);
        setShowProductForm(true);
    };

    const openEditProductForm = (p) => {
        let imagesList = [];
        if (p.images && p.images.length > 0) {
            imagesList = [...p.images];
        } else if (p.image) {
            imagesList = [p.image];
        }
        setProductForm({
            name: p.name, brand: p.brand, category: p.category || 'accessories',
            description: p.description || '', price: p.price,
            images: imagesList, inStock: p.inStock, specifications: p.specifications || '',
            rentalFee: p.rentalFee != null ? p.rentalFee : '', condition: p.item_condition || '',
            costing: p.costing != null ? p.costing : '',
            swingWeight: p.swing_weight || '', weightType: p.weight_type || '',
            weight: p.weight || '', balancePoint: p.balance_point || '',
            grommetSystem: p.grommet_system || '', outForRent: !!p.out_for_rent,
            stringType: p.string_type || '', gauge: p.gauge || ''
        });
        setEditingProductId(p.id);
        setShowProductForm(true);
    };

    const handleProductFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'racquetStatus') {
            setProductForm(prev => ({ ...prev, inStock: value === 'inStock', outForRent: value === 'outForRent' }));
        } else {
            setProductForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    // ========== PRODUCT IMAGE FUNCTIONS ==========
    const handleProductImageAdd = async (files) => {
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setUploadingImage(true);
        setUploadProgress({ done: 0, total: fileArray.length });
        setError('');
        for (let i = 0; i < fileArray.length; i++) {
            try {
                const formData = new FormData();
                formData.append('image', fileArray[i]);
                const res = await fetch(`${API_BASE_URL}/admin/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                });
                if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Upload failed'); }
                const data = await res.json();
                setProductForm(prev => ({ ...prev, images: [...prev.images, data.url] }));
                setUploadProgress({ done: i + 1, total: fileArray.length });
            } catch (err) {
                setError('Image upload failed: ' + err.message);
            }
        }
        setUploadingImage(false);
    };

    const removeProductImage = (index) => {
        setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    };

    const handleDragStart = (e, index) => {
        dragItem.current = index;
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDrop = (e, index) => {
        e.preventDefault();
        if (dragItem.current !== null && dragItem.current !== index) {
            setProductForm(prev => {
                const imgs = [...prev.images];
                const dragged = imgs.splice(dragItem.current, 1)[0];
                imgs.splice(index, 0, dragged);
                return { ...prev, images: imgs };
            });
        }
        dragItem.current = null;
        setDragOverIndex(null);
    };
    const handleDragEnd = () => {
        dragItem.current = null;
        setDragOverIndex(null);
    };

    // ========== STRING IMAGE FUNCTIONS ==========
    const handleStringImageAdd = async (files) => {
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setUploadingImage(true);
        setUploadProgress({ done: 0, total: fileArray.length });
        setError('');
        for (let i = 0; i < fileArray.length; i++) {
            try {
                const formData = new FormData();
                formData.append('image', fileArray[i]);
                const res = await fetch(`${API_BASE_URL}/admin/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData
                });
                if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Upload failed'); }
                const data = await res.json();
                setStringForm(prev => ({ ...prev, images: [...prev.images, data.url] }));
                setUploadProgress({ done: i + 1, total: fileArray.length });
            } catch (err) {
                setError('Image upload failed: ' + err.message);
            }
        }
        setUploadingImage(false);
    };

    const removeStringImage = (index) => {
        setStringForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    };

    const handleStringImageDragStart = (e, index) => {
        dragItem.current = index;
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleStringImageDrop = (e, index) => {
        e.preventDefault();
        if (dragItem.current !== null && dragItem.current !== index) {
            setStringForm(prev => {
                const imgs = [...prev.images];
                const dragged = imgs.splice(dragItem.current, 1)[0];
                imgs.splice(index, 0, dragged);
                return { ...prev, images: imgs };
            });
        }
        dragItem.current = null;
        setDragOverIndex(null);
    };

    const handleProductSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...productForm,
                price: parseFloat(productForm.price),
                rentalFee: productForm.rentalFee !== '' ? parseFloat(productForm.rentalFee) : ''
            };
            const url = editingProductId ? `${API_BASE_URL}/admin/products/${editingProductId}` : `${API_BASE_URL}/admin/products`;
            const res = await fetch(url, { method: editingProductId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
            if (!res.ok) { const data = await res.json(); throw new Error(data.error || 'Save failed'); }
            setShowProductForm(false);
            setEditingProductId(null);
            fetchProducts();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleProductDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/products/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchProducts();
        } catch (err) {
            setError('Failed to delete product');
        }
    };

    const toggleProductStock = async (product) => {
        try {
            await fetch(`${API_BASE_URL}/admin/products/${product.id}`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ inStock: !product.inStock })
            });
            fetchProducts();
        } catch (err) {
            setError('Failed to update stock status');
        }
    };

    // ========== ROW DRAG HANDLERS ==========
    const handleProductRowDragStart = (index) => {
        productRowDragIdx.current = index;
    };
    const handleProductRowDragOver = (e, index) => {
        e.preventDefault();
        setProductRowDragOver(index);
    };
    const handleProductRowDrop = async (index) => {
        const from = productRowDragIdx.current;
        productRowDragIdx.current = null;
        setProductRowDragOver(null);
        if (from === null || from === index) return;
        const reordered = [...products];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(index, 0, moved);
        setProducts(reordered);
        try {
            await fetch(`${API_BASE_URL}/admin/products/reorder`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ ids: reordered.map(p => p.id) })
            });
        } catch (err) {
            setError('Failed to save product order');
            fetchProducts();
        }
    };
    const handleProductRowDragEnd = () => {
        productRowDragIdx.current = null;
        setProductRowDragOver(null);
    };

    const handleStringRowDragStart = (index) => {
        stringRowDragIdx.current = index;
    };
    const handleStringRowDragOver = (e, index) => {
        e.preventDefault();
        setStringRowDragOver(index);
    };
    const handleStringRowDrop = async (index) => {
        const from = stringRowDragIdx.current;
        stringRowDragIdx.current = null;
        setStringRowDragOver(null);
        if (from === null || from === index) return;
        const reordered = [...strings];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(index, 0, moved);
        setStrings(reordered);
        try {
            await fetch(`${API_BASE_URL}/admin/strings/reorder`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ ids: reordered.map(s => s.id) })
            });
        } catch (err) {
            setError('Failed to save string order');
            fetchStrings();
        }
    };
    const handleStringRowDragEnd = () => {
        stringRowDragIdx.current = null;
        setStringRowDragOver(null);
    };

    // ========== CSV IMPORT ==========
    const handleDownloadTemplate = () => {
        const header = 'name,brand,category,price,description,specifications,inStock,rentalFee';
        const example = '"Yonex Astrox 99","Yonex","racquets","299.00","High performance racquet","Weight: 83g","true",""';
        const blob = new Blob([header + '\n' + example], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'products_template.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    const handleCsvImport = async () => {
        if (!csvFile) return;
        setCsvImporting(true);
        setCsvResult(null);
        setError('');
        try {
            const formData = new FormData();
            formData.append('csv', csvFile);
            const res = await fetch(`${API_BASE_URL}/admin/products/import-csv`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Import failed');
            setCsvResult(data);
            if (data.imported > 0) fetchProducts();
        } catch (err) {
            setError('CSV import failed: ' + err.message);
        } finally {
            setCsvImporting(false);
        }
    };

    const openCsvModal = () => {
        setCsvFile(null);
        setCsvResult(null);
        setShowCsvModal(true);
    };

    const handleArrivalImageUpload = async (file) => {
        if (!file) return;
        setUploadingArrivalImage(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`${API_BASE_URL}/admin/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Upload failed'); }
            const data = await res.json();
            setArrivalForm(p => ({ ...p, image: data.url }));
        } catch (err) {
            setError('Image upload failed: ' + err.message);
        } finally {
            setUploadingArrivalImage(false);
        }
    };

    // ========== NEW ARRIVALS FUNCTIONS ==========
    const fetchNewArrivalsAdmin = useCallback(async () => {
        setNewArrivalsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/new-arrivals`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setNewArrivalsAdmin(data.arrivals || []);
        } catch (err) {
            setError('Failed to load new arrivals');
        } finally {
            setNewArrivalsLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const handleSaveArrival = async () => {
        if (!arrivalForm.name.trim()) { setError('Name is required'); return; }
        setError('');
        try {
            const method = editingArrivalId ? 'PUT' : 'POST';
            const url = editingArrivalId
                ? `${API_BASE_URL}/admin/new-arrivals/${editingArrivalId}`
                : `${API_BASE_URL}/admin/new-arrivals`;
            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify(arrivalForm),
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Save failed'); }
            setShowArrivalForm(false);
            setEditingArrivalId(null);
            setArrivalForm({ name: '', category: 'racquets', label: 'New Arrival', description: '', image: '' });
            fetchNewArrivalsAdmin();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteArrival = (id) => {
        Modal.confirm({
            title: 'Remove New Arrival',
            content: 'This item will be removed from the Home page New Arrivals section.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            centered: true,
            onOk: async () => {
                await fetch(`${API_BASE_URL}/admin/new-arrivals/${id}`, { method: 'DELETE', headers: authHeaders() });
                fetchNewArrivalsAdmin();
            },
        });
    };

    const handleToggleArrival = async (item) => {
        await fetch(`${API_BASE_URL}/admin/new-arrivals/${item.id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ enabled: item.enabled ? 0 : 1 }),
        });
        fetchNewArrivalsAdmin();
    };

    const openEditArrival = (item) => {
        setArrivalForm({ name: item.name, category: item.category || 'racquets', label: item.label || 'New Arrival', description: item.description || '', image: item.image || '' });
        setEditingArrivalId(item.id);
        setShowArrivalForm(true);
    };

    // ========== STRINGING ORDER FUNCTIONS ==========
    const fetchOrders = useCallback(async (status) => {
        try {
            setOrderLoading(true);
            const qs = status && status !== 'All' ? `?status=${encodeURIComponent(status)}` : '';
            const res = await fetch(`${API_BASE_URL}/admin/string-order${qs}`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setOrders(data.orders);
        } catch (err) {
            setError('Failed to load stringing orders');
        } finally {
            setOrderLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/customers`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setCustomers(data.customers || []);
        } catch (err) {
            setError('Failed to load customers');
        }
    }, [authHeaders, handleAuthError]);

    const openAddOrderModal = () => {
        setOrderForm(emptyOrderForm);
        setOrderWizardStep('phone');
        setOrderPhoneLookupStatus(null);
        setOrderCustomerId(null);
        setShowOrderModal(true);
        // Refresh so items added on the Items tab (own component state) show up immediately
        // instead of waiting for the dashboard's initial-load fetch or a full page reload.
        fetchItems();
    };

    const updateRacquet = (rIdx, updater) => {
        setOrderForm(prev => ({
            ...prev,
            racquets: prev.racquets.map((r, i) => i === rIdx ? updater(r) : r),
        }));
    };

    const handleRacquetChange = (rIdx, field, val) => {
        updateRacquet(rIdx, r => {
            if (field === 'racquetBrandSelect') return { ...r, racquetBrandSelect: val, racquetBrandOther: '', racquetModel: '', racquetModelOther: '' };
            return { ...r, [field]: val };
        });
    };

    const handleAddRacquet = () => {
        setOrderForm(prev => ({
            ...prev,
            racquets: [...prev.racquets.map(r => ({ ...r, collapsed: true })), emptyRacquet()],
        }));
    };

    const handleEditRacquet = (rIdx) => {
        setOrderForm(prev => ({
            ...prev,
            racquets: prev.racquets.map((r, i) => ({ ...r, collapsed: i !== rIdx })),
        }));
    };

    const handleDuplicateRacquet = (rIdx) => {
        updateRacquet(rIdx, (r) => {
            const prevRacquet = orderForm.racquets[rIdx - 1];
            if (!prevRacquet) return r;
            return { ...prevRacquet, collapsed: false, string: { ...prevRacquet.string } };
        });
    };

    const handleRemoveRacquet = (rIdx) => {
        setOrderForm(prev => {
            const next = prev.racquets.filter((_, i) => i !== rIdx);
            // ensure at least one is expanded
            if (next.length > 0 && next.every(r => r.collapsed)) {
                next[next.length - 1] = { ...next[next.length - 1], collapsed: false };
            }
            return { ...prev, racquets: next };
        });
    };

    const handleStringFieldChange = (rIdx, field, value) => {
        updateRacquet(rIdx, r => ({ ...r, string: { ...r.string, [field]: value } }));
    };

    // Main tension mirrors into cross tension until the user has deliberately
    // set cross to something different from main.
    const handleMainTensionChange = (rIdx, value) => {
        updateRacquet(rIdx, r => {
            const inSync = r.string.crossTension === '' || r.string.crossTension === r.string.tension;
            return { ...r, string: { ...r.string, tension: value, crossTension: inSync ? value : r.string.crossTension } };
        });
    };

    // Step 1 — phone lookup. Existing customer -> straight to Racquet Details
    // (pre-filled). Unknown number -> New Customer Name step.
    const handlePhoneLookupSubmit = async () => {
        const digits = (orderForm.phone || '').replace(/\D/g, '');
        if (digits.length < 5) {
            setError('Please enter a valid phone number.');
            return;
        }
        setOrderPhoneLookupLoading(true);
        setError('');
        try {
            const phone = '+' + digits;
            const res = await fetch(`${API_BASE_URL}/admin/customers/lookup?phone=${encodeURIComponent(phone)}`, {
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Lookup failed');
            const data = await res.json();
            if (data.customer) {
                setOrderPhoneLookupStatus('found');
                setOrderCustomerId(data.customer.id);
                setOrderForm(prev => ({ ...prev, custName: data.customer.customer_name }));
                setOrderWizardStep('racquet');
            } else {
                setOrderPhoneLookupStatus('not_found');
                setOrderCustomerId(null);
                setOrderWizardStep('newCustomer');
            }
        } catch (err) {
            setError('Failed to look up phone number: ' + err.message);
        } finally {
            setOrderPhoneLookupLoading(false);
        }
    };

    // Step 2 — new customer name (only shown when the phone wasn't found).
    const handleCreateNewCustomerSubmit = async () => {
        if (!orderForm.custName.trim()) {
            setError('Please enter the customer name.');
            return;
        }
        setOrderSaving(true);
        setError('');
        try {
            const digits = (orderForm.phone || '').replace(/\D/g, '');
            const res = await fetch(`${API_BASE_URL}/admin/customers`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ customer_name: orderForm.custName.trim(), contact_number: '+' + digits }),
            });
            const data = await res.json();
            if (!res.ok && !data.duplicate) throw new Error(data.error || 'Failed to create customer');
            // duplicate === true means someone else created this contact a moment ago — fine,
            // string-order creation below still finds/attaches to it by contact_number.
            setOrderWizardStep('racquet');
        } catch (err) {
            setError(err.message);
        } finally {
            setOrderSaving(false);
        }
    };

    // Step 4 — collect date + confirm. Creates the order and (server-side, atomically)
    // decrements stock_on_hand for each string used.
    const handleOrderConfirm = async () => {
        if (orderForm.expectedCollectionDate && orderForm.expectedCollectionDate < todayDateStr()) {
            setError('Collect date cannot be in the past.');
            return;
        }
        const orderTotal = orderForm.racquets.reduce((sum, r) => sum + orderRacquetPrice(r.string, zohoItems).price, 0);
        const discountVal = orderForm.discount !== '' ? Number(orderForm.discount) : 0;
        if (!Number.isFinite(discountVal) || discountVal < 0) {
            setError('Discount must be 0 or more.');
            return;
        }
        if (discountVal > orderTotal + 0.001) {
            setError(`Discount can't be more than the total fees (SGD ${orderTotal.toFixed(2)}).`);
            return;
        }
        for (const r of orderForm.racquets) {
            const brand = r.racquetBrandSelect === 'Other' ? r.racquetBrandOther : r.racquetBrandSelect;
            const model = r.racquetBrandSelect === 'Other' ? r.racquetModelOther : r.racquetModel;
            if (!brand.trim() || !model.trim()) {
                setError('Please fill in brand and model for all racquets.');
                return;
            }
            if (!r.string.bringOwnString && !r.string.zohoItemId) {
                setError('Please select a string for all racquets.');
                return;
            }
            if (!r.string.bringOwnString && r.string.hybrid && !r.string.crossZohoItemId) {
                setError('Please select the 2nd string (crosses) or untick "Use 2 different strings".');
                return;
            }
            if (!String(r.string.tension || '').trim() || !String(r.string.crossTension || '').trim()) {
                setError('Please enter main and cross tension for all racquets.');
                return;
            }
        }
        setOrderSaving(true);
        setError('');
        try {
            const digits = (orderForm.phone || '').replace(/\D/g, '');
            const payload = {
                contact_number: '+' + digits,
                customer_name: orderForm.custName,
                racquets: orderForm.racquets.map(r => ({
                    racquet_brand: r.racquetBrandSelect === 'Other' ? r.racquetBrandOther.trim() : r.racquetBrandSelect,
                    racquet_model: r.racquetBrandSelect === 'Other' ? r.racquetModelOther.trim() : r.racquetModel,
                    paid: !!orderForm.paid,
                    express_service: r.expressService ? 1 : 0,
                    strings: [{
                        brand: r.string.bringOwnString ? r.string.ownStringType.trim() : r.string.zohoItemName, model: '', color: '',
                        zoho_item_id: r.string.bringOwnString ? null : (r.string.zohoItemId || null),
                        bring_own_string: !!r.string.bringOwnString,
                        ...(isHybridString(r.string) ? { cross_zoho_item_id: r.string.crossZohoItemId, cross_brand: r.string.crossZohoItemName } : {}),
                        tension: r.string.tension, cross_tension: r.string.crossTension,
                        pre_stretch: r.string.preStretch || 'None',
                        string_pattern: r.string.stringPattern || null,
                    }],
                })),
                collection_date: orderForm.expectedCollectionDate || null,
                discount: orderForm.discount !== '' ? orderForm.discount : 0,
            };
            const res = await fetch(`${API_BASE_URL}/admin/string-order`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Save failed');
            setShowOrderModal(false);
            setOrderForm(emptyOrderForm);
            fetchOrders(orderFilter);
            fetchItems();
            const orderRef = data.orders?.[0]?.order_group_ref;
            fetchInvoices();
            notification.success({
                message: 'Order Created',
                description: (orderRef ? `Order ${orderRef} has been created successfully.` : 'The string order has been created successfully.')
                    + (data.invoice_id ? ` Draft invoice ${data.invoice_number || data.invoice_id} was created — add other items to it from the Invoicing tab.` : ''),
                placement: 'topRight',
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setOrderSaving(false);
        }
    };

    const handleOrderStatusChange = async (id, status) => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/string-order/${id}`, {
                method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status })
            });
            if (!res.ok) throw new Error('Failed to update order status');
            fetchOrders(orderFilter);
            notification.success({
                message: 'Order Updated',
                description: `Order status has been updated to "${status}".`,
                placement: 'topRight',
            });
        } catch (err) {
            setError('Failed to update order status');
        }
    };

    const handlePaidChange = async (id, paid) => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/string-order/${id}`, {
                method: 'PUT', headers: authHeaders(), body: JSON.stringify({ paid })
            });
            if (!res.ok) throw new Error('Failed to update paid status');
            fetchOrders(orderFilter);
        } catch (err) {
            setError('Failed to update paid status');
        }
    };

    // Deletes an order together with its invoice. If the invoice also holds other
    // racquets or extra items, asks whether to delete the whole invoice or keep it
    // and only remove this order's line. Resolves true once the order is deleted.
    const deleteStringOrder = async (orderId) => {
        const send = async (choice) => {
            const res = await fetch(`${API_BASE_URL}/admin/string-order/${orderId}${choice ? `?invoice=${choice}` : ''}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            const data = await res.json().catch(() => ({}));
            return { ok: res.ok, data };
        };
        const finish = (data) => {
            fetchOrders(orderFilter);
            fetchItems();
            fetchInvoices();
            notification.success({
                message: 'Order Deleted',
                description: data.invoice_deleted ? `Invoice ${data.invoice_deleted} was deleted too.` : 'The order has been deleted.',
                placement: 'topRight',
            });
        };
        const first = await send();
        if (first.ok) { finish(first.data); return true; }
        if (first.data.code !== 'INVOICE_HAS_OTHER_ITEMS') throw new Error(first.data.error || 'Failed to delete order');

        const d = first.data;
        return new Promise(resolve => {
            const run = async (choice) => {
                try {
                    const r = await send(choice);
                    if (!r.ok) throw new Error(r.data.error || 'Failed to delete order');
                    finish(r.data);
                    resolve(true);
                } catch (err) {
                    setError(err.message);
                    resolve(false);
                }
            };
            let dialog;
            dialog = Modal.confirm({
                title: `Invoice ${d.invoice_number} has other items`,
                centered: true,
                width: 480,
                content: (
                    <div>
                        <div style={{ marginBottom: 8 }}>Besides this order, the invoice also has:</div>
                        <ul style={{ paddingLeft: 18, marginBottom: 8 }}>
                            {d.other_orders.length > 0 && <li>Other racquets: {d.other_orders.join(', ')}</li>}
                            {d.extra_items.map((it, i) => (
                                <li key={i}>{it.name} × {it.quantity} (SGD {Number(it.total).toFixed(2)})</li>
                            ))}
                        </ul>
                        {d.paid_amount > 0 && (
                            <div style={{ marginBottom: 8, color: '#c41e3a' }}>SGD {Number(d.paid_amount).toFixed(2)} of recorded payments will be deleted with the invoice.</div>
                        )}
                        <div style={{ color: '#888', fontSize: '0.85rem' }}>
                            <strong>Delete invoice too</strong> removes the whole invoice{d.other_orders.length > 0 ? ' (the other racquets become uninvoiced)' : ''}.
                            {' '}<strong>Keep invoice</strong> only removes this order's line.
                        </div>
                    </div>
                ),
                okText: 'Delete invoice too',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => run('delete'),
                onCancel: () => resolve(false),
                footer: (_, { OkBtn, CancelBtn }) => (
                    <>
                        <CancelBtn />
                        <Button onClick={() => { dialog.destroy(); run('keep'); }}>Keep invoice</Button>
                        <OkBtn />
                    </>
                ),
            });
        });
    };

    const handleOrderDelete = async (id) => {
        const order = orders.find(o => o.order_id === id);
        const stockNote = order && order.status === 'Pending to String'
            ? ' Its string will be returned to stock.'
            : ' It has already been strung, so its string will not be returned to stock.';
        if (!window.confirm(`Delete this stringing order and its invoice?${stockNote}`)) return;
        try {
            await deleteStringOrder(id);
        } catch (err) {
            setError(err.message || 'Failed to delete order');
        }
    };

    const openUpdateModal = (order) => {
        setUpdateModalEditing(true);
        setOrderDetailPhotos([]);
        setPhotoCarouselVisible(false);
        fetch(`${API_BASE_URL}/admin/string-order-photos/${order.order_id}`, { headers: authHeaders() })
            .then(r => r.json())
            .then(d => setOrderDetailPhotos(d.photos || []))
            .catch(() => { });
        setUpdateForm(buildOrderForm(order));
        setShowUpdateModal(true);
    };

    // Maps an order row to the Order Details form shape (also used to print straight from the table)
    const buildOrderForm = (order) => {
        const toDateStr = (val) => {
            if (!val) return '';
            const s = typeof val === 'string' ? val : val.toISOString();
            return s.split('T')[0];
        };
        return {
            order_id: order.order_id,
            _order_ref: order.order_ref,
            // read-only display fields (prefixed with _)
            _customer_name: order.customer_name,
            _contact_number: order.contact_number,
            _order_date: order.order_date,
            _collected_date: order.collected_date,
            // editable racquet fields
            racquet_brand: RACQUET_BRANDS.includes(order.racquet_brand) ? order.racquet_brand : 'Others',
            racquet_brand_other: RACQUET_BRANDS.includes(order.racquet_brand) ? '' : (order.racquet_brand || ''),
            racquet_model: order.racquet_model || '',
            // editable fields
            status: order.status,
            zoho_item_id: order.zoho_item_id || '',
            zoho_item_name: (() => { const it = (zohoItems || []).find(i => i.item_id === order.zoho_item_id); return it ? it.name : (order.string_brand || ''); })(),
            cross_zoho_item_id: order.cross_zoho_item_id || '',
            cross_string_brand: order.cross_string_brand || '',
            color: order.color || '',
            tension: order.tension || '',
            cross_tension: order.cross_tension || order.tension || '',
            pre_stretch: order.pre_stretch || 'None',
            stringer_name: order.stringer_name || '',
            express_service: !!order.express_service,
            bring_own_string: !!order.bring_own_string,
            paid: !!order.paid,
            string_pattern: order.string_pattern || '',
            string_machine: order.string_machine || '',
            dt: order.dt || '',
            fees: order.fees != null ? String(order.fees) : '',
            collection_date: toDateStr(order.collection_date),
            remarks: order.remarks || '',
        };
    };

    const handlePrintOrder = (form = updateForm) => {
        const tensionStr = form.tension
            ? `${form.tension} / ${form.cross_tension || form.tension} lbs`
            : '—';
        const stringModel = form.bring_own_string
            ? (form.zoho_item_name?.trim() ? `Customer's Own String (${form.zoho_item_name.trim()})` : "Customer's Own String")
            : (form.cross_string_brand
                ? `${form.zoho_item_name || '—'} (mains) / ${form.cross_string_brand} (crosses)`
                : (form.zoho_item_name || '—'));
        const preStretchStr = (!form.pre_stretch || form.pre_stretch === 'None')
            ? '0%'
            : `${form.pre_stretch}%`;
        const orderRef = form._order_ref || `#${form.order_id}`;
        const feesStr = form.fees !== '' && form.fees != null ? `$${form.fees}` : 'nil';
        const paidStr = form.paid ? 'PAID' : 'UNPAID';
        const collectionDateStr = form.collection_date
            ? new Date(form.collection_date).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
            : 'nil';

        // Inside the Takumi Tension tablet app, window.AndroidPrinter is injected by the
        // native WebView bridge — hand the order slip data to the native printer service
        // instead of falling back to the browser print dialog (which the app's WebView
        // doesn't support anyway). Desktop/Chrome users keep the existing window.print() flow.
        if (window.AndroidPrinter && typeof window.AndroidPrinter.printReceipt === 'function') {
            const receiptData = {
                order_ref: orderRef,
                date: new Date().toLocaleDateString(),
                customer_name: form._customer_name || '—',
                contact_number: form._contact_number || '—',
                string_model: stringModel,
                tension: tensionStr,
                pre_stretch: preStretchStr,
                string_pattern: form.string_pattern || '—',
                fees: feesStr,
                paid: paidStr,
                collection_date: collectionDateStr,
            };
            window.AndroidPrinter.printReceipt(JSON.stringify(receiptData));
            return;
        }

        const win = window.open('', '_blank', 'width=800,height=1000');
        if (!win) return;
        const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Stringing Order ${escapeHtml(orderRef)}</title>
<style>
  @page { size: A5; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 0; }
  .sheet { max-width: 480px; margin: 0 auto; padding: 24px; }
  .header { display: flex; align-items: center; gap: 14px; border-bottom: 3px solid #c41e3a; padding-bottom: 16px; margin-bottom: 24px; }
  .header img { height: 56px; width: auto; object-fit: contain; }
  .header .brand { font-size: 1.3rem; font-weight: 700; color: #c41e3a; letter-spacing: 0.5px; }
  .header .ref { margin-left: auto; text-align: right; font-size: 0.85rem; color: #555; line-height: 1.4; }
  .row { display: flex; justify-content: space-between; align-items: baseline; padding: 12px 0; border-bottom: 1px solid #eee; }
  .label { font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.5px; color: #888; }
  .value { font-size: 1.05rem; font-weight: 600; color: #1a1a1a; text-align: right; }
  .tension-value { font-size: 1.35rem; font-weight: 700; color: #c41e3a; }
  .remarks { margin-top: 28px; }
  .remarks .label { margin-bottom: 8px; }
  .remarks-box { border: 1px solid #ccc; border-radius: 6px; min-height: 90px; }
  .footer-note { margin-top: 24px; font-size: 0.72rem; color: #999; text-align: center; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <img src="${takumiLogo}" alt="Takumi Tension" />
      <div class="brand">Takumi Tension</div>
      <div class="ref">Order ${escapeHtml(orderRef)}<br/>${escapeHtml(new Date().toLocaleDateString())}</div>
    </div>
    <div class="row"><span class="label">Customer</span><span class="value">${escapeHtml(form._customer_name || '—')}</span></div>
    <div class="row"><span class="label">Contact Number</span><span class="value">${escapeHtml(form._contact_number || '—')}</span></div>
    <div class="row"><span class="label">String Model</span><span class="value">${escapeHtml(stringModel)}</span></div>
    <div class="row"><span class="label">Tension (Main / Cross)</span><span class="value tension-value">${escapeHtml(tensionStr)}</span></div>
    <div class="row"><span class="label">Pre-Stretch</span><span class="value">${escapeHtml(preStretchStr)}</span></div>
    <div class="row"><span class="label">String Pattern</span><span class="value">${escapeHtml(form.string_pattern || '—')}</span></div>
    <div class="row"><span class="label">Fees</span><span class="value">${escapeHtml(feesStr)}</span></div>
    <div class="row"><span class="label">Paid</span><span class="value">${escapeHtml(paidStr)}</span></div>
    <div class="row"><span class="label">Collection Date</span><span class="value">${escapeHtml(collectionDateStr)}</span></div>
    <div class="remarks">
      <div class="label">Remarks</div>
      <div class="remarks-box"></div>
    </div>
    <div class="footer-note">Thank you for choosing Takumi Tension 🏸</div>
  </div>
  <script>
    window.onload = function () { setTimeout(function () { window.print(); }, 200); };
  </script>
</body>
</html>`;
        win.document.open();
        win.document.write(html);
        win.document.close();
    };

    const handleUpdateSubmit = async () => {
        setError('');
        const statusRequiresFull = ['Ready to Collect', 'Collected'].includes(updateForm.status);
        if (statusRequiresFull) {
            const missing = [];
            if (!updateForm.tension?.trim()) missing.push('Tension');
            if (!updateForm.cross_tension?.trim()) missing.push('Cross Tension');
            if (!updateForm.string_pattern?.trim()) missing.push('String Pattern');
            if (!updateForm.stringer_name?.trim()) missing.push('Stringer Name');
            if (!updateForm.dt?.trim()) missing.push('DT');
            if (!updateForm.string_machine?.trim()) missing.push('String Machine');
            if (!updateForm.bring_own_string && !updateForm.zoho_item_name?.trim()) missing.push('String Item');
            if (missing.length > 0) {
                setError(`Required for "${updateForm.status}": ${missing.join(', ')}`);
                return;
            }
        }
        setUpdateSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/string-order/${updateForm.order_id}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({
                    status: updateForm.status,
                    string_brand: updateForm.zoho_item_name,
                    string_model: '',
                    zoho_item_id: updateForm.bring_own_string ? null : (updateForm.zoho_item_id || null),
                    cross_zoho_item_id: updateForm.bring_own_string ? null : (updateForm.cross_zoho_item_id || null),
                    cross_string_brand: updateForm.bring_own_string || !updateForm.cross_zoho_item_id ? null : updateForm.cross_string_brand,
                    bring_own_string: updateForm.bring_own_string ? 1 : 0,
                    racquet_brand: updateForm.racquet_brand === 'Others' ? updateForm.racquet_brand_other : updateForm.racquet_brand,
                    racquet_model: updateForm.racquet_model,
                    tension: updateForm.tension,
                    cross_tension: updateForm.cross_tension,
                    pre_stretch: updateForm.pre_stretch,
                    stringer_name: updateForm.stringer_name,
                    express_service: updateForm.express_service ? 1 : 0,
                    paid: updateForm.paid ? 1 : 0,
                    string_pattern: updateForm.string_pattern,
                    string_machine: updateForm.string_machine || null,
                    dt: updateForm.dt,
                    fees: updateForm.fees !== '' ? updateForm.fees : null,
                    collection_date: updateForm.collection_date || null,
                    remarks: updateForm.remarks || '',
                })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Update failed'); }
            setShowUpdateModal(false);
            fetchOrders(orderFilter);
            fetchItems();
            notification.success({
                message: 'Order Updated',
                description: updateForm._order_ref ? `Order ${updateForm._order_ref} has been updated successfully.` : 'The order has been updated successfully.',
                placement: 'topRight',
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setUpdateSaving(false);
        }
    };

    const handleDeleteFromModal = () => {
        Modal.confirm({
            title: 'Delete Stringing Order',
            content: `Order #${updateForm._order_ref || updateForm.order_id} for ${updateForm._customer_name || 'this customer'} and its invoice will be permanently deleted and cannot be recovered.`
                + (orders.find(o => o.order_id === updateForm.order_id)?.status === 'Pending to String'
                    ? ' Its string will be returned to stock.'
                    : ' It has already been strung, so its string will not be returned to stock.'),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            centered: true,
            onOk: async () => {
                try {
                    if (await deleteStringOrder(updateForm.order_id)) setShowUpdateModal(false);
                } catch (err) {
                    setError(err.message || 'Failed to delete order');
                }
            },
        });
    };


    // ========== STRINGER FUNCTIONS ==========
    const fetchStringers = useCallback(async () => {
        try {
            setStringerLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/stringers`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setStringers(data.stringers || []);
        } catch (err) {
            console.error(err);
        } finally {
            setStringerLoading(false);
        }
    }, [authHeaders]);

    const handleAddStringer = async () => {
        if (!newStringerName.trim()) return;
        setStringerSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/stringers`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ name: newStringerName.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add stringer');
            setNewStringerName('');
            fetchStringers();
        } catch (err) {
            setError(err.message);
        } finally {
            setStringerSaving(false);
        }
    };

    const handleDeleteStringer = async (id) => {
        if (!window.confirm('Remove this stringer?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/stringers/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchStringers();
        } catch (err) {
            setError('Failed to remove stringer');
        }
    };

    // ========== STRINGING MACHINE FUNCTIONS ==========
    const fetchStringMachines = useCallback(async () => {
        try {
            setMachineLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/stringing-machines`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setStringMachines(data.machines || []);
        } catch (err) {
            console.error(err);
        } finally {
            setMachineLoading(false);
        }
    }, [authHeaders]);

    const handleAddMachine = async () => {
        if (!newMachineName.trim()) return;
        setMachineSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/stringing-machines`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ name: newMachineName.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add machine');
            setNewMachineName('');
            fetchStringMachines();
        } catch (err) {
            setError(err.message);
        } finally {
            setMachineSaving(false);
        }
    };

    const handleDeleteMachine = async (id) => {
        if (!window.confirm('Remove this machine?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/stringing-machines/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchStringMachines();
        } catch (err) {
            setError('Failed to remove machine');
        }
    };

    // ========== STRINGING PATTERN FUNCTIONS ==========
    const fetchStringPatterns = useCallback(async () => {
        try {
            setPatternLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/stringing-patterns`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setStringPatterns(data.patterns || []);
        } catch (err) {
            console.error(err);
        } finally {
            setPatternLoading(false);
        }
    }, [authHeaders]);

    const handleAddPattern = async () => {
        if (!newPatternName.trim()) return;
        setPatternSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/stringing-patterns`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ name: newPatternName.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add pattern');
            setNewPatternName('');
            fetchStringPatterns();
        } catch (err) {
            setError(err.message);
        } finally {
            setPatternSaving(false);
        }
    };

    const handleDeletePattern = async (id) => {
        if (!window.confirm('Remove this pattern?')) return;
        try {
            await fetch(`${API_BASE_URL}/admin/stringing-patterns/${id}`, { method: 'DELETE', headers: authHeaders() });
            fetchStringPatterns();
        } catch (err) {
            setError('Failed to remove pattern');
        }
    };

    // ========== TELEGRAM FUNCTIONS ==========
    const fetchTelegramSettings = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/telegram/settings`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setTelegramSettings(data);
        } catch (err) {
            console.error(err);
        }
    }, [authHeaders]);

    const handleSaveTelegramSettings = async () => {
        if (!telegramForm.bot_token || !telegramForm.chat_id) {
            setError('Bot token and Chat ID are both required');
            return;
        }
        setTelegramSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/telegram/settings`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(telegramForm),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save');
            setTelegramForm({ bot_token: '', chat_id: '' });
            fetchTelegramSettings();
        } catch (err) {
            setError(err.message);
        } finally {
            setTelegramSaving(false);
        }
    };

    const [telegramRestarting, setTelegramRestarting] = useState(false);
    const [telegramRestartResult, setTelegramRestartResult] = useState(null);

    const handleRestartTelegramBot = async () => {
        setTelegramRestarting(true);
        setTelegramRestartResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/telegram/restart`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to restart');
            setTelegramRestartResult({ success: true });
        } catch (err) {
            setTelegramRestartResult({ success: false, error: err.message });
        } finally {
            setTelegramRestarting(false);
        }
    };

    const handleFindTelegramChat = async () => {
        setTelegramFinding(true);
        setTelegramFoundChats(null);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/telegram/find-chat`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to fetch chats');
            setTelegramFoundChats(data.chats || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setTelegramFinding(false);
        }
    };

    const handleSendTelegramMessage = async () => {
        if (!telegramMessage.trim()) return;
        setTelegramSending(true);
        setTelegramSendResult(null);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/telegram/send`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ message: telegramMessage }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Send failed');
            setTelegramSendResult({ success: true });
            setTelegramMessage('');
        } catch (err) {
            setTelegramSendResult({ success: false, error: err.message });
        } finally {
            setTelegramSending(false);
        }
    };

    // ========== INVOICE FUNCTIONS ==========
    const fetchInvoices = useCallback(async () => {
        try {
            setInvoiceLoading(true);
            const res = await fetch(`${API_BASE_URL}/admin/invoices`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            const list = data.invoices || [];
            setInvoices(list);
            // Keep an open Add Items modal showing the latest copy of its invoice.
            setAddItemsInvoice(prev => (prev ? list.find(i => i.invoice_id === prev.invoice_id) || null : prev));
        } catch (err) {
            setError('Failed to load invoices');
        } finally {
            setInvoiceLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    const handleSyncInvoices = async () => {
        setInvoiceSyncing(true);
        setInvoiceSyncResult(null);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/zoho/sync-invoices`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Sync failed');
            setInvoiceSyncResult({ success: true, synced: data.synced, itemsSynced: data.itemsSynced });
            message.success(`Sync complete — ${data.synced} invoice(s), ${data.itemsSynced} line item(s) saved`);
        } catch (err) {
            setInvoiceSyncResult({ success: false, error: err.message });
            message.error(`Sync failed: ${err.message}`);
        } finally {
            setInvoiceSyncing(false);
            await fetchInvoices();
        }
    };

    const fetchRacquetModels = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/racquet-models`, { headers: authHeaders() });
            const data = await res.json();
            if (data.models) setRacquetModelsData(data.models);
        } catch (err) {
            // Non-fatal — falls back to the bundled racquetModels.js list already in state.
        }
    }, [authHeaders]);

    const fetchItems = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/items`, { headers: authHeaders() });
            const data = await res.json();
            setZohoItems(data.items || []);
        } catch (err) {
            setError('Failed to load items');
        }
    }, [authHeaders]);

    const fetchAllOrdersForInvoice = async () => {
        setInvoiceModalLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/string-order?includeAll=true`, { headers: authHeaders() });
            const data = await res.json();
            setInvoiceModalOrders(data.orders || []);
        } catch (err) {
            setInvoiceModalOrders([]);
        } finally {
            setInvoiceModalLoading(false);
        }
    };

    const openDiscountModal = (inv) => {
        const current = invoiceDiscount(inv);
        setDiscountInvoice(inv);
        setDiscountAmount(current > 0 ? current.toFixed(2) : '');
        setDiscountError('');
    };

    const handleSaveDiscount = async () => {
        const amount = discountAmount === '' ? 0 : Number(discountAmount);
        const subtotal = Number(discountInvoice.total) + invoiceDiscount(discountInvoice);
        if (!Number.isFinite(amount) || amount < 0) { setDiscountError('Discount must be 0 or more.'); return; }
        if (amount > subtotal + 0.001) { setDiscountError(`Discount can't be more than SGD ${subtotal.toFixed(2)}.`); return; }
        setDiscountSubmitting(true);
        setDiscountError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/invoices/${encodeURIComponent(discountInvoice.invoice_id)}/discount`, {
                method: 'PUT', headers: authHeaders(), body: JSON.stringify({ amount }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save the discount');
            message.success(amount > 0 ? `Discount of SGD ${amount.toFixed(2)} saved on ${discountInvoice.invoice_number}` : `Discount removed from ${discountInvoice.invoice_number}`);
            if (data.overpaid > 0) {
                Modal.warning({
                    title: 'Customer has overpaid',
                    content: `${discountInvoice.invoice_number} was already paid, and with this discount the customer has paid SGD ${Number(data.overpaid).toFixed(2)} more than the new total. Refund it, or keep it as credit for items added later.`,
                    centered: true,
                });
            }
            setDiscountInvoice(null);
            await fetchInvoices();
        } catch (err) {
            setDiscountError(err.message);
        } finally {
            setDiscountSubmitting(false);
        }
    };

    // Runs a simple invoice action (void / delete payment) and refreshes the list.
    const runInvoiceAction = async (path, method, successText) => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/invoices/${path}`, { method, headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Request failed');
            message.success(successText);
            await fetchInvoices();
        } catch (err) {
            message.error(err.message);
        }
    };

    const handleVoidInvoice = (inv) => {
        Modal.confirm({
            title: `Void ${inv.invoice_number}?`,
            content: 'The invoice stays on record as void and cannot be changed. Its string orders are released so they can go on a new invoice.',
            okText: 'Void Invoice',
            okButtonProps: { danger: true },
            centered: true,
            onOk: () => runInvoiceAction(`${encodeURIComponent(inv.invoice_id)}/void`, 'POST', `Invoice ${inv.invoice_number} voided`),
        });
    };

    const handleDeletePayment = (inv, payment) => {
        Modal.confirm({
            title: 'Remove this payment?',
            content: `SGD ${Number(payment.amount).toFixed(2)} on ${payment.payment_date} will be removed and added back to the balance.`,
            okText: 'Remove',
            okButtonProps: { danger: true },
            centered: true,
            onOk: () => runInvoiceAction(`${encodeURIComponent(inv.invoice_id)}/payments/${payment.id}`, 'DELETE', 'Payment removed'),
        });
    };

    const openPaymentModal = (inv) => {
        setPaymentInvoice(inv);
        setPaymentForm({ amount: Number(inv.balance).toFixed(2), method: 'cash', payment_date: todayDateStr(), note: '' });
        setPaymentError('');
    };

    const handleRecordPayment = async () => {
        const amount = parseFloat(paymentForm.amount);
        if (!(amount > 0)) {
            setPaymentError('Enter an amount above 0.');
            return;
        }
        if (amount > Number(paymentInvoice.balance) + 0.001) {
            setPaymentError(`Amount is more than the balance (SGD ${Number(paymentInvoice.balance).toFixed(2)}).`);
            return;
        }
        setPaymentSubmitting(true);
        setPaymentError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/invoices/${encodeURIComponent(paymentInvoice.invoice_id)}/payments`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ ...paymentForm, amount }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to record payment');
            message.success(`Payment of SGD ${amount.toFixed(2)} recorded on ${paymentInvoice.invoice_number}`);
            setExpandedInvoiceId(paymentInvoice.invoice_id);
            setPaymentInvoice(null);
            fetchInvoices();
        } catch (err) {
            setPaymentError(err.message);
        } finally {
            setPaymentSubmitting(false);
        }
    };

    const handlePrintInvoice = (inv) => {
        const contact = customers.find(c => c.id === inv.customer_db_id)?.contact_number
            || customers.find(c => c.customer_name === inv.customer_name)?.contact_number;
        // Inside the tablet app, print to its Bluetooth thermal printer — the WebView can't open
        // pop-ups or show the print dialog. Older app builds without printInvoice fall through.
        if (window.AndroidPrinter && typeof window.AndroidPrinter.printInvoice === 'function') {
            window.AndroidPrinter.printInvoice(JSON.stringify(buildInvoicePrintData(inv, contact)));
            message.info('Invoice sent to printer');
            return;
        }
        const w = window.open('', '_blank');
        if (!w) {
            message.error('Pop-up blocked — allow pop-ups for this site to print invoices.');
            return;
        }
        w.document.write(buildInvoiceHtml(inv, contact));
        w.document.close();
    };

    const openAddItemsModal = (inv) => {
        setAddItemsInvoice(inv);
        setAddItemsRows([{ id: Date.now(), description: '', price: '', quantity: 1, item_id: null }]);
        setAddItemsPaid(false);
        setAddItemsError('');
    };

    const handleAddItemsSubmit = async () => {
        const items = addItemsRows
            .filter(it => it.description.trim() && parseFloat(it.price) > 0)
            .map(it => ({ description: it.description.trim(), price: parseFloat(it.price), quantity: parseInt(it.quantity, 10) || 1, item_id: it.item_id || null }));
        if (items.length === 0) {
            setAddItemsError('Add at least one item with a name and a price.');
            return;
        }
        setAddItemsSubmitting(true);
        setAddItemsError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/invoices/${encodeURIComponent(addItemsInvoice.invoice_id)}/items`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ items, mark_paid: addItemsPaid }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to add items');
            if (data.warning) message.warning(data.warning);
            else message.success(`${items.length} item${items.length !== 1 ? 's' : ''} added to ${addItemsInvoice.invoice_number}`);
            setAddItemsInvoice(null);
            setExpandedInvoiceId(addItemsInvoice.invoice_id);
            fetchInvoices();
        } catch (err) {
            setAddItemsError(err.message);
        } finally {
            setAddItemsSubmitting(false);
        }
    };

    const handleAddInvoiceSubmit = () => {
        const validOtherItems = invoiceOtherItems.filter(it => it.description.trim() && parseFloat(it.price) > 0);
        if (selectedOrderIds.size === 0 && validOtherItems.length === 0) {
            setAddInvoiceError('Please select at least one order or add an additional item.');
            return;
        }
        const orderCount = selectedOrderIds.size;
        const balance = invoiceNewBalance !== '' ? parseFloat(invoiceNewBalance) : 0;
        const extraLabel = validOtherItems.length > 0 ? ` + ${validOtherItems.length} additional item${validOtherItems.length !== 1 ? 's' : ''}` : '';
        Modal.confirm({
            title: 'Create Invoice',
            content: `Create an invoice for ${orderCount} order${orderCount !== 1 ? 's' : ''}${extraLabel}${balance === 0 ? ' — fully paid (balance $0.00)' : ` with balance SGD ${balance.toFixed(2)}`}?`,
            okText: 'Create Invoice',
            cancelText: 'Cancel',
            centered: true,
            onOk: async () => {
                setAddInvoiceSubmitting(true);
                setAddInvoiceError('');
                setAddInvoiceResult(null);
                try {
                    const res = await fetch(`${API_BASE_URL}/admin/invoices/create`, {
                        method: 'POST',
                        headers: authHeaders(),
                        body: JSON.stringify({
                            order_ids: Array.from(selectedOrderIds),
                            invoice_date: invoiceNewDate,
                            discount: invoiceNewDiscount !== '' ? Number(invoiceNewDiscount) : 0,
                            invoice_balance: balance,
                            additional_items: validOtherItems.map(it => ({ description: it.description.trim(), price: parseFloat(it.price), quantity: parseInt(it.quantity, 10) || 1, item_id: it.item_id || null })),
                            customer_name: invoiceSelectedCustomer || undefined,
                            customer_db_id: customers.find(c => c.customer_name === invoiceSelectedCustomer)?.id,
                        }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Failed to create invoice');
                    setAddInvoiceResult(data.results);
                    for (const r of data.results || []) {
                        message.success(`Invoice ${r.invoice_number} created`);
                    }
                    fetchInvoices();
                    setShowAddInvoiceModal(false);
                } catch (err) {
                    setAddInvoiceError(err.message);
                } finally {
                    setAddInvoiceSubmitting(false);
                }
            },
        });
    };

    // ========== ZOHO FUNCTIONS ==========
    const fetchStats = useCallback(async (type, from, to, limit) => {
        setStatsLoading(true);
        try {
            const params = new URLSearchParams({ from, to, limit });
            const res = await fetch(`${API_BASE_URL}/admin/statistics/${type}?${params}`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setStatsData(data.data || []);
        } catch (err) {
            setError('Failed to load statistics');
        } finally {
            setStatsLoading(false);
        }
    }, [authHeaders, handleAuthError]);

    // ========== ANALYTICS HELPERS ==========

    const getUniqueFieldValues = (orders, dbKey) =>
        [...new Set(
            (orders || []).map(o => o[dbKey]).filter(v => v != null && String(v).trim() !== '')
        )].sort();

    const getConstOptions = (fieldKey) => {
        const field = ANALYTICS_FIELDS.find(f => f.key === fieldKey);
        if (!field || !analyticsOrders) return [];
        return getUniqueFieldValues(analyticsOrders, field.dbKey);
    };

    const handleAnalyticsFilter = async () => {
        if (!analyticsFrom || !analyticsTo) {
            setError('Please select both Start Date and End Date.');
            return;
        }
        setError('');
        setAnalyticsFiltering(true);
        setAnalyticsOrders(null);
        setAnalyticsResult(null);
        setConstChecked({ ...ANALYTICS_INIT_CHECKED });
        setConstValues({ ...ANALYTICS_INIT_VALUES });
        setManipChecked({ ...ANALYTICS_INIT_CHECKED });
        setRespEnabled(false);
        setRespDtChecked(true);
        try {
            const params = new URLSearchParams({ from: analyticsFrom, to: analyticsTo, includeAll: 'true' });
            const res = await fetch(`${API_BASE_URL}/admin/string-order?${params}`, { headers: authHeaders() });
            if (handleAuthError(res)) return;
            const data = await res.json();
            setAnalyticsOrders(data.orders || []);
        } catch (err) {
            setError('Failed to fetch orders for analytics.');
        } finally {
            setAnalyticsFiltering(false);
        }
    };

    const handleConstCheck = (fieldKey, checked) => {
        const field = ANALYTICS_FIELDS.find(f => f.key === fieldKey);
        setConstChecked(prev => ({ ...prev, [fieldKey]: checked }));
        if (!checked) {
            setConstValues(prev => ({ ...prev, [fieldKey]: field?.multiSelect ? [] : '' }));
        } else {
            setManipChecked(prev => ({ ...prev, [fieldKey]: false }));
        }
    };

    const handleConstValueChange = (fieldKey, value) => {
        setConstValues(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleAnalyse = () => {
        // 1. Apply constant variable filters
        let filtered = [...(analyticsOrders || [])];
        const constFilters = [];
        ANALYTICS_FIELDS.forEach(field => {
            const val = constValues[field.key];
            if (!constChecked[field.key]) return;
            if (Array.isArray(val)) {
                if (val.length === 0) return;
                filtered = filtered.filter(o => val.includes(String(o[field.dbKey] ?? '')));
                constFilters.push({ label: field.label, value: val.join(', ') });
            } else if (val) {
                filtered = filtered.filter(o => String(o[field.dbKey] ?? '').toLowerCase() === val.toLowerCase());
                constFilters.push({ label: field.label, value: val });
            }
        });

        // 2. Selected manipulation fields
        const manipFields = ANALYTICS_FIELDS.filter(f => manipChecked[f.key]);

        // 3. Group by manipulation variable combination
        const groupMap = new Map();
        filtered.forEach(order => {
            const groupKey = manipFields.length > 0
                ? manipFields.map(f => String(order[f.dbKey] ?? '—')).join(' · ')
                : 'All Orders';
            if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
            groupMap.get(groupKey).push(order);
        });

        // 4. Build chart data
        const useDT = respEnabled && respDtChecked;
        const groupList = [...groupMap.keys()];
        let chartData;
        if (useDT) {
            // Group by (manipulation group, exact DT) — count duplicates for Z-axis bubble size
            const dtMap = new Map();
            [...groupMap.entries()].forEach(([group, orders], gIdx) => {
                orders.forEach(order => {
                    const dtVal = parseFloat(order.dt);
                    if (!isNaN(dtVal) && isFinite(dtVal)) {
                        const key = `${gIdx}|${dtVal}`;
                        if (!dtMap.has(key)) dtMap.set(key, { group, groupIdx: gIdx, dt: dtVal, count: 0 });
                        dtMap.get(key).count++;
                    }
                });
            });
            chartData = [...dtMap.values()].sort((a, b) => a.groupIdx - b.groupIdx || b.dt - a.dt);
        } else {
            chartData = [...groupMap.entries()].map(([group, orders]) => ({
                group,
                count: orders.length,
            }));
            chartData.sort((a, b) => b.count - a.count || a.group.localeCompare(b.group));
        }

        setAnalyticsResult({
            constFilters,
            manipFields,
            yAxisMode: useDT ? 'dt-individual' : 'count',
            chartData,
            groupList,
            totalFiltered: filtered.length,
        });
    };

    const fetchZohoCredentials = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/admin/zoho/credentials`, { headers: authHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            setZohoCredentials(data);
        } catch (err) {
            console.error(err);
        }
    }, [authHeaders]);

    const handleSaveZohoCredentials = async () => {
        if (!zohoForm.client_id || !zohoForm.client_secret || !zohoForm.refresh_token) {
            setError('All Zoho credential fields are required');
            return;
        }
        setZohoSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/admin/zoho/credentials`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(zohoForm),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save credentials');
            setZohoForm({ client_id: '', client_secret: '', refresh_token: '', organization_id: '' });
            fetchZohoCredentials();
        } catch (err) {
            setError(err.message);
        } finally {
            setZohoSaving(false);
        }
    };

    const handleTestZohoConnection = async () => {
        setZohoTesting(true);
        setZohoTestResult(null);
        try {
            const res = await fetch(`${API_BASE_URL}/admin/zoho/organizations`, { headers: authHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Connection failed');
            setZohoTestResult({ success: true, data });
        } catch (err) {
            setZohoTestResult({ success: false, error: err.message });
        } finally {
            setZohoTesting(false);
        }
    };

    // ========== EFFECTS ==========
    useEffect(() => {
        if (!token) { navigate('/admin'); return; }
        fetchVideos();
        fetchStrings();
        fetchProducts();
        fetchCustomers();
        fetchOrders('All');
        fetchStringers();
        fetchStringMachines();
        fetchStringPatterns();
        fetchZohoCredentials();
        fetchInvoices();
        fetchItems();
        fetchTelegramSettings();
        fetchNewArrivalsAdmin();
        fetchRacquetModels();
    }, [token, navigate, fetchVideos, fetchStrings, fetchProducts, fetchCustomers, fetchOrders, fetchStringers, fetchStringMachines, fetchStringPatterns, fetchZohoCredentials, fetchInvoices, fetchItems, fetchTelegramSettings, fetchNewArrivalsAdmin, fetchRacquetModels]);

    // Re-fetch orders whenever the Orders/String Order tab is opened, so edits made
    // elsewhere (e.g. a contact number changed in Customer Info) are reflected instead
    // of showing the stale list fetched once on initial dashboard load.
    useEffect(() => {
        if (activeTab === 'orders') fetchOrders(orderFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Correct orderPage if data refresh shrinks the total pages below current page
    useEffect(() => {
        const total = Math.max(1, Math.ceil(filterOrders(orders, orderSearch).length / ORDER_PAGE_SIZE));
        if (orderPage > total) setOrderPage(total);
    }, [orders, orderSearch, orderPage]);

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin');
    };

    const adminUsername = localStorage.getItem('adminUsername') || 'Admin';
    const activeNavItem = ADMIN_NAV_ITEMS.find(item => item.key === activeTab);
    const filteredOrders = filterOrders(orders, orderSearch);
    // Collected view always leads with the most recently collected items, tie-broken
    // by order ID — column-header sorting doesn't apply here, it's a fixed ordering.
    const sortedOrders = orderFilter === 'Collected'
        ? [...filteredOrders].sort((a, b) => {
            const at = a.collected_date ? new Date(a.collected_date).getTime() : -Infinity;
            const bt = b.collected_date ? new Date(b.collected_date).getTime() : -Infinity;
            if (at !== bt) return bt - at;
            return (a.order_id ?? 0) - (b.order_id ?? 0);
        })
        : orderSort.key ? [...filteredOrders].sort((a, b) => {
            const { key, dir } = orderSort;
            let av = a[key], bv = b[key];
            let cmp;
            if (key === 'order_id') {
                cmp = (av ?? 0) - (bv ?? 0);
            } else if (key === 'status') {
                cmp = (av || '').localeCompare(bv || '');
            } else {
                // date sort
                const an = av ? new Date(av).getTime() : (dir === 'asc' ? Infinity : -Infinity);
                const bn = bv ? new Date(bv).getTime() : (dir === 'asc' ? Infinity : -Infinity);
                cmp = an - bn;
            }
            return dir === 'asc' ? cmp : -cmp;
        }) : filteredOrders;
    const paginatedOrders = paginateOrders(sortedOrders, orderPage);
    const invoiceSearchTerm = invoiceSearch.trim().toLowerCase();
    const filteredInvoices = invoices.filter(inv => {
        const d = inv.date ? String(inv.date).slice(0, 10) : '';
        if (invoiceDateFrom && d < invoiceDateFrom) return false;
        if (invoiceDateTo && d > invoiceDateTo) return false;
        if (invoiceSearchTerm && ![inv.invoice_number, inv.customer_name, inv.reference_number]
            .some(v => String(v || '').toLowerCase().includes(invoiceSearchTerm))) return false;
        return true;
    });
    const paginatedInvoices = paginateOrders(filteredInvoices, invoicePage, 25);
    // Older Zoho invoices only link orders through their reference number.
    const invoicedOrderRefs = new Set(
        invoices.flatMap(inv =>
            (inv.reference_number || '').split(',').map(r => r.trim()).filter(Boolean)
        )
    );
    const activeInvoiceIds = new Set(invoices.filter(inv => (inv.status || '').toLowerCase() !== 'void').map(inv => inv.invoice_id));
    const isOrderInvoiced = (o) => (o.invoice_id ? activeInvoiceIds.has(o.invoice_id) : invoicedOrderRefs.has(o.order_group_ref) || invoicedOrderRefs.has(o.order_ref));

    return (
        <div className="admin-dashboard">
            <div className="admin-shell">
                {mobileSidebarOpen && (
                    <div className="admin-sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />
                )}
                <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
                    <div className="admin-sidebar-top">
                        <button
                            type="button"
                            className="admin-sidebar-toggle admin-sidebar-toggle-desktop"
                            onClick={() => setSidebarCollapsed(prev => !prev)}
                            aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
                        >
                            {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        </button>
                        <button
                            type="button"
                            className="admin-sidebar-toggle admin-sidebar-close-mobile"
                            onClick={() => setMobileSidebarOpen(false)}
                            aria-label="Close menu"
                        >
                            <MenuFoldOutlined />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="admin-sidebar-brand">
                                <span className="admin-sidebar-title">Admin Menu</span>
                                <span className="admin-user">Logged in as: {adminUsername}</span>
                            </div>
                        )}
                    </div>

                    <nav className="admin-side-nav">
                        {ADMIN_NAV_ITEMS.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`admin-side-link ${activeTab === item.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(item.key)}
                                title={sidebarCollapsed ? item.label : undefined}
                            >
                                <span className="admin-side-link-icon">{item.icon}</span>
                                <span className="admin-side-link-label">{item.label}</span>
                            </button>
                        ))}
                    </nav>

                    <button className="btn-logout admin-sidebar-logout" onClick={handleLogout}>
                        {!sidebarCollapsed ? 'Logout' : '↩'}
                    </button>
                </aside>

                <div className="admin-main">
                    <div className="admin-header">
                        <div className="admin-header-left">
                            <button
                                type="button"
                                className="admin-mobile-menu-btn"
                                onClick={() => setMobileSidebarOpen(true)}
                                aria-label="Open menu"
                            >
                                <MenuUnfoldOutlined />
                            </button>
                            <h1>Admin Dashboard</h1>
                            <span className="admin-section-caption">{activeNavItem?.label}</span>
                        </div>
                    </div>

                    {error && <div className="admin-error">{error}</div>}

                    {activeTab === 'customers' && <CustomersTab />}

                    {/* ========== VIDEOS TAB ========== */}
                    {activeTab === 'videos' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>TikTok Videos</h2>
                                <button className="btn-add" onClick={openAddVideoForm}>+ Add Video</button>
                            </div>

                            {videoLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Category</th>
                                                <th>Date</th>
                                                <th>URL</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {videos.map(v => (
                                                <tr key={v.id} className={v.enabled ? '' : 'unavailable-row'}>
                                                    <td>{v.id}</td>
                                                    <td>{v.title}</td>
                                                    <td><span className="category-tag">{v.category}</span></td>
                                                    <td>{v.date}</td>
                                                    <td className="url-cell">
                                                        <a href={v.embedUrl} target="_blank" rel="noopener noreferrer">
                                                            View
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className={`toggle-btn ${v.enabled ? 'available' : 'unavailable'}`}
                                                            onClick={() => toggleVideoEnabled(v)}
                                                        >
                                                            {v.enabled ? 'Enabled' : 'Disabled'}
                                                        </button>
                                                    </td>
                                                    <td className="action-cell">
                                                        <button className="btn-edit" onClick={() => openEditVideoForm(v)}>Edit</button>
                                                        <button className="btn-delete" onClick={() => handleVideoDelete(v.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {videos.length === 0 && (
                                        <div className="no-data">No videos found. Click "+ Add Video" to add one.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== STRINGS TAB ========== */}
                    {activeTab === 'strings' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>String Brands</h2>
                                <button className="btn-add" onClick={openAddStringForm}>+ Add String</button>
                            </div>

                            {stringLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Photo</th>
                                                <th>Brand</th>
                                                <th>Model</th>
                                                <th>Type</th>
                                                <th>Gauge</th>
                                                <th>Color Group</th>
                                                <th>Price</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {strings.map((s, index) => (
                                                <tr
                                                    key={s.id}
                                                    className={stringRowDragOver === index ? 'row-drag-over' : ''}
                                                    draggable
                                                    onDragStart={() => handleStringRowDragStart(index)}
                                                    onDragOver={(e) => handleStringRowDragOver(e, index)}
                                                    onDrop={() => handleStringRowDrop(index)}
                                                    onDragEnd={handleStringRowDragEnd}
                                                >
                                                    <td className="drag-handle-cell">
                                                        <span className="drag-handle">⠿</span>
                                                    </td>
                                                    <td>
                                                        {s.image
                                                            ? <img src={s.image} alt={s.model} className="string-thumb" />
                                                            : <div className="string-thumb-placeholder">🏸</div>
                                                        }
                                                    </td>
                                                    <td>{s.brand}</td>
                                                    <td>{s.model}</td>
                                                    <td><span className="category-tag">{s.type}</span></td>
                                                    <td>{s.gauge}</td>
                                                    <td>{readColorGroups(s).join(', ') || '—'}</td>
                                                    <td>${s.price}</td>
                                                    <td className="action-cell">
                                                        <button className="btn-edit" onClick={() => openEditStringForm(s)}>Edit</button>
                                                        <button className="btn-delete" onClick={() => handleStringDelete(s.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {strings.length === 0 && (
                                        <div className="no-data">No strings found. Click "+ Add String" to add one.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== STATIC INFO TAB ========== */}
                    {activeTab === 'stringers' && (() => {
                        const pagedStringers = paginateOrders(stringers, stringerPage, 5);
                        const pagedMachines = paginateOrders(stringMachines, machinePage, 5);
                        const pagedPatterns = paginateOrders(stringPatterns, patternPage, 5);
                        return (
                            <div className="tab-content">

                                {/* — Stringer Details panel — */}
                                <div className="section-header">
                                    <h2>Stringer Details</h2>
                                </div>

                                <div className="stringer-add-row">
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Stringer name"
                                        value={newStringerName}
                                        onChange={e => setNewStringerName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddStringer()}
                                    />
                                    <button
                                        className="btn-add"
                                        onClick={handleAddStringer}
                                        disabled={stringerSaving || !newStringerName.trim()}
                                    >
                                        {stringerSaving ? 'Adding...' : '+ Add Stringer'}
                                    </button>
                                </div>

                                {stringerLoading ? (
                                    <div className="admin-loading">Loading...</div>
                                ) : (
                                    <div className="admin-table-wrapper">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Stringer Name</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagedStringers.items.map(s => (
                                                    <tr key={s.id}>
                                                        <td>{s.name}</td>
                                                        <td className="action-cell">
                                                            <button className="btn-delete" onClick={() => handleDeleteStringer(s.id)}>Remove</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {stringers.length === 0 && (
                                            <div className="no-data">No stringers yet. Add one above.</div>
                                        )}
                                        {pagedStringers.totalPages > 1 && (
                                            <div className="table-pagination">
                                                <span className="table-pagination-summary">{stringers.length} stringers</span>
                                                <div className="table-pagination-controls">
                                                    <button className="btn-cancel table-page-btn" onClick={() => setStringerPage(p => Math.max(1, p - 1))} disabled={pagedStringers.currentPage === 1}>‹ Prev</button>
                                                    <span className="table-page-indicator">Page {pagedStringers.currentPage} of {pagedStringers.totalPages}</span>
                                                    <button className="btn-save table-page-btn" onClick={() => setStringerPage(p => Math.min(pagedStringers.totalPages, p + 1))} disabled={pagedStringers.currentPage === pagedStringers.totalPages}>Next ›</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* — Stringing Machine panel — */}
                                <div className="section-header" style={{ marginTop: 32 }}>
                                    <h2>Stringing Machine</h2>
                                </div>

                                <div className="stringer-add-row">
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Machine name"
                                        value={newMachineName}
                                        onChange={e => setNewMachineName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddMachine()}
                                    />
                                    <button
                                        className="btn-add"
                                        onClick={handleAddMachine}
                                        disabled={machineSaving || !newMachineName.trim()}
                                    >
                                        {machineSaving ? 'Adding...' : '+ Add Machine'}
                                    </button>
                                </div>

                                {machineLoading ? (
                                    <div className="admin-loading">Loading...</div>
                                ) : (
                                    <div className="admin-table-wrapper">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Machine Name</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagedMachines.items.map(m => (
                                                    <tr key={m.id}>
                                                        <td>{m.name}</td>
                                                        <td className="action-cell">
                                                            <button className="btn-delete" onClick={() => handleDeleteMachine(m.id)}>Remove</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {stringMachines.length === 0 && (
                                            <div className="no-data">No machines yet. Add one above.</div>
                                        )}
                                        {pagedMachines.totalPages > 1 && (
                                            <div className="table-pagination">
                                                <span className="table-pagination-summary">{stringMachines.length} machines</span>
                                                <div className="table-pagination-controls">
                                                    <button className="btn-cancel table-page-btn" onClick={() => setMachinePage(p => Math.max(1, p - 1))} disabled={pagedMachines.currentPage === 1}>‹ Prev</button>
                                                    <span className="table-page-indicator">Page {pagedMachines.currentPage} of {pagedMachines.totalPages}</span>
                                                    <button className="btn-save table-page-btn" onClick={() => setMachinePage(p => Math.min(pagedMachines.totalPages, p + 1))} disabled={pagedMachines.currentPage === pagedMachines.totalPages}>Next ›</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* — Stringing Pattern panel — */}
                                <div className="section-header" style={{ marginTop: 32 }}>
                                    <h2>Stringing Pattern</h2>
                                </div>

                                <div className="stringer-add-row">
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Pattern name"
                                        value={newPatternName}
                                        onChange={e => setNewPatternName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddPattern()}
                                    />
                                    <button
                                        className="btn-add"
                                        onClick={handleAddPattern}
                                        disabled={patternSaving || !newPatternName.trim()}
                                    >
                                        {patternSaving ? 'Adding...' : '+ Add Pattern'}
                                    </button>
                                </div>

                                {patternLoading ? (
                                    <div className="admin-loading">Loading...</div>
                                ) : (
                                    <div className="admin-table-wrapper">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Pattern Name</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pagedPatterns.items.map(p => (
                                                    <tr key={p.id}>
                                                        <td>{p.name}</td>
                                                        <td className="action-cell">
                                                            <button className="btn-delete" onClick={() => handleDeletePattern(p.id)}>Remove</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {stringPatterns.length === 0 && (
                                            <div className="no-data">No patterns yet. Add one above.</div>
                                        )}
                                        {pagedPatterns.totalPages > 1 && (
                                            <div className="table-pagination">
                                                <span className="table-pagination-summary">{stringPatterns.length} patterns</span>
                                                <div className="table-pagination-controls">
                                                    <button className="btn-cancel table-page-btn" onClick={() => setPatternPage(p => Math.max(1, p - 1))} disabled={pagedPatterns.currentPage === 1}>‹ Prev</button>
                                                    <span className="table-page-indicator">Page {pagedPatterns.currentPage} of {pagedPatterns.totalPages}</span>
                                                    <button className="btn-save table-page-btn" onClick={() => setPatternPage(p => Math.min(pagedPatterns.totalPages, p + 1))} disabled={pagedPatterns.currentPage === pagedPatterns.totalPages}>Next ›</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>
                        );
                    })()}

                    {/* ========== ZOHO INTEGRATION TAB ========== */}
                    {activeTab === 'zoho' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>Zoho Integration</h2>
                            </div>

                            {zohoCredentials?.configured && (
                                <div className="zoho-status-card">
                                    <div className="zoho-status-label">Current Credentials</div>
                                    <div className="zoho-status-row"><span>Client ID</span><code>{zohoCredentials.client_id}</code></div>
                                    <div className="zoho-status-row"><span>Client Secret</span><code>{zohoCredentials.client_secret}</code></div>
                                    <div className="zoho-status-row"><span>Refresh Token</span><code>{zohoCredentials.refresh_token}</code></div>
                                    <div className="zoho-status-row"><span>Org ID</span><code>{zohoCredentials.organization_id || '—'}</code></div>
                                    {zohoCredentials.access_token_expires_at && (
                                        <div className="zoho-status-row"><span>Token Expires</span><code>{new Date(zohoCredentials.access_token_expires_at).toLocaleString()}</code></div>
                                    )}
                                    <button
                                        className="btn-add"
                                        style={{ marginTop: 12 }}
                                        onClick={handleTestZohoConnection}
                                        disabled={zohoTesting}
                                    >
                                        {zohoTesting ? 'Testing...' : 'Test Connection'}
                                    </button>
                                    {zohoTestResult && (
                                        <div className={`zoho-test-result ${zohoTestResult.success ? 'success' : 'error'}`}>
                                            {zohoTestResult.success
                                                ? `Connected — ${zohoTestResult.data?.organizations?.length ?? 0} organisation(s) found`
                                                : `Error: ${zohoTestResult.error}`}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="zoho-form-card">
                                <div className="zoho-status-label">{zohoCredentials?.configured ? 'Update Credentials' : 'Configure Credentials'}</div>
                                <div className="stringer-add-row" style={{ flexDirection: 'column', gap: 10 }}>
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Client ID"
                                        value={zohoForm.client_id}
                                        onChange={e => setZohoForm(p => ({ ...p, client_id: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Client Secret"
                                        value={zohoForm.client_secret}
                                        onChange={e => setZohoForm(p => ({ ...p, client_secret: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Refresh Token"
                                        value={zohoForm.refresh_token}
                                        onChange={e => setZohoForm(p => ({ ...p, refresh_token: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Organization ID (e.g. 909968311)"
                                        value={zohoForm.organization_id}
                                        onChange={e => setZohoForm(p => ({ ...p, organization_id: e.target.value }))}
                                    />
                                    <button
                                        className="btn-add"
                                        onClick={handleSaveZohoCredentials}
                                        disabled={zohoSaving || !zohoForm.client_id || !zohoForm.client_secret || !zohoForm.refresh_token}
                                    >
                                        {zohoSaving ? 'Saving...' : 'Save Credentials'}
                                    </button>
                                </div>
                            </div>

                            {/* ── Telegram Bot ── */}
                            <div className="section-header" style={{ marginTop: 32 }}>
                                <h2 style={{ fontSize: '1.1rem' }}>Telegram Bot</h2>
                            </div>

                            {telegramSettings?.configured && (
                                <div className="zoho-status-card">
                                    <div className="zoho-status-label">Current Configuration</div>
                                    <div className="zoho-status-row"><span>Group Name</span><code>{telegramSettings.group_name || '—'}</code></div>
                                    <div className="zoho-status-row"><span>Chat ID</span><code>{telegramSettings.chat_id}</code></div>
                                    <div className="zoho-status-row"><span>Bot Token</span><code>{telegramSettings.token_masked}</code></div>
                                    {telegramSettings.updated_at && (
                                        <div className="zoho-status-row"><span>Last Updated</span><code>{new Date(telegramSettings.updated_at).toLocaleString()}</code></div>
                                    )}
                                    <div style={{ marginTop: 12 }}>
                                        <button
                                            className="btn-add"
                                            onClick={handleRestartTelegramBot}
                                            disabled={telegramRestarting}
                                            style={{ marginRight: 8 }}
                                        >
                                            {telegramRestarting ? 'Restarting...' : 'Restart Bot / Re-register Webhook'}
                                        </button>
                                        {telegramRestartResult?.success && (
                                            <span style={{ color: '#389e0d', fontSize: '0.85rem' }}>Bot restarted successfully</span>
                                        )}
                                        {telegramRestartResult?.error && (
                                            <span style={{ color: '#cf1322', fontSize: '0.85rem' }}>{telegramRestartResult.error}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="zoho-form-card">
                                <div className="zoho-status-label">{telegramSettings?.configured ? 'Update Bot Settings' : 'Configure Bot'}</div>
                                <div className="stringer-add-row" style={{ flexDirection: 'column', gap: 10 }}>
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Bot Token (from @BotFather)"
                                        value={telegramForm.bot_token}
                                        onChange={e => setTelegramForm(p => ({ ...p, bot_token: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Group Name (e.g. TestingTesting)"
                                        value={telegramForm.group_name}
                                        onChange={e => setTelegramForm(p => ({ ...p, group_name: e.target.value }))}
                                    />
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="text"
                                            className="order-search-input"
                                            placeholder="Chat ID (e.g. -1001234567890)"
                                            value={telegramForm.chat_id}
                                            onChange={e => setTelegramForm(p => ({ ...p, chat_id: e.target.value }))}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            className="btn-db"
                                            onClick={handleFindTelegramChat}
                                            disabled={telegramFinding}
                                            title="Scan recent bot updates to find group chat IDs"
                                        >
                                            {telegramFinding ? 'Scanning...' : '🔍 Find'}
                                        </button>
                                    </div>
                                    {telegramFoundChats !== null && (
                                        <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', fontSize: '0.88rem' }}>
                                            {telegramFoundChats.length === 0 ? (
                                                <span style={{ color: '#888' }}>No groups found. Send any message in the group first, then scan again.</span>
                                            ) : (
                                                <>
                                                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#333' }}>Groups found — click to fill:</div>
                                                    {telegramFoundChats.map(chat => (
                                                        <div
                                                            key={chat.id}
                                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                                                            onClick={() => setTelegramForm(p => ({ ...p, chat_id: chat.id, group_name: p.group_name || chat.title }))}
                                                        >
                                                            <span style={{ fontWeight: 500 }}>{chat.title}</span>
                                                            <code style={{ fontSize: '0.8rem', color: '#555' }}>{chat.id}</code>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        className="btn-add"
                                        onClick={handleSaveTelegramSettings}
                                        disabled={telegramSaving || !telegramForm.bot_token || !telegramForm.chat_id}
                                    >
                                        {telegramSaving ? 'Saving...' : 'Save Bot Settings'}
                                    </button>
                                </div>
                            </div>

                            {telegramSettings?.configured && (
                                <>
                                    <div className="zoho-form-card">
                                        <div className="zoho-status-label">Send Message</div>
                                        <div className="stringer-add-row" style={{ flexDirection: 'column', gap: 10 }}>
                                            <textarea
                                                className="order-search-input"
                                                placeholder="Type your message… (HTML: <b>bold</b>, <i>italic</i>)"
                                                value={telegramMessage}
                                                onChange={e => setTelegramMessage(e.target.value)}
                                                rows={4}
                                                style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                            />
                                            {telegramSendResult && (
                                                <div className={`zoho-test-result ${telegramSendResult.success ? 'success' : 'error'}`}>
                                                    {telegramSendResult.success ? '✅ Message sent to Telegram group!' : `❌ ${telegramSendResult.error}`}
                                                </div>
                                            )}
                                            <button
                                                className="btn-add"
                                                onClick={handleSendTelegramMessage}
                                                disabled={telegramSending || !telegramMessage.trim()}
                                            >
                                                {telegramSending ? 'Sending...' : '✈ Send to Telegram'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ========== INVOICING TAB ========== */}
                    {activeTab === 'invoicing' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>Invoicing</h2>
                                <div className="section-header-actions">
                                    <button className="btn-add" onClick={() => {
                                        setShowAddInvoiceModal(true);
                                        setSelectedOrderIds(new Set());
                                        setInvoiceOrderSearch('');
                                        setInvoiceNewDate(new Date().toISOString().slice(0, 10));
                                        setInvoiceNewBalance('');
                                        setInvoiceNewDiscount('');
                                        setExpandedInvoiceGroups(new Set());
                                        setAddInvoiceError('');
                                        setAddInvoiceResult(null);
                                        setInvoiceOtherItems([]);
                                        setInvoiceSelectedCustomer(null);
                                        fetchAllOrdersForInvoice();
                                    }}>+ Add Invoice</button>
                                    <button
                                        className="btn-add"
                                        onClick={handleSyncInvoices}
                                        disabled={invoiceSyncing}
                                        style={{ background: invoiceSyncing ? '#ccc' : '#c41e3a' }}
                                    >
                                        {invoiceSyncing ? 'Syncing...' : 'Sync Invoice'}
                                    </button>
                                </div>
                            </div>

                            {/* Date range filter */}
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                                <label style={{ fontSize: '0.88rem', color: '#555', whiteSpace: 'nowrap' }}>Invoice Date:</label>
                                <input
                                    type="date"
                                    className="order-search-input"
                                    style={{ width: 160 }}
                                    value={invoiceDateFrom}
                                    onChange={e => { setInvoiceDateFrom(e.target.value); setInvoicePage(1); }}
                                />
                                <span style={{ color: '#888', fontSize: '0.88rem' }}>to</span>
                                <input
                                    type="date"
                                    className="order-search-input"
                                    style={{ width: 160 }}
                                    value={invoiceDateTo}
                                    onChange={e => { setInvoiceDateTo(e.target.value); setInvoicePage(1); }}
                                />
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                                    onClick={() => { setInvoiceDateFrom(defaultInvoiceDateFrom()); setInvoiceDateTo(defaultInvoiceDateTo()); setInvoiceSearch(''); setInvoicePage(1); }}
                                >
                                    Reset
                                </button>
                                <input
                                    type="text"
                                    className="order-search-input"
                                    style={{ width: 240 }}
                                    placeholder="Search customer, invoice # or order ref…"
                                    value={invoiceSearch}
                                    onChange={e => { setInvoiceSearch(e.target.value); setInvoicePage(1); }}
                                />
                            </div>

                            {invoiceSyncResult && (
                                <div className={`zoho-test-result ${invoiceSyncResult.success ? 'success' : 'error'}`} style={{ marginBottom: 16 }}>
                                    {invoiceSyncResult.success
                                        ? `Sync complete — ${invoiceSyncResult.synced} invoice(s), ${invoiceSyncResult.itemsSynced} line item(s) saved`
                                        : `Sync error: ${invoiceSyncResult.error}`}
                                </div>
                            )}

                            {invoiceLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : invoices.length === 0 ? (
                                <div className="no-data">No invoices yet. Invoices are created automatically with each string order, or click "+ Add Invoice".</div>
                            ) : filteredInvoices.length === 0 ? (
                                <div className="no-data">No invoices match the selected date range.</div>
                            ) : (
                                <>
                                    <div className="admin-table-wrapper">
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 30 }}></th>
                                                    <th>Invoice #</th>
                                                    <th>String Order</th>
                                                    <th>Customer</th>
                                                    <th>Date</th>
                                                    <th>Due Date</th>
                                                    <th>Status</th>
                                                    <th>Payment Date</th>
                                                    <th>Total</th>
                                                    <th>Balance</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedInvoices.items.map(inv => {
                                                    const statusStyle = (() => {
                                                        switch ((inv.status || '').toLowerCase()) {
                                                            case 'paid': return { background: '#d4edda', color: '#155724' };
                                                            case 'overdue': return { background: '#f8d7da', color: '#721c24' };
                                                            case 'active': return { background: '#d4f7e8', color: '#0a6640' };
                                                            case 'sent':
                                                            case 'unpaid': return { background: '#cce5ff', color: '#004085' };
                                                            case 'draft': return { background: '#e2e3e5', color: '#383d41' };
                                                            case 'void': return { background: '#f0f0f0', color: '#666' };
                                                            case 'partially_paid': return { background: '#fff3cd', color: '#856404' };
                                                            default: return { background: '#e9ecef', color: '#495057' };
                                                        }
                                                    })();
                                                    const isExpanded = expandedInvoiceId === inv.invoice_id;
                                                    const isLocal = String(inv.invoice_id).startsWith('LOCAL-');
                                                    const isVoid = (inv.status || '').toLowerCase() === 'void';
                                                    return (
                                                        <React.Fragment key={inv.invoice_id}>
                                                            <tr
                                                                className="order-row-clickable"
                                                                onClick={() => setExpandedInvoiceId(isExpanded ? null : inv.invoice_id)}
                                                            >
                                                                <td style={{ textAlign: 'center', color: '#aaa', fontSize: '0.75rem' }}>
                                                                    {isExpanded ? '▲' : '▼'}
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                                        <code style={{ fontSize: '0.82rem' }}>{inv.invoice_number || '—'}</code>
                                                                        {!isLocal && (
                                                                            <span title="Created in Zoho Books before invoicing moved into this system" style={{ fontSize: '0.68rem', background: '#f0f0f0', color: '#666', border: '1px solid #d9d9d9', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap', cursor: 'help' }}>
                                                                                Zoho
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {(inv.reference_number || '').split(',').map(r => r.trim()).filter(r => /^TT\d+$/.test(r)).map(r => (
                                                                        <span key={r} className="order-ref-badge" style={{ background: '#faad14', color: '#000', marginRight: 4 }}>{r}</span>
                                                                    ))}
                                                                    {!/TT\d+/.test(inv.reference_number || '') && <span style={{ color: '#bbb' }}>—</span>}
                                                                </td>
                                                                <td>{inv.customer_name || '—'}</td>
                                                                <td>{inv.date ? String(inv.date).slice(0, 10) : '—'}</td>
                                                                <td>{inv.due_date ? String(inv.due_date).slice(0, 10) : '—'}</td>
                                                                <td>
                                                                    <span className="order-status-pill" style={statusStyle}>
                                                                        {invoiceStatusLabel(inv.status)}
                                                                    </span>
                                                                </td>
                                                                <td>{inv.payment_date ? String(inv.payment_date).slice(0, 10) : '—'}</td>
                                                                <td style={{ fontWeight: 600 }}>
                                                                    {inv.currency_code} {Number(inv.total).toFixed(2)}
                                                                </td>
                                                                <td style={{ color: Number(inv.balance) > 0 ? '#c41e3a' : '#28a745', fontWeight: 600 }}>
                                                                    {inv.currency_code} {Number(inv.balance).toFixed(2)}
                                                                </td>
                                                                <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                                        {!isVoid && (
                                                                            <Button size="small" onClick={() => openAddItemsModal(inv)}>+ Items</Button>
                                                                        )}
                                                                        {isLocal && !isVoid && (
                                                                            <Button size="small" onClick={() => openDiscountModal(inv)}>Discount</Button>
                                                                        )}
                                                                        {!isVoid && Number(inv.balance) > 0 && (
                                                                            <Button size="small" onClick={() => openPaymentModal(inv)}>Pay</Button>
                                                                        )}
                                                                        <Button size="small" onClick={() => handlePrintInvoice(inv)}>Print</Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {isExpanded && (
                                                                <tr>
                                                                    <td colSpan={11} style={{ padding: 0, background: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                                                                        <div style={{ padding: '12px 20px' }}>
                                                                            {inv.line_items && inv.line_items.length > 0 ? (
                                                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                                                    <thead>
                                                                                        <tr style={{ background: '#f0f0f0' }}>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Item</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Rate</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Qty</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Discount</th>
                                                                                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Total</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {inv.line_items.map((item, idx) => (
                                                                                            <tr key={item.line_item_id || idx} style={{ borderTop: '1px solid #e8e8e8', color: isDiscountLine(item) ? '#c41e3a' : undefined }}>
                                                                                                <td style={{ padding: '8px 12px' }}>{item.name || '—'}</td>
                                                                                                <td style={{ padding: '8px 12px', color: '#666' }}>{item.description || '—'}</td>
                                                                                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{Number(item.rate).toFixed(2)}</td>
                                                                                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{Number(item.quantity).toFixed(0)}</td>
                                                                                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{Number(item.discount).toFixed(2)}</td>
                                                                                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{Number(item.item_total).toFixed(2)}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            ) : (
                                                                                <div style={{ color: '#888', fontSize: '0.88rem' }}>No line items.</div>
                                                                            )}

                                                                            {isLocal && (inv.payments || []).length > 0 && (
                                                                                <div style={{ marginTop: 14 }}>
                                                                                    <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>Payments</div>
                                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                                                        <tbody>
                                                                                            {inv.payments.map(pay => (
                                                                                                <tr key={pay.id} style={{ borderTop: '1px solid #e8e8e8' }}>
                                                                                                    <td style={{ padding: '6px 12px', width: 110 }}>{pay.payment_date}</td>
                                                                                                    <td style={{ padding: '6px 12px', width: 130 }}>{PAYMENT_METHOD_LABELS[pay.method] || (Number(pay.amount) < 0 ? 'Reversal' : '—')}</td>
                                                                                                    <td style={{ padding: '6px 12px', color: '#666' }}>{pay.note || ''}</td>
                                                                                                    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: Number(pay.amount) < 0 ? '#c41e3a' : '#222' }}>
                                                                                                        {Number(pay.amount).toFixed(2)}
                                                                                                    </td>
                                                                                                    <td style={{ padding: '6px 12px', width: 40, textAlign: 'right' }}>
                                                                                                        {!isVoid && pay.method && (
                                                                                                            <Button size="small" type="text" danger title="Remove this payment" onClick={() => handleDeletePayment(inv, pay)}>✕</Button>
                                                                                                        )}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            )}

                                                                            {isLocal && !isVoid && (
                                                                                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                                                                                    <Button size="small" danger onClick={() => handleVoidInvoice(inv)}>
                                                                                        Void
                                                                                    </Button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-pagination">
                                        <span className="table-pagination-summary">
                                            Showing {paginatedInvoices.items.length} of {paginatedInvoices.totalItems} invoice(s)
                                        </span>
                                        <div className="table-pagination-controls">
                                            <button
                                                type="button"
                                                className="btn-cancel table-page-btn"
                                                onClick={() => setInvoicePage(prev => Math.max(1, prev - 1))}
                                                disabled={paginatedInvoices.currentPage === 1}
                                            >
                                                Previous
                                            </button>
                                            <span className="table-page-indicator">
                                                Page {paginatedInvoices.currentPage} of {paginatedInvoices.totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn-save table-page-btn"
                                                onClick={() => setInvoicePage(prev => Math.min(paginatedInvoices.totalPages, prev + 1))}
                                                disabled={paginatedInvoices.currentPage === paginatedInvoices.totalPages}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {showAddInvoiceModal && (() => {
                        // Unique customers from pending (non-invoiced) orders
                        const pendingCustomerMap = {};
                        for (const o of invoiceModalOrders) {
                            if (isOrderInvoiced(o)) continue;
                            if (!pendingCustomerMap[o.customer_name]) {
                                pendingCustomerMap[o.customer_name] = o.contact_number;
                            }
                        }
                        // Also include all known customers (for additional-items-only invoices)
                        const customerSuggestions = [
                            ...Object.entries(pendingCustomerMap).map(([name, contact]) => ({ name, contact, hasPending: true })),
                            ...customers
                                .filter(c => !pendingCustomerMap[c.customer_name])
                                .map(c => ({ name: c.customer_name, contact: c.contact_number, hasPending: false })),
                        ].filter(c => !invoiceSelectedCustomer || c.name.toLowerCase().includes(invoiceSelectedCustomer.toLowerCase()));

                        // Orders for the selected customer (non-invoiced only)
                        const customerOrders = invoiceSelectedCustomer
                            ? invoiceModalOrders.filter(o => o.customer_name === invoiceSelectedCustomer && !isOrderInvoiced(o))
                            : [];
                        // The customer's invoices that can still take more items
                        const customerOpenInvoices = invoiceSelectedCustomer
                            ? invoices.filter(inv => inv.customer_name === invoiceSelectedCustomer && !['void', 'paid'].includes((inv.status || '').toLowerCase()))
                            : [];

                        const toggleOrder = (orderId) => {
                            const next = new Set(selectedOrderIds);
                            next.has(orderId) ? next.delete(orderId) : next.add(orderId);
                            setSelectedOrderIds(next);
                        };

                        const selectAllOrders = () => {
                            const next = new Set(selectedOrderIds);
                            customerOrders.filter(o => parseFloat(o.fees) > 0).forEach(o => next.add(o.order_id));
                            setSelectedOrderIds(next);
                        };

                        const clearAllOrders = () => {
                            const next = new Set(selectedOrderIds);
                            customerOrders.forEach(o => next.delete(o.order_id));
                            setSelectedOrderIds(next);
                        };

                        // Totals
                        const ordersTotal = Array.from(selectedOrderIds).reduce((sum, id) => {
                            const o = invoiceModalOrders.find(ord => ord.order_id === id);
                            return sum + (o ? parseFloat(o.fees) || 0 : 0);
                        }, 0);
                        const validOtherItemsForTotal = invoiceOtherItems.filter(it => it.description.trim() && parseFloat(it.price) > 0);
                        const otherTotal = validOtherItemsForTotal.reduce((sum, it) => sum + parseFloat(it.price), 0);
                        const newDiscount = invoiceNewDiscount !== '' ? Number(invoiceNewDiscount) || 0 : 0;
                        const grandTotal = Math.max(0, ordersTotal + otherTotal - newDiscount);

                        const allCustomerOrdersSelected = customerOrders.length > 0 &&
                            customerOrders.filter(o => parseFloat(o.fees) > 0).every(o => selectedOrderIds.has(o.order_id));

                        return (
                            <div className="modal-overlay" onClick={() => { if (!addInvoiceSubmitting) setShowAddInvoiceModal(false); }}>
                                <div className="modal-content" style={{ maxWidth: 720, width: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                                    <button className="modal-close" onClick={() => setShowAddInvoiceModal(false)}>×</button>
                                    <h3 style={{ marginBottom: 16 }}>Add Invoice</h3>

                                    {/* Step 1: Customer selector */}
                                    <div style={{ marginBottom: 14 }}>
                                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#444', marginBottom: 5 }}>Customer</div>
                                        <AutoComplete
                                            style={{ width: '100%' }}
                                            size="large"
                                            value={invoiceSelectedCustomer || ''}
                                            placeholder="Search customer name…"
                                            options={customerSuggestions.slice(0, 30).map(c => ({
                                                value: c.name,
                                                label: (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span>{c.name}</span>
                                                        <span style={{ fontSize: '0.78rem', color: '#aaa' }}>
                                                            {c.contact}{c.hasPending ? ' · pending orders' : ''}
                                                        </span>
                                                    </div>
                                                ),
                                            }))}
                                            onChange={val => {
                                                setInvoiceSelectedCustomer(val || null);
                                                setSelectedOrderIds(new Set());
                                            }}
                                            onSelect={val => {
                                                setInvoiceSelectedCustomer(val);
                                                setSelectedOrderIds(new Set());
                                            }}
                                            allowClear
                                            onClear={() => { setInvoiceSelectedCustomer(null); setSelectedOrderIds(new Set()); }}
                                        />
                                    </div>

                                    {customerOpenInvoices.length > 0 && (
                                        <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: '#fffbe6', border: '1px solid #ffe58f' }}>
                                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#614700', marginBottom: 6 }}>
                                                This customer already has open invoices — add items to one instead of creating a new invoice:
                                            </div>
                                            {customerOpenInvoices.slice(0, 5).map(inv => (
                                                <div key={inv.invoice_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                                                    <span style={{ fontSize: '0.82rem', color: '#444' }}>
                                                        <code>{inv.invoice_number}</code> · {inv.date ? String(inv.date).slice(0, 10) : '—'} · SGD {Number(inv.total).toFixed(2)}
                                                        {inv.reference_number ? ` · ${inv.reference_number}` : ''}
                                                    </span>
                                                    <Button size="small" onClick={() => { setShowAddInvoiceModal(false); openAddItemsModal(inv); }}>
                                                        Add items here
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Step 2: String orders for selected customer */}
                                    <div style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#444' }}>
                                                String Orders
                                                {customerOrders.length > 0 && (
                                                    <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>({customerOrders.length} found)</span>
                                                )}
                                            </div>
                                            {customerOrders.filter(o => parseFloat(o.fees) > 0).length > 0 && (
                                                <div style={{ display: 'flex', gap: 10 }}>
                                                    <button
                                                        type="button"
                                                        style={{ fontSize: '0.75rem', color: '#1677ff', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                        onClick={allCustomerOrdersSelected ? clearAllOrders : selectAllOrders}
                                                    >
                                                        {allCustomerOrdersSelected ? 'Deselect all' : 'Select all'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, minHeight: 72, maxHeight: 220, overflowY: 'auto' }}>
                                            {!invoiceSelectedCustomer ? (
                                                <div style={{ padding: '18px 16px', color: '#bbb', fontSize: '0.85rem', textAlign: 'center' }}>
                                                    Select a customer above to see their pending string orders
                                                </div>
                                            ) : invoiceModalLoading ? (
                                                <div style={{ padding: '18px 16px', color: '#888', textAlign: 'center' }}>Loading orders…</div>
                                            ) : customerOrders.length === 0 ? (
                                                <div style={{ padding: '18px 16px', color: '#bbb', fontSize: '0.85rem', textAlign: 'center' }}>
                                                    No pending string orders for this customer
                                                </div>
                                            ) : customerOrders.map((o, idx) => {
                                                const hasFee = parseFloat(o.fees) > 0;
                                                const isSelected = selectedOrderIds.has(o.order_id);
                                                return (
                                                    <div
                                                        key={o.order_id}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: 10,
                                                            padding: '10px 14px',
                                                            borderBottom: idx < customerOrders.length - 1 ? '1px solid #f5f5f5' : 'none',
                                                            background: isSelected ? '#f0f7ff' : hasFee ? '#fff' : '#fafafa',
                                                            opacity: hasFee ? 1 : 0.5,
                                                            cursor: hasFee ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                        }}
                                                        onClick={() => hasFee && toggleOrder(o.order_id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => hasFee && toggleOrder(o.order_id)}
                                                            disabled={!hasFee}
                                                            style={{ width: 15, height: 15, flexShrink: 0, cursor: hasFee ? 'pointer' : 'not-allowed', accentColor: '#1677ff' }}
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                <code style={{ fontSize: '0.74rem', background: '#f5f5f5', padding: '1px 6px', borderRadius: 4, color: '#555' }}>{o.order_ref}</code>
                                                                <span style={{ fontSize: '0.83rem', color: '#222', fontWeight: 500 }}>
                                                                    {[o.racquet_brand, o.racquet_model].filter(Boolean).join(' ')}
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: '0.74rem', color: '#999', marginTop: 2 }}>
                                                                {[o.string_brand, o.string_model, o.tension ? `${o.tension} lbs` : null].filter(Boolean).join(' · ')}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                                            {hasFee ? (
                                                                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: isSelected ? '#1677ff' : '#333' }}>
                                                                    SGD {parseFloat(o.fees).toFixed(2)}
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span style={{ fontSize: '0.85rem', color: '#ccc' }}>—</span>
                                                                    <span style={{ fontSize: '0.7rem', background: '#fff2f0', color: '#cf1322', border: '1px solid #ffccc7', borderRadius: 4, padding: '1px 5px' }}>No fee</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Additional Items */}
                                    <div style={{ marginBottom: 14, border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: invoiceOtherItems.length > 0 ? 8 : 0 }}>
                                            <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#444' }}>Additional Items</span>
                                            <Button size="small" onClick={() => setInvoiceOtherItems(prev => [...prev, { id: Date.now(), description: '', price: '' }])}>
                                                + Add Item
                                            </Button>
                                        </div>
                                        {invoiceOtherItems.length === 0 ? (
                                            <div style={{ fontSize: '0.78rem', color: '#ccc' }}>No additional items — click "+ Add Item" to include custom charges.</div>
                                        ) : (
                                            invoiceOtherItems.map((item, idx) => (
                                                <div key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: idx === 0 ? 0 : 8 }}>
                                                    <AutoComplete
                                                        value={item.description}
                                                        onChange={val => setInvoiceOtherItems(prev => prev.map(it => it.id === item.id ? { ...it, description: val, item_id: null } : it))}
                                                        onSelect={(val, option) => setInvoiceOtherItems(prev => prev.map(it => it.id === item.id ? { ...it, description: val, item_id: option.itemId, price: option.rate != null ? String(option.rate) : it.price } : it))}
                                                        options={(zohoItems || [])
                                                            .filter(zi => !item.description || zi.name.toLowerCase().includes(item.description.toLowerCase()))
                                                            .slice(0, 20)
                                                            .map(zi => ({ value: zi.name, label: zi.name, rate: zi.rate, itemId: zi.item_id }))}
                                                        placeholder="Search item…"
                                                        style={{ flex: 3 }}
                                                    />
                                                    <Input
                                                        placeholder="Price"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.price}
                                                        onChange={e => setInvoiceOtherItems(prev => prev.map(it => it.id === item.id ? { ...it, price: e.target.value } : it))}
                                                        prefix="SGD"
                                                        style={{ flex: 2 }}
                                                    />
                                                    <Button size="small" danger onClick={() => setInvoiceOtherItems(prev => prev.filter(it => it.id !== item.id))}>✕</Button>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Invoice total summary */}
                                    <div style={{ marginBottom: 14, padding: '10px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #dce8ff' }}>
                                        {selectedOrderIds.size > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                <span style={{ fontSize: '0.82rem', color: '#666' }}>
                                                    String orders ({selectedOrderIds.size})
                                                </span>
                                                <span style={{ fontSize: '0.82rem', color: '#444' }}>SGD {ordersTotal.toFixed(2)}</span>
                                            </div>
                                        )}
                                        {validOtherItemsForTotal.length > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                <span style={{ fontSize: '0.82rem', color: '#666' }}>
                                                    Additional items ({validOtherItemsForTotal.length})
                                                </span>
                                                <span style={{ fontSize: '0.82rem', color: '#444' }}>SGD {otherTotal.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontSize: '0.82rem', color: '#666' }}>Discount</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                value={invoiceNewDiscount}
                                                onChange={e => setInvoiceNewDiscount(e.target.value)}
                                                className="order-search-input"
                                                style={{ width: 110, fontSize: '0.82rem', textAlign: 'right' }}
                                            />
                                        </div>
                                        {newDiscount > ordersTotal + otherTotal + 0.001 && (
                                            <div style={{ fontSize: '0.8rem', color: '#cf1322', marginBottom: 4 }}>Discount can't be more than SGD {(ordersTotal + otherTotal).toFixed(2)}.</div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: (selectedOrderIds.size > 0 || validOtherItemsForTotal.length > 0) ? '1px solid #d0ddff' : 'none', paddingTop: (selectedOrderIds.size > 0 || validOtherItemsForTotal.length > 0) ? 6 : 0, marginTop: (selectedOrderIds.size > 0 || validOtherItemsForTotal.length > 0) ? 4 : 0 }}>
                                            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#222' }}>Invoice Total</span>
                                            <span style={{ fontWeight: 700, fontSize: '1.1rem', color: grandTotal > 0 ? '#1677ff' : '#999' }}>
                                                SGD {grandTotal.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Invoice Date + Balance */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: '0.88rem', color: '#555', whiteSpace: 'nowrap' }}>Invoice Date:</span>
                                            <input
                                                type="date"
                                                value={invoiceNewDate}
                                                onChange={e => setInvoiceNewDate(e.target.value)}
                                                className="order-search-input"
                                                style={{ width: 160, fontSize: '0.88rem' }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: '0.88rem', color: '#555', whiteSpace: 'nowrap' }}>Balance:</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                value={invoiceNewBalance}
                                                onChange={e => setInvoiceNewBalance(e.target.value)}
                                                className="order-search-input"
                                                style={{ width: 110, fontSize: '0.88rem' }}
                                            />
                                        </div>
                                    </div>

                                    {/* Error / result */}
                                    {addInvoiceError && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7', fontSize: '0.85rem', color: '#cf1322' }}>
                                            {addInvoiceError}
                                        </div>
                                    )}
                                    {addInvoiceResult && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#f6ffed', border: '1px solid #b7eb8f', fontSize: '0.85rem', color: '#389e0d' }}>
                                            {addInvoiceResult.map((r, i) => (
                                                <div key={i}>✓ {r.customer} — {r.invoice_number}{r.zoho ? ' (Zoho)' : ' (local only)'}</div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <Button size="large" style={{ flex: 1 }} onClick={() => setShowAddInvoiceModal(false)} disabled={addInvoiceSubmitting}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="large"
                                            style={{ flex: 2 }}
                                            loading={addInvoiceSubmitting}
                                            disabled={selectedOrderIds.size === 0 && invoiceOtherItems.filter(it => it.description.trim() && parseFloat(it.price) > 0).length === 0}
                                            onClick={handleAddInvoiceSubmit}
                                        >
                                            {addInvoiceResult ? 'Done' : 'Create Invoice'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {addItemsInvoice && (() => {
                        const inv = addItemsInvoice;
                        const isLocalInv = String(inv.invoice_id).startsWith('LOCAL-');
                        const validRows = addItemsRows.filter(it => it.description.trim() && parseFloat(it.price) > 0);
                        const addedTotal = validRows.reduce((sum, it) => sum + parseFloat(it.price) * (parseInt(it.quantity, 10) || 1), 0);
                        const updateRow = (id, patch) => setAddItemsRows(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
                        const close = () => { if (!addItemsSubmitting) setAddItemsInvoice(null); };
                        return (
                            <div className="modal-overlay" onClick={close}>
                                <div className="modal-content" style={{ maxWidth: 720, width: '95vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                                    <button className="modal-close" onClick={close}>×</button>
                                    <h3 style={{ marginBottom: 4 }}>Add Items to Invoice</h3>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 14 }}>
                                        <code>{inv.invoice_number}</code> · {inv.customer_name || '—'}
                                        {inv.reference_number ? ` · ${inv.reference_number}` : ''}
                                        {isLocalInv ? '' : ' · Zoho Books'}
                                    </div>

                                    {/* Current items */}
                                    <div style={{ marginBottom: 14, border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px' }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#444', marginBottom: 6 }}>Current items</div>
                                        {(inv.line_items || []).length === 0 ? (
                                            <div style={{ fontSize: '0.8rem', color: '#bbb' }}>No items yet.</div>
                                        ) : inv.line_items.map((li, idx) => (
                                            <div key={li.id || idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.82rem', padding: '3px 0' }}>
                                                <span style={{ color: '#333', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={li.description || ''}>
                                                    {li.name || li.description}{Number(li.quantity) > 1 ? ` × ${Number(li.quantity)}` : ''}
                                                </span>
                                                <span style={{ color: '#444', flexShrink: 0 }}>SGD {Number(li.item_total).toFixed(2)}</span>
                                            </div>
                                        ))}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', marginTop: 6, paddingTop: 6, fontSize: '0.82rem' }}>
                                            <span style={{ color: '#666' }}>Total · Balance</span>
                                            <span style={{ fontWeight: 600 }}>SGD {Number(inv.total).toFixed(2)} · <span style={{ color: Number(inv.balance) > 0 ? '#c41e3a' : '#28a745' }}>SGD {Number(inv.balance).toFixed(2)}</span></span>
                                        </div>
                                    </div>

                                    {/* New items */}
                                    <div style={{ marginBottom: 14, border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#444' }}>New items</span>
                                            <Button size="small" onClick={() => setAddItemsRows(prev => [...prev, { id: Date.now(), description: '', price: '', quantity: 1, item_id: null }])}>
                                                + Add Item
                                            </Button>
                                        </div>
                                        {addItemsRows.map((item, idx) => (
                                            <div key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: idx === 0 ? 0 : 8 }}>
                                                <AutoComplete
                                                    value={item.description}
                                                    onChange={val => updateRow(item.id, { description: val, item_id: null })}
                                                    onSelect={(val, option) => updateRow(item.id, { description: val, item_id: option.itemId, ...(option.rate != null ? { price: String(option.rate) } : {}) })}
                                                    options={(zohoItems || [])
                                                        .filter(zi => !item.description || (zi.name || '').toLowerCase().includes(item.description.toLowerCase()))
                                                        .slice(0, 20)
                                                        .map(zi => ({ value: zi.name, label: zi.name, rate: zi.rate, itemId: zi.item_id }))}
                                                    placeholder="Item (e.g. shoes, socks, racquet)…"
                                                    style={{ flex: 3 }}
                                                />
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={item.quantity}
                                                    onChange={e => updateRow(item.id, { quantity: e.target.value })}
                                                    prefix="Qty"
                                                    style={{ flex: 1, minWidth: 80 }}
                                                />
                                                <Input
                                                    placeholder="Price"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.price}
                                                    onChange={e => updateRow(item.id, { price: e.target.value })}
                                                    prefix="SGD"
                                                    style={{ flex: 2 }}
                                                />
                                                <Button size="small" danger disabled={addItemsRows.length === 1} onClick={() => setAddItemsRows(prev => prev.filter(it => it.id !== item.id))}>✕</Button>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, padding: '10px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #dce8ff', flexWrap: 'wrap' }}>
                                        <Checkbox checked={addItemsPaid} onChange={e => setAddItemsPaid(e.target.checked)}>
                                            Customer paid for these items now
                                        </Checkbox>
                                        <span style={{ fontSize: '0.88rem' }}>
                                            New total <strong>SGD {(Number(inv.total) + addedTotal).toFixed(2)}</strong>
                                            {' · '}balance <strong>SGD {Math.max(0, Number(inv.balance) + (addItemsPaid ? 0 : addedTotal)).toFixed(2)}</strong>
                                        </span>
                                    </div>

                                    {addItemsError && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7', fontSize: '0.85rem', color: '#cf1322' }}>
                                            {addItemsError}
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <Button size="large" style={{ flex: 1 }} onClick={close} disabled={addItemsSubmitting}>Cancel</Button>
                                        <Button
                                            type="primary"
                                            size="large"
                                            style={{ flex: 2 }}
                                            loading={addItemsSubmitting}
                                            disabled={validRows.length === 0}
                                            onClick={handleAddItemsSubmit}
                                        >
                                            Add {validRows.length || ''} Item{validRows.length === 1 ? '' : 's'}{!isLocalInv ? ' in Zoho' : ''}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {discountInvoice && (() => {
                        const close = () => { if (!discountSubmitting) setDiscountInvoice(null); };
                        const subtotal = Number(discountInvoice.total) + invoiceDiscount(discountInvoice);
                        const amount = discountAmount === '' ? 0 : Number(discountAmount) || 0;
                        return (
                            <div className="modal-overlay" onClick={close}>
                                <div className="modal-content" style={{ maxWidth: 420, width: '95vw' }} onClick={e => e.stopPropagation()}>
                                    <button className="modal-close" onClick={close}>×</button>
                                    <h3 style={{ marginBottom: 4 }}>Invoice Discount</h3>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 14 }}>
                                        <code>{discountInvoice.invoice_number}</code> · {discountInvoice.customer_name || '—'}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, fontSize: '0.9rem', marginBottom: 14 }}>
                                        <span style={{ color: '#555' }}>Total before discount</span>
                                        <span>SGD {subtotal.toFixed(2)}</span>
                                        <span style={{ color: '#555', alignSelf: 'center' }}>Discount</span>
                                        <Input
                                            type="number"
                                            min="0"
                                            max={subtotal}
                                            step="0.01"
                                            prefix="SGD"
                                            placeholder="0.00"
                                            value={discountAmount}
                                            onChange={e => { setDiscountAmount(e.target.value); setDiscountError(''); }}
                                            onKeyDown={e => e.key === 'Enter' && handleSaveDiscount()}
                                            style={{ width: 150 }}
                                            autoFocus
                                        />
                                        <strong>New total</strong>
                                        <strong>SGD {Math.max(0, subtotal - amount).toFixed(2)}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 12 }}>
                                        Replaces any discount already on this invoice (including one entered with the string order). Enter 0 to remove it.
                                    </div>
                                    {discountError && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7', fontSize: '0.85rem', color: '#cf1322' }}>
                                            {discountError}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <Button size="large" style={{ flex: 1 }} onClick={close} disabled={discountSubmitting}>Cancel</Button>
                                        <Button size="large" type="primary" style={{ flex: 1 }} loading={discountSubmitting} onClick={handleSaveDiscount}>Save Discount</Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {paymentInvoice && (() => {
                        const close = () => { if (!paymentSubmitting) setPaymentInvoice(null); };
                        return (
                            <div className="modal-overlay" onClick={close}>
                                <div className="modal-content" style={{ maxWidth: 460, width: '95vw' }} onClick={e => e.stopPropagation()}>
                                    <button className="modal-close" onClick={close}>×</button>
                                    <h3 style={{ marginBottom: 4 }}>Record Payment</h3>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: 14 }}>
                                        <code>{paymentInvoice.invoice_number}</code> · {paymentInvoice.customer_name || '—'} · balance{' '}
                                        <strong style={{ color: '#c41e3a' }}>SGD {Number(paymentInvoice.balance).toFixed(2)}</strong>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, alignItems: 'center', marginBottom: 14 }}>
                                        <span style={{ fontSize: '0.88rem', color: '#555' }}>Amount</span>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            prefix="SGD"
                                            value={paymentForm.amount}
                                            onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                                        />
                                        <span style={{ fontSize: '0.88rem', color: '#555' }}>Method</span>
                                        <Select
                                            value={paymentForm.method}
                                            onChange={val => setPaymentForm(f => ({ ...f, method: val }))}
                                            options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({ value, label }))}
                                        />
                                        <span style={{ fontSize: '0.88rem', color: '#555' }}>Date</span>
                                        <input
                                            type="date"
                                            className="order-search-input"
                                            value={paymentForm.payment_date}
                                            onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                                        />
                                        <span style={{ fontSize: '0.88rem', color: '#555' }}>Note</span>
                                        <Input
                                            placeholder="Optional, e.g. PayNow reference"
                                            value={paymentForm.note}
                                            onChange={e => setPaymentForm(f => ({ ...f, note: e.target.value }))}
                                        />
                                    </div>
                                    {paymentError && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#fff2f0', border: '1px solid #ffccc7', fontSize: '0.85rem', color: '#cf1322' }}>
                                            {paymentError}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <Button size="large" style={{ flex: 1 }} onClick={close} disabled={paymentSubmitting}>Cancel</Button>
                                        <Button type="primary" size="large" style={{ flex: 2 }} loading={paymentSubmitting} onClick={handleRecordPayment}>
                                            Record Payment
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* ========== ITEMS TAB ========== */}
                    {activeTab === 'items' && <ItemsTab />}

                    {/* ========== INVENTORY TAB ========== */}
                    {activeTab === 'inventory' && <InventoryTab />}

                    {/* ========== STATISTICS TAB ========== */}
                    {activeTab === 'statistics' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>Statistics</h2>
                            </div>

                            {/* Controls — Report type selector always visible */}
                            <div className="stats-controls">
                                <div className="stats-control-group">
                                    <label className="stats-control-label">Report</label>
                                    <Select
                                        value={statsType}
                                        onChange={val => setStatsType(val)}
                                        size="large"
                                        style={{ width: 200 }}
                                        options={[
                                            { value: 'string-usage', label: 'String Usage' },
                                            { value: 'stringer-stringing', label: 'Stringer Stringing' },
                                            { value: 'analytics', label: 'Analytics' },
                                        ]}
                                    />
                                </div>
                                {statsType !== 'analytics' && (
                                    <>
                                        <div className="stats-control-group">
                                            <label className="stats-control-label">From</label>
                                            <input
                                                type="date"
                                                className="stats-date-input"
                                                value={statsFrom}
                                                onChange={e => setStatsFrom(e.target.value)}
                                            />
                                        </div>
                                        <div className="stats-control-group">
                                            <label className="stats-control-label">To</label>
                                            <input
                                                type="date"
                                                className="stats-date-input"
                                                value={statsTo}
                                                onChange={e => setStatsTo(e.target.value)}
                                            />
                                        </div>
                                        {statsType === 'string-usage' && (
                                            <div className="stats-control-group">
                                                <label className="stats-control-label">Show</label>
                                                <Select
                                                    value={statsLimit}
                                                    onChange={val => setStatsLimit(val)}
                                                    size="large"
                                                    style={{ width: 130 }}
                                                    options={[
                                                        { value: 'top5', label: 'Top 5' },
                                                        { value: 'top10', label: 'Top 10' },
                                                        { value: 'top20', label: 'Top 20' },
                                                        { value: 'least5', label: 'Least 5' },
                                                        { value: 'least10', label: 'Least 10' },
                                                        { value: 'least20', label: 'Least 20' },
                                                    ]}
                                                />
                                            </div>
                                        )}
                                        <div className="stats-control-group" style={{ alignSelf: 'flex-end' }}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                loading={statsLoading}
                                                onClick={() => fetchStats(statsType, statsFrom, statsTo, statsLimit)}
                                                style={{ background: '#c41e3a', borderColor: '#c41e3a' }}
                                            >
                                                Generate
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* ── Chart / Table (existing reports) ── */}
                            {statsType !== 'analytics' && statsData.length > 0 && (() => {
                                const isStringer = statsType === 'stringer-stringing';
                                const xKey = isStringer ? 'stringer_name' : 'string_name';
                                const title = isStringer ? 'Stringer Stringing' : 'String Usage';
                                const yLabel = isStringer ? 'Racquets' : 'Orders';
                                return (
                                    <div className="stats-chart-card">
                                        <h3 className="stats-chart-title">
                                            {title}
                                            {statsFrom && statsTo ? ` · ${statsFrom} to ${statsTo}` : ''}
                                        </h3>
                                        <ResponsiveContainer width="100%" height={380}>
                                            <BarChart
                                                data={statsData}
                                                margin={{ top: 10, right: 20, left: 0, bottom: 80 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey={xKey}
                                                    tick={{ fontSize: 12, fill: '#555' }}
                                                    angle={-35}
                                                    textAnchor="end"
                                                    interval={0}
                                                />
                                                <YAxis
                                                    allowDecimals={false}
                                                    tick={{ fontSize: 12, fill: '#555' }}
                                                    label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: '#888' } }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => [value, yLabel]}
                                                    contentStyle={{ borderRadius: 8, fontSize: 13 }}
                                                />
                                                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
                                                    {statsData.map((entry, index) => (
                                                        <Cell
                                                            key={index}
                                                            fill={`hsl(${(index * 47) % 360}, 65%, 52%)`}
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>

                                        {/* Data table beneath chart */}
                                        <table className="stats-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    {isStringer
                                                        ? <th>Stringer</th>
                                                        : <><th>String</th><th>Brand</th><th>Model</th></>
                                                    }
                                                    <th>Racquets Strung</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statsData.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ color: '#aaa', fontWeight: 600 }}>{idx + 1}</td>
                                                        {isStringer
                                                            ? <td style={{ fontWeight: 600 }}>{row.stringer_name}</td>
                                                            : <><td style={{ fontWeight: 600 }}>{row.string_name}</td><td>{row.string_brand}</td><td>{row.string_model || '—'}</td></>
                                                        }
                                                        <td>
                                                            <span className="stats-count-badge">{row.count}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}

                            {statsType !== 'analytics' && statsData.length === 0 && !statsLoading && (
                                <div className="no-data" style={{ marginTop: 40 }}>
                                    Select a date range and click Generate to view statistics.
                                </div>
                            )}

                            {/* ── Analytics section ── */}
                            {statsType === 'analytics' && (
                                <div className="analytics-section">

                                    {/* Date range + filter */}
                                    <div className="analytics-filter-row">
                                        <div className="stats-control-group">
                                            <label className="stats-control-label">Start Date</label>
                                            <input
                                                type="date"
                                                className="stats-date-input"
                                                value={analyticsFrom}
                                                onChange={e => setAnalyticsFrom(e.target.value)}
                                            />
                                        </div>
                                        <div className="stats-control-group">
                                            <label className="stats-control-label">End Date</label>
                                            <input
                                                type="date"
                                                className="stats-date-input"
                                                value={analyticsTo}
                                                onChange={e => setAnalyticsTo(e.target.value)}
                                            />
                                        </div>
                                        <div className="stats-control-group" style={{ alignSelf: 'flex-end' }}>
                                            <Button
                                                type="primary"
                                                size="large"
                                                loading={analyticsFiltering}
                                                onClick={handleAnalyticsFilter}
                                                style={{ background: '#c41e3a', borderColor: '#c41e3a' }}
                                            >
                                                Order Filtering
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Results only appear after filtering */}
                                    {analyticsOrders !== null && (
                                        <>
                                            {/* Order count badge */}
                                            <div className="analytics-order-count">
                                                <span className="analytics-order-count-num">{analyticsOrders.length}</span>
                                                {' '}order{analyticsOrders.length !== 1 ? 's' : ''} found
                                                {analyticsFrom && analyticsTo && (
                                                    <span className="analytics-order-count-range"> · {analyticsFrom} to {analyticsTo}</span>
                                                )}
                                            </div>

                                            {/* Panel 1: Constant Variable */}
                                            <div className="analytics-panel">
                                                <div className="analytics-panel-title">Panel 1 — Constant Variable</div>
                                                <div className="analytics-panel-desc">Select a field and choose a fixed value to hold constant during analysis.</div>
                                                {analyticsOrders.length === 0 ? (
                                                    <div className="no-data" style={{ padding: '12px 0' }}>No orders in this date range to derive options from.</div>
                                                ) : (
                                                    <div className="analytics-fields-list">
                                                        {ANALYTICS_FIELDS.map(field => {
                                                            const options = getConstOptions(field.key);
                                                            return (
                                                                <div key={field.key} className="analytics-field-row">
                                                                    <Checkbox
                                                                        checked={constChecked[field.key]}
                                                                        onChange={e => handleConstCheck(field.key, e.target.checked)}
                                                                    >
                                                                        <span className="analytics-field-label">{field.label}</span>
                                                                    </Checkbox>
                                                                    {constChecked[field.key] && (
                                                                        <div className="analytics-field-dropdown">
                                                                            {field.multiSelect ? (
                                                                                <Select
                                                                                    mode="multiple"
                                                                                    value={constValues[field.key] || []}
                                                                                    onChange={val => handleConstValueChange(field.key, val)}
                                                                                    getPopupContainer={() => document.body}
                                                                                    size="middle"
                                                                                    style={{ minWidth: 260 }}
                                                                                    allowClear
                                                                                    showSearch
                                                                                    placeholder={options.length ? `Select ${field.label}` : 'No options available'}
                                                                                    options={options.map(v => ({ value: v, label: v }))}
                                                                                    notFoundContent="No options available"
                                                                                    disabled={options.length === 0}
                                                                                    maxTagCount="responsive"
                                                                                />
                                                                            ) : (
                                                                                <Select
                                                                                    value={constValues[field.key] || undefined}
                                                                                    onChange={val => handleConstValueChange(field.key, val || '')}
                                                                                    getPopupContainer={() => document.body}
                                                                                    size="middle"
                                                                                    style={{ minWidth: 220 }}
                                                                                    allowClear
                                                                                    showSearch
                                                                                    placeholder={options.length ? `Select ${field.label}` : 'No options available'}
                                                                                    options={options.map(v => ({ value: v, label: v }))}
                                                                                    notFoundContent="No options available"
                                                                                    disabled={options.length === 0}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Panel 2: Manipulation Variable */}
                                            <div className="analytics-panel">
                                                <div className="analytics-panel-title">Panel 2 — Manipulation Variable</div>
                                                <div className="analytics-panel-desc">Select the variables you want to manipulate and observe effects on the responding variable.</div>
                                                <div className="analytics-fields-list">
                                                    {ANALYTICS_FIELDS.map(field => (
                                                        <div key={field.key} className="analytics-field-row">
                                                            <Checkbox
                                                                checked={manipChecked[field.key]}
                                                                disabled={constChecked[field.key]}
                                                                onChange={e => setManipChecked(prev => ({ ...prev, [field.key]: e.target.checked }))}
                                                            >
                                                                <span className="analytics-field-label" style={constChecked[field.key] ? { color: '#bbb' } : undefined}>{field.label}</span>
                                                            </Checkbox>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Panel 3: Responding Variable */}
                                            <div className="analytics-panel">
                                                <div className="analytics-panel-title">Panel 3 — Responding Variable</div>
                                                <div className="analytics-panel-desc">Define what outcome you are measuring.</div>
                                                <div className="analytics-fields-list">
                                                    <div className="analytics-field-row">
                                                        <Checkbox
                                                            checked={respEnabled}
                                                            onChange={e => {
                                                                setRespEnabled(e.target.checked);
                                                                if (e.target.checked) setRespDtChecked(true);
                                                            }}
                                                        >
                                                            <span className="analytics-field-label">Enable Responding Variable</span>
                                                        </Checkbox>
                                                    </div>
                                                    {respEnabled && (
                                                        <div className="analytics-field-row" style={{ marginLeft: 24 }}>
                                                            <Checkbox
                                                                checked={respDtChecked}
                                                                onChange={e => setRespDtChecked(e.target.checked)}
                                                            >
                                                                <span className="analytics-field-label">DT</span>
                                                            </Checkbox>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Analyse button */}
                                            <div className="analytics-analyse-btn-row">
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    onClick={handleAnalyse}
                                                    style={{ background: '#c41e3a', borderColor: '#c41e3a', minWidth: 160 }}
                                                >
                                                    Analyse
                                                </Button>
                                            </div>

                                            {/* ── Result chart ── */}
                                            {analyticsResult !== null && (
                                                <div className="analytics-result-card">
                                                    <h3 className="stats-chart-title" style={{ marginBottom: 12 }}>
                                                        Analytics Result
                                                        <span style={{ fontWeight: 400, fontSize: '0.82rem', color: '#888', marginLeft: 10 }}>
                                                            {analyticsResult.totalFiltered} order{analyticsResult.totalFiltered !== 1 ? 's' : ''} analysed
                                                        </span>
                                                    </h3>

                                                    {/* Constant filter badges */}
                                                    {analyticsResult.constFilters.length > 0 && (
                                                        <div className="analytics-const-badges">
                                                            <span className="analytics-const-label">Fixed:</span>
                                                            {analyticsResult.constFilters.map(f => (
                                                                <span key={f.label} className="analytics-const-badge">
                                                                    {f.label} = <strong>{f.value}</strong>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Axis description */}
                                                    <div className="analytics-result-axis-desc">
                                                        <span><strong>X-axis:</strong> {analyticsResult.manipFields.length > 0 ? analyticsResult.manipFields.map(f => f.label).join(' + ') : 'All Orders (no group)'}</span>
                                                        <span><strong>Y-axis:</strong> {analyticsResult.yAxisMode === 'dt-individual' ? 'DT Value (per order)' : 'Order Count'}</span>
                                                    </div>

                                                    {analyticsResult.chartData.length === 0 ? (
                                                        <div className="no-data" style={{ padding: '20px 0' }}>
                                                            {analyticsResult.yAxisMode === 'dt-individual'
                                                                ? 'No orders with DT data found.'
                                                                : 'No orders match the selected constant variable filters.'}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Group colour legend for DT-individual mode */}
                                                            {analyticsResult.yAxisMode === 'dt-individual' && analyticsResult.groupList.length > 1 && (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                                                                    {analyticsResult.groupList.map((g, i) => (
                                                                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#444' }}>
                                                                            <span style={{ width: 12, height: 12, borderRadius: 2, background: `hsl(${(i * 47) % 360}, 65%, 52%)`, display: 'inline-block', flexShrink: 0 }} />
                                                                            {g}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {analyticsResult.yAxisMode === 'dt-individual' ? (
                                                                /* Bubble chart: X = group, Y = DT value, bubble size = count of same DT */
                                                                <ResponsiveContainer width="100%" height={420}>
                                                                    <ScatterChart margin={{ top: 20, right: 30, left: 10, bottom: 80 }}>
                                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                                        <XAxis
                                                                            type="number"
                                                                            dataKey="groupIdx"
                                                                            name="Group"
                                                                            domain={[-0.5, Math.max(analyticsResult.groupList.length - 0.5, 0.5)]}
                                                                            ticks={analyticsResult.groupList.map((_, i) => i)}
                                                                            tickFormatter={v => analyticsResult.groupList[v] ?? v}
                                                                            tick={{ fontSize: 11, fill: '#555' }}
                                                                            angle={-25}
                                                                            textAnchor="end"
                                                                            interval={0}
                                                                        />
                                                                        <YAxis
                                                                            type="number"
                                                                            dataKey="dt"
                                                                            name="DT"
                                                                            allowDecimals
                                                                            tick={{ fontSize: 12, fill: '#555' }}
                                                                            label={{ value: 'DT', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: '#888' } }}
                                                                        />
                                                                        <ZAxis type="number" dataKey="count" range={[60, 800]} name="Count" />
                                                                        <Tooltip
                                                                            cursor={{ strokeDasharray: '3 3' }}
                                                                            content={({ active, payload }) => {
                                                                                if (!active || !payload?.length) return null;
                                                                                const d = payload[0]?.payload;
                                                                                return (
                                                                                    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 14px', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                                                        {d.group !== 'All Orders' && <div style={{ fontWeight: 700, marginBottom: 4, color: '#222' }}>{d.group}</div>}
                                                                                        <div style={{ color: '#1677ff', fontWeight: 700, marginBottom: 2 }}>DT: {d.dt}</div>
                                                                                        <div style={{ color: '#555' }}>Count: <strong>{d.count}</strong> order{d.count > 1 ? 's' : ''}</div>
                                                                                    </div>
                                                                                );
                                                                            }}
                                                                        />
                                                                        <Scatter data={analyticsResult.chartData} fill="#1677ff">
                                                                            {analyticsResult.chartData.map((entry, i) => (
                                                                                <Cell key={i} fill={`hsl(${(entry.groupIdx * 47) % 360}, 65%, 52%)`} />
                                                                            ))}
                                                                        </Scatter>
                                                                    </ScatterChart>
                                                                </ResponsiveContainer>
                                                            ) : (
                                                                <ResponsiveContainer width="100%" height={380}>
                                                                    <BarChart
                                                                        data={analyticsResult.chartData}
                                                                        margin={{ top: 10, right: 20, left: 10, bottom: 100 }}
                                                                    >
                                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                                        <XAxis
                                                                            dataKey="group"
                                                                            tick={{ fontSize: 11, fill: '#555' }}
                                                                            angle={-35}
                                                                            textAnchor="end"
                                                                            interval={0}
                                                                        />
                                                                        <YAxis
                                                                            allowDecimals={false}
                                                                            tick={{ fontSize: 12, fill: '#555' }}
                                                                            label={{ value: 'Order Count', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 12, fill: '#888' } }}
                                                                        />
                                                                        <Tooltip
                                                                            content={({ active, payload, label }) => {
                                                                                if (!active || !payload?.length) return null;
                                                                                const d = payload[0]?.payload;
                                                                                return (
                                                                                    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 14px', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                                                                        <div style={{ fontWeight: 700, marginBottom: 6, color: '#222' }}>{label}</div>
                                                                                        <div style={{ color: '#555' }}>Orders: <strong>{d.count}</strong></div>
                                                                                    </div>
                                                                                );
                                                                            }}
                                                                        />
                                                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
                                                                            {analyticsResult.chartData.map((_, index) => (
                                                                                <Cell key={index} fill={`hsl(${(index * 47) % 360}, 65%, 52%)`} />
                                                                            ))}
                                                                        </Bar>
                                                                    </BarChart>
                                                                </ResponsiveContainer>
                                                            )}

                                                            {/* Data table */}
                                                            {analyticsResult.yAxisMode === 'dt-individual' ? (
                                                                <table className="stats-table" style={{ marginTop: 20 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>#</th>
                                                                            {analyticsResult.manipFields.length > 0 && (
                                                                                <th>{analyticsResult.manipFields.map(f => f.label).join(' · ')}</th>
                                                                            )}
                                                                            <th>DT</th>
                                                                            <th>Count</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {analyticsResult.chartData.map((row, idx) => (
                                                                            <tr key={idx}>
                                                                                <td style={{ color: '#aaa', fontWeight: 600 }}>{idx + 1}</td>
                                                                                {analyticsResult.manipFields.length > 0 && (
                                                                                    <td>
                                                                                        <span style={{ background: `hsl(${(row.groupIdx * 47) % 360}, 65%, 90%)`, color: `hsl(${(row.groupIdx * 47) % 360}, 50%, 30%)`, borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 500 }}>
                                                                                            {row.group}
                                                                                        </span>
                                                                                    </td>
                                                                                )}
                                                                                <td style={{ fontWeight: 700, color: '#1677ff' }}>{row.dt}</td>
                                                                                <td><span className="stats-count-badge">{row.count}</span></td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            ) : (
                                                                <table className="stats-table" style={{ marginTop: 20 }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th>#</th>
                                                                            <th>
                                                                                {analyticsResult.manipFields.length > 0
                                                                                    ? analyticsResult.manipFields.map(f => f.label).join(' · ')
                                                                                    : 'Group'}
                                                                            </th>
                                                                            <th>Orders</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {analyticsResult.chartData.map((row, idx) => (
                                                                            <tr key={idx}>
                                                                                <td style={{ color: '#aaa', fontWeight: 600 }}>{idx + 1}</td>
                                                                                <td style={{ fontWeight: 600 }}>{row.group}</td>
                                                                                <td><span className="stats-count-badge">{row.count}</span></td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {analyticsOrders === null && !analyticsFiltering && (
                                        <div className="no-data" style={{ marginTop: 32 }}>
                                            Select a date range and click <strong>Order Filtering</strong> to begin.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== PRODUCTS TAB ========== */}
                    {activeTab === 'products' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h2>Shop Products</h2>
                                <div className="section-header-actions">
                                    <button className="btn-csv" onClick={openCsvModal}>⬆ Import CSV</button>
                                    <button className="btn-add" onClick={openAddProductForm}>+ Add Product</button>
                                </div>
                            </div>

                            {productLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Image</th>
                                                <th>Name</th>
                                                <th>Brand</th>
                                                <th>Category</th>
                                                <th>Price</th>
                                                <th>Costing</th>
                                                <th>Stock</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map((p, index) => (
                                                <tr
                                                    key={p.id}
                                                    className={`${p.inStock ? '' : 'unavailable-row'} ${productRowDragOver === index ? 'row-drag-over' : ''}`}
                                                    draggable
                                                    onDragStart={() => handleProductRowDragStart(index)}
                                                    onDragOver={(e) => handleProductRowDragOver(e, index)}
                                                    onDrop={() => handleProductRowDrop(index)}
                                                    onDragEnd={handleProductRowDragEnd}
                                                >
                                                    <td className="drag-handle-cell">
                                                        <span className="drag-handle">⠿</span>
                                                    </td>
                                                    <td>
                                                        {(p.images?.[0] || p.image) ? (
                                                            <div className="table-thumbnail-wrap">
                                                                <img src={p.images?.[0] || p.image} alt={p.name} className="table-thumbnail" onError={(e) => { e.target.style.display = 'none'; }} />
                                                                {p.images && p.images.length > 1 && <span className="table-img-count">+{p.images.length - 1}</span>}
                                                            </div>
                                                        ) : (
                                                            <span className="no-image">—</span>
                                                        )}
                                                    </td>
                                                    <td>{p.name}</td>
                                                    <td>{p.brand}</td>
                                                    <td><span className="category-tag">{p.category}</span></td>
                                                    <td>${p.price}</td>
                                                    <td>{p.costing != null ? `$${p.costing}` : '—'}</td>
                                                    <td>
                                                        <button
                                                            className={`toggle-btn ${p.inStock ? 'available' : p.out_for_rent ? 'out-for-rent' : 'unavailable'}`}
                                                            onClick={() => toggleProductStock(p)}
                                                        >
                                                            {p.inStock ? 'In Stock' : p.out_for_rent ? 'Out for Rent' : 'Out of Stock'}
                                                        </button>
                                                    </td>
                                                    <td className="action-cell">
                                                        <button className="btn-edit" onClick={() => openEditProductForm(p)}>Edit</button>
                                                        <button className="btn-delete" onClick={() => handleProductDelete(p.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {products.length === 0 && (
                                        <div className="no-data">No products found. Click "+ Add Product" to add one.</div>
                                    )}
                                </div>
                            )}

                            {/* ── New Arrivals sub-section ── */}
                            <div className="section-header" style={{ marginTop: '2.5rem' }}>
                                <h2>New Arrivals <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#888' }}>(shown on Home page)</span></h2>
                                <button className="btn-add" onClick={() => { setArrivalForm({ name: '', category: 'racquets', label: 'New Arrival', description: '', image: '' }); setEditingArrivalId(null); setShowArrivalForm(true); }}>+ Add Item</button>
                            </div>

                            {showArrivalForm && (
                                <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 12, padding: '1.5rem', marginBottom: '1.25rem' }}>
                                    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1.25rem', color: '#222' }}>
                                        {editingArrivalId ? 'Edit Arrival Item' : 'New Arrival Item'}
                                    </div>
                                    <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                                        <Form layout="vertical">
                                            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                                                {/* Photo upload column */}
                                                <Form.Item label="Photo" style={{ marginBottom: 0, flexShrink: 0 }}>
                                                    {arrivalForm.image ? (
                                                        <div style={{ position: 'relative', width: 120, height: 120 }}>
                                                            <img
                                                                src={arrivalForm.image}
                                                                alt="preview"
                                                                style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10, border: '1px solid #e8e8e8', display: 'block' }}
                                                                onError={e => { e.target.style.display = 'none'; }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => setArrivalForm(p => ({ ...p, image: '' }))}
                                                                style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: '1px solid #ddd', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14, lineHeight: '20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', padding: 0 }}
                                                            >×</button>
                                                            <label style={{ display: 'block', marginTop: 6, textAlign: 'center', fontSize: '0.75rem', color: '#888', cursor: 'pointer' }}>
                                                                Change
                                                                <input type="file" accept="image/*,image/heic,image/heif" style={{ display: 'none' }} disabled={uploadingArrivalImage} onChange={e => { if (e.target.files[0]) handleArrivalImageUpload(e.target.files[0]); e.target.value = ''; }} />
                                                            </label>
                                                        </div>
                                                    ) : (
                                                        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 120, height: 120, border: '1px dashed #d9d9d9', borderRadius: 10, cursor: uploadingArrivalImage ? 'not-allowed' : 'pointer', background: '#fff', gap: 6 }}>
                                                            {uploadingArrivalImage
                                                                ? <><span style={{ fontSize: 22, color: '#aaa' }}>⟳</span><span style={{ fontSize: 12, color: '#aaa' }}>Uploading…</span></>
                                                                : <><span style={{ fontSize: 28, color: '#bbb', lineHeight: 1 }}>+</span><span style={{ fontSize: 12, color: '#aaa' }}>Upload Photo</span></>
                                                            }
                                                            <input type="file" accept="image/*,image/heic,image/heif" style={{ display: 'none' }} disabled={uploadingArrivalImage} onChange={e => { if (e.target.files[0]) handleArrivalImageUpload(e.target.files[0]); e.target.value = ''; }} />
                                                        </label>
                                                    )}
                                                </Form.Item>

                                                {/* Text fields column */}
                                                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                                                    <Form.Item label={<span>Name <span style={{ color: '#c41e3a' }}>*</span></span>} style={{ marginBottom: 14 }}>
                                                        <Input
                                                            size="large"
                                                            value={arrivalForm.name}
                                                            onChange={e => setArrivalForm(p => ({ ...p, name: e.target.value }))}
                                                            placeholder="e.g. Yonex Astrox 88D Pro"
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Label" style={{ marginBottom: 14 }}>
                                                        <Input
                                                            size="large"
                                                            value={arrivalForm.label}
                                                            onChange={e => setArrivalForm(p => ({ ...p, label: e.target.value }))}
                                                            placeholder="e.g. New Arrival, Just In"
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Category" style={{ marginBottom: 14 }}>
                                                        <Select
                                                            size="large"
                                                            style={{ width: '100%' }}
                                                            value={arrivalForm.category}
                                                            onChange={val => setArrivalForm(p => ({ ...p, category: val }))}
                                                            getPopupContainer={() => document.body}
                                                            options={[
                                                                { value: 'racquets', label: 'Racquets' },
                                                                { value: 'strings', label: 'Strings' },
                                                                { value: 'accessories', label: 'Accessories' },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item label="Description" style={{ marginBottom: 14, gridColumn: 'span 2' }}>
                                                        <Input.TextArea
                                                            size="large"
                                                            rows={2}
                                                            value={arrivalForm.description}
                                                            onChange={e => setArrivalForm(p => ({ ...p, description: e.target.value }))}
                                                            placeholder="Short product description shown on the home page"
                                                        />
                                                    </Form.Item>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Button type="primary" size="large" onClick={handleSaveArrival}>
                                                    {editingArrivalId ? 'Save Changes' : 'Add Item'}
                                                </Button>
                                                <Button size="large" onClick={() => setShowArrivalForm(false)}>Cancel</Button>
                                            </div>
                                        </Form>
                                    </ConfigProvider>
                                </div>
                            )}

                            {newArrivalsLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Image</th>
                                                <th>Name</th>
                                                <th>Category</th>
                                                <th>Label</th>
                                                <th>Description</th>
                                                <th>Visible</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {newArrivalsAdmin.map(item => (
                                                <tr key={item.id} className={item.enabled ? '' : 'unavailable-row'}>
                                                    <td>
                                                        {item.image ? (
                                                            <img src={item.image} alt={item.name} className="table-thumbnail" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} onError={e => { e.target.style.display = 'none'; }} />
                                                        ) : <span className="no-image">—</span>}
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>{item.name}</td>
                                                    <td><span className="category-tag">{item.category}</span></td>
                                                    <td>{item.label}</td>
                                                    <td style={{ maxWidth: 220, color: '#666', fontSize: '0.85rem' }}>{item.description || '—'}</td>
                                                    <td>
                                                        <button
                                                            className={`toggle-btn ${item.enabled ? 'available' : 'unavailable'}`}
                                                            onClick={() => handleToggleArrival(item)}
                                                        >
                                                            {item.enabled ? 'Visible' : 'Hidden'}
                                                        </button>
                                                    </td>
                                                    <td className="action-cell">
                                                        <button className="btn-edit" onClick={() => openEditArrival(item)}>Edit</button>
                                                        <button className="btn-delete" onClick={() => handleDeleteArrival(item.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {newArrivalsAdmin.length === 0 && (
                                        <div className="no-data">No new arrivals yet. Click "+ Add Item" to add one.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== CSV IMPORT MODAL ========== */}
                    {showCsvModal && (
                        <div className="modal-overlay" onClick={() => setShowCsvModal(false)}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowCsvModal(false)}>×</button>
                                <h2>Import Products via CSV</h2>

                                <div className="csv-instructions">
                                    <p>Upload a CSV file to bulk-add products. Required columns: <strong>name</strong>, <strong>brand</strong>, <strong>price</strong>.</p>
                                    <p>Optional: category, description, specifications, inStock (true/false), rentalFee</p>
                                    <button className="btn-template" onClick={handleDownloadTemplate}>⬇ Download Template</button>
                                </div>

                                <div className="form-group" style={{ marginTop: '1.25rem' }}>
                                    <label>Select CSV File</label>
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(e) => { setCsvFile(e.target.files[0] || null); setCsvResult(null); }}
                                    />
                                </div>

                                {csvResult && (
                                    <div className="csv-result">
                                        <div className="csv-result-summary">
                                            <span className="csv-ok">✓ {csvResult.imported} imported</span>
                                            {csvResult.skipped.length > 0 && (
                                                <span className="csv-skip">✗ {csvResult.skipped.length} skipped</span>
                                            )}
                                            <span className="csv-total">of {csvResult.total} rows</span>
                                        </div>
                                        {csvResult.skipped.length > 0 && (
                                            <ul className="csv-skip-list">
                                                {csvResult.skipped.map((s, i) => (
                                                    <li key={i}><strong>{s.name}</strong>: {s.reason}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setShowCsvModal(false)}>Close</button>
                                    <button
                                        type="button"
                                        className="btn-save"
                                        onClick={handleCsvImport}
                                        disabled={!csvFile || csvImporting}
                                    >
                                        {csvImporting ? 'Importing...' : 'Import'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ========== VIDEO MODAL ========== */}
                    {showVideoForm && (
                        <div className="modal-overlay" onClick={() => setShowVideoForm(false)}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowVideoForm(false)}>×</button>
                                <h2>{editingVideoId ? 'Edit Video' : 'Add New TikTok Video'}</h2>
                                <form onSubmit={handleVideoSubmit} className="admin-form">
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input name="title" value={videoForm.title} onChange={handleVideoFormChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>TikTok URL *</label>
                                        <input name="embedUrl" value={videoForm.embedUrl} onChange={handleVideoFormChange} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea name="description" value={videoForm.description} onChange={handleVideoFormChange} rows="3" />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Category</label>
                                            <select name="category" value={videoForm.category} onChange={handleVideoFormChange}>
                                                <option value="guide">Guide</option>
                                                <option value="technique">Technique</option>
                                                <option value="transformation">Transformation</option>
                                                <option value="professional">Professional</option>
                                                <option value="education">Education</option>
                                                <option value="challenge">Challenge</option>
                                                <option value="testimonial">Testimonial</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Date</label>
                                            <input name="date" type="date" value={videoForm.date} onChange={handleVideoFormChange} />
                                        </div>
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input type="checkbox" name="enabled" checked={videoForm.enabled} onChange={handleVideoFormChange} />
                                            Enabled (visible on Events page)
                                        </label>
                                    </div>
                                    <div className="form-actions">
                                        <button type="button" className="btn-cancel" onClick={() => setShowVideoForm(false)}>Cancel</button>
                                        <button type="submit" className="btn-save" disabled={saving}>
                                            {saving ? 'Saving...' : (editingVideoId ? 'Update' : 'Add Video')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* ========== STRING MODAL ========== */}
                    {showStringForm && (
                        <div className="modal-overlay" onClick={() => setShowStringForm(false)}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowStringForm(false)}>×</button>
                                <h2>{editingStringId ? 'Edit String' : 'Add New String'}</h2>
                                <form onSubmit={handleStringSubmit} className="admin-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Brand *</label>
                                            <input name="brand" value={stringForm.brand} onChange={handleStringFormChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Model *</label>
                                            <input name="model" value={stringForm.model} onChange={handleStringFormChange} required />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Type</label>
                                            <input name="type" value={stringForm.type} onChange={handleStringFormChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Racquet Type</label>
                                            <select name="racquet_type" value={stringForm.racquet_type} onChange={handleStringFormChange}>
                                                <option value="Badminton">Badminton</option>
                                                <option value="Tennis">Tennis</option>
                                                <option value="Squash">Squash</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Price ($) *</label>
                                            <input name="price" type="number" step="0.01" value={stringForm.price} onChange={handleStringFormChange} required />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea name="description" value={stringForm.description} onChange={handleStringFormChange} rows="2" />
                                    </div>
                                    <div className="form-group">
                                        <label>Color Group</label>
                                        <textarea
                                            name="colorGroupsText"
                                            value={stringForm.colorGroupsText}
                                            onChange={handleStringFormChange}
                                            rows="2"
                                            placeholder="White, Yellow, Black"
                                        />
                                        <small className="form-help-text">Comma or new-line separated. One string model can have multiple colors.</small>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Photos
                                            <span className="img-count-badge">{stringForm.images.length} photo{stringForm.images.length !== 1 ? 's' : ''}</span>
                                            <span className="img-hint">Drag to reorder · First photo is cover</span>
                                        </label>
                                        <div className="multi-image-editor">
                                            {stringForm.images.map((img, index) => (
                                                <div
                                                    key={index}
                                                    className={`img-thumb-wrap${dragOverIndex === index ? ' drag-over' : ''}`}
                                                    draggable
                                                    onDragStart={(e) => handleStringImageDragStart(e, index)}
                                                    onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
                                                    onDrop={(e) => handleStringImageDrop(e, index)}
                                                    onDragEnd={() => { dragItem.current = null; setDragOverIndex(null); }}
                                                >
                                                    <img src={img} alt={`String ${index + 1}`} className="img-thumb" />
                                                    {index === 0 && <span className="img-cover-badge">Cover</span>}
                                                    <button type="button" className="img-remove-btn" onClick={() => removeStringImage(index)}>×</button>
                                                </div>
                                            ))}
                                            <label className={`multi-image-add${uploadingImage ? ' uploading' : ''}`}>
                                                <span className="multi-image-add-icon">{uploadingImage ? '⟳' : '+'}</span>
                                                <span className="multi-image-add-label">{uploadingImage ? `${uploadProgress.done}/${uploadProgress.total}` : 'Upload Photos'}</span>
                                                <input
                                                    type="file"
                                                    accept="image/*,image/heic,image/heif"
                                                    multiple
                                                    style={{ display: 'none' }}
                                                    disabled={uploadingImage}
                                                    onChange={(e) => { if (e.target.files.length > 0) { handleStringImageAdd(e.target.files); e.target.value = ''; } }}
                                                />
                                            </label>
                                        </div>
                                        {uploadingImage && (
                                            <div className="img-upload-progress">
                                                <div className="img-upload-bar" style={{ width: `${uploadProgress.total > 0 ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%` }} />
                                                <span className="img-upload-text">Uploading {uploadProgress.done} of {uploadProgress.total}...</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Gauge</label>
                                        <input name="gauge" value={stringForm.gauge} onChange={handleStringFormChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Core Material</label>
                                        <input name="coreMaterial" value={stringForm.coreMaterial} onChange={handleStringFormChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Outer Material</label>
                                        <input name="outerMaterial" value={stringForm.outerMaterial} onChange={handleStringFormChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Coating</label>
                                        <input name="coating" value={stringForm.coating} onChange={handleStringFormChange} />
                                    </div>
                                    <div className="form-actions">
                                        <button type="button" className="btn-cancel" onClick={() => setShowStringForm(false)}>Cancel</button>
                                        <button type="submit" className="btn-save" disabled={saving || uploadingImage}>
                                            {saving ? 'Saving...' : (editingStringId ? 'Update' : 'Add String')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* ========== PRODUCT MODAL ========== */}
                    {showProductForm && (
                        <div className="modal-overlay" onClick={() => setShowProductForm(false)}>
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowProductForm(false)}>×</button>
                                <h2>{editingProductId ? 'Edit Product' : 'Add New Product'}</h2>
                                <form onSubmit={handleProductSubmit} className="admin-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Name *</label>
                                            <input name="name" value={productForm.name} onChange={handleProductFormChange} required />
                                        </div>
                                        <div className="form-group">
                                            <label>Brand *</label>
                                            <input name="brand" value={productForm.brand} onChange={handleProductFormChange} required />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Category</label>
                                            <select name="category" value={productForm.category} onChange={handleProductFormChange}>
                                                <option value="racquets">Racquets</option>
                                                <option value="strings">Strings</option>
                                                <option value="grips">Grips</option>
                                                <option value="bags">Bags</option>
                                                <option value="accessories">Accessories</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Price ($) *</label>
                                            <input name="price" type="number" step="0.01" value={productForm.price} onChange={handleProductFormChange} required />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea name="description" value={productForm.description} onChange={handleProductFormChange} rows="2" />
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Photos
                                            <span className="img-count-badge">{productForm.images.length} photo{productForm.images.length !== 1 ? 's' : ''}</span>
                                            <span className="img-hint">Drag to reorder · First photo is cover</span>
                                        </label>
                                        <div className="multi-image-editor">
                                            {productForm.images.map((img, index) => (
                                                <div
                                                    key={index}
                                                    className={`multi-image-item${dragOverIndex === index ? ' drag-over' : ''}`}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, index)}
                                                    onDragEnter={() => setDragOverIndex(index)}
                                                    onDragOver={(e) => e.preventDefault()}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                >
                                                    <img
                                                        src={img}
                                                        alt={`Photo ${index + 1}`}
                                                        draggable={false}
                                                        onError={(e) => { e.target.onerror = null; e.target.style.opacity = '0.4'; }}
                                                    />
                                                    {index === 0 && <div className="img-cover-badge">Cover</div>}
                                                    <div className="img-item-actions">
                                                        <button type="button" className="img-action-btn img-remove" onClick={() => removeProductImage(index)} title="Remove">×</button>
                                                    </div>
                                                </div>
                                            ))}
                                            <label className={`multi-image-add${uploadingImage ? ' uploading' : ''}`}>
                                                <span className="multi-image-add-icon">{uploadingImage ? '⟳' : '+'}</span>
                                                <span className="multi-image-add-label">{uploadingImage ? `${uploadProgress.done}/${uploadProgress.total}` : 'Upload Photos'}</span>
                                                <input
                                                    type="file"
                                                    accept="image/*,image/heic,image/heif"
                                                    multiple
                                                    style={{ display: 'none' }}
                                                    disabled={uploadingImage}
                                                    onChange={(e) => { if (e.target.files.length > 0) { handleProductImageAdd(e.target.files); e.target.value = ''; } }}
                                                />
                                            </label>
                                        </div>
                                        {uploadingImage && (
                                            <div className="img-upload-progress">
                                                <div className="img-upload-bar" style={{ width: `${uploadProgress.total > 0 ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%` }} />
                                                <span className="img-upload-text">Uploading {uploadProgress.done} of {uploadProgress.total}...</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Specifications</label>
                                        <input name="specifications" value={productForm.specifications} onChange={handleProductFormChange} />
                                    </div>
                                    {productForm.category === 'strings' && (<>
                                        <div className="form-group">
                                            <label>String Type</label>
                                            <select name="stringType" value={productForm.stringType} onChange={handleProductFormChange}>
                                                <option value="">Select type</option>
                                                <option value="Repulsion">Repulsion</option>
                                                <option value="Control">Control</option>
                                                <option value="Power">Power</option>
                                                <option value="Durability">Durability</option>
                                                <option value="All-round">All-round</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Gauge (Thickness)</label>
                                            <select name="gauge" value={productForm.gauge} onChange={handleProductFormChange}>
                                                <option value="">Select gauge</option>
                                                <option value="0.58mm">0.58mm</option>
                                                <option value="0.61mm">0.61mm</option>
                                                <option value="0.62mm">0.62mm</option>
                                                <option value="0.63mm">0.63mm</option>
                                                <option value="0.65mm">0.65mm</option>
                                                <option value="0.66mm">0.66mm</option>
                                                <option value="0.68mm">0.68mm</option>
                                                <option value="0.70mm">0.70mm</option>
                                                <option value="0.72mm">0.72mm</option>
                                            </select>
                                        </div>
                                    </>)}
                                    <div className="form-group">
                                        <label>Costing ($) <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                        <input name="costing" type="number" step="0.01" value={productForm.costing} onChange={handleProductFormChange} />
                                    </div>
                                    {productForm.category === 'racquets' && (
                                        <div className="form-group">
                                            <label>Rental Fee - 10 Days ($)</label>
                                            <input name="rentalFee" type="number" step="0.01" value={productForm.rentalFee} onChange={handleProductFormChange} />
                                        </div>
                                    )}
                                    {productForm.category === 'racquets' && (
                                        <div className="form-group">
                                            <label>Item Condition</label>
                                            <select name="condition" value={productForm.condition} onChange={handleProductFormChange}>
                                                <option value="">Select condition</option>
                                                <option value="Brand new">Brand new</option>
                                                <option value="Like new">Like new</option>
                                                <option value="Lightly used">Lightly used</option>
                                                <option value="Well used">Well used</option>
                                            </select>
                                        </div>
                                    )}
                                    {productForm.category === 'racquets' && (<>
                                        <div className="form-group">
                                            <label>Swing Weight <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                            <input name="swingWeight" value={productForm.swingWeight} onChange={handleProductFormChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Weight Type <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                            <input name="weightType" value={productForm.weightType} onChange={handleProductFormChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Weight <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                            <input name="weight" value={productForm.weight} onChange={handleProductFormChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Balance Point <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                            <input name="balancePoint" value={productForm.balancePoint} onChange={handleProductFormChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Grommet System <span style={{ fontWeight: 'normal', color: '#888' }}>()</span></label>
                                            <input name="grommetSystem" value={productForm.grommetSystem} onChange={handleProductFormChange} />
                                        </div>
                                    </>)}
                                    {productForm.category === 'racquets' ? (
                                        <div className="form-group">
                                            <label>Status</label>
                                            <div className="radio-group">
                                                <label className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="racquetStatus"
                                                        value="inStock"
                                                        checked={productForm.inStock && !productForm.outForRent}
                                                        onChange={handleProductFormChange}
                                                    />
                                                    In Stock
                                                </label>
                                                <label className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="racquetStatus"
                                                        value="outForRent"
                                                        checked={productForm.outForRent}
                                                        onChange={handleProductFormChange}
                                                    />
                                                    Out for Rent
                                                </label>
                                                <label className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="racquetStatus"
                                                        value="outOfStock"
                                                        checked={!productForm.inStock && !productForm.outForRent}
                                                        onChange={handleProductFormChange}
                                                    />
                                                    Out of Stock
                                                </label>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="form-group checkbox-group">
                                            <label>
                                                <input type="checkbox" name="inStock" checked={productForm.inStock} onChange={handleProductFormChange} />
                                                In Stock
                                            </label>
                                        </div>
                                    )}
                                    <div className="form-actions">
                                        <button type="button" className="btn-cancel" onClick={() => setShowProductForm(false)}>Cancel</button>
                                        <button type="submit" className="btn-save" disabled={saving || uploadingImage}>
                                            {saving ? 'Saving...' : (editingProductId ? 'Update' : 'Add Product')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                    {/* ========== STRINGING ORDER TAB ========== */}
                    {activeTab === 'orders' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <button className="btn-add" onClick={openAddOrderModal}>+ Add New</button>
                                <div className="section-header-actions">
                                    <input
                                        type="text"
                                        className="order-search-input"
                                        placeholder="Search by order ID, customer, racquet, string, status..."
                                        value={orderSearch}
                                        onChange={(e) => {
                                            setOrderSearch(e.target.value);
                                            setOrderPage(1);
                                        }}
                                    />
                                    <Select
                                        value={orderFilter}
                                        onChange={val => {
                                            setOrderFilter(val);
                                            setOrderPage(1);
                                            fetchOrders(val);
                                        }}
                                        getPopupContainer={() => document.body}
                                        className="order-filter-select"
                                        style={{ minWidth: 160 }}
                                        options={[
                                            { value: 'All', label: 'All Status' },
                                            ...ORDER_STATUSES.map(s => ({ value: s, label: s }))
                                        ]}
                                    />
                                </div>
                            </div>

                            {orderLoading ? (
                                <div className="admin-loading">Loading...</div>
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th
                                                    onClick={() => setOrderSort(prev => ({ key: 'order_id', dir: prev.key === 'order_id' && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    Order ID {orderSort.key === 'order_id' ? (orderSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                                                </th>
                                                <th
                                                    onClick={() => setOrderSort(prev => ({ key: 'status', dir: prev.key === 'status' && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    Status {orderSort.key === 'status' ? (orderSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                                                </th>
                                                <th>Customer</th>
                                                <th>Racquet</th>
                                                {orderFilter === 'Collected' ? (
                                                    <th style={{ whiteSpace: 'nowrap' }}>Collected Date</th>
                                                ) : (
                                                    <th
                                                        onClick={() => setOrderSort(prev => ({ key: 'collection_date', dir: prev.key === 'collection_date' && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                                                        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    >
                                                        Exp. Collect {orderSort.key === 'collection_date' ? (orderSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                                                    </th>
                                                )}
                                                <th>Paid</th>
                                                <th>Contact</th>
                                                <th>Action</th>
                                                <th>Print</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedOrders.items.map((o, idx) => {
                                                const prevGroupRef = idx > 0 ? paginatedOrders.items[idx - 1].order_group_ref : null;
                                                const isFirstInGroup = o.order_group_ref !== prevGroupRef;
                                                const nextGroupRef = idx < paginatedOrders.items.length - 1 ? paginatedOrders.items[idx + 1].order_group_ref : null;
                                                const isLastInGroup = o.order_group_ref !== nextGroupRef;
                                                const belongsToGroup = paginatedOrders.items.some((x, i) => i !== idx && x.order_group_ref === o.order_group_ref);
                                                const rowClass = [
                                                    'order-row-clickable',
                                                    o.express_service ? 'order-row-express' : '',
                                                    belongsToGroup && isFirstInGroup ? 'order-row-group-first' : '',
                                                    belongsToGroup && !isFirstInGroup ? 'order-row-group-member' : '',
                                                    belongsToGroup && isLastInGroup ? 'order-row-group-last' : '',
                                                ].filter(Boolean).join(' ');
                                                const groupOrders = orders.filter(x => x.order_group_ref === o.order_group_ref);
                                                return (
                                                    <tr key={o.order_id} className={rowClass} onClick={() => openUpdateModal(o)}>
                                                        <td>
                                                            {isFirstInGroup
                                                                ? <><span
                                                                    className="order-ref-badge"
                                                                    title={o.invoice_id
                                                                        ? (String(o.invoice_id).startsWith('LOCAL-') ? `Draft invoice ${invoices.find(inv => inv.invoice_id === o.invoice_id)?.invoice_number || ''} (not yet in Zoho)` : 'Invoiced in Zoho Books')
                                                                        : invoicedOrderRefs.has(o.order_group_ref) ? 'Invoiced in Zoho Books' : 'Not invoiced'}
                                                                    style={{ background: (o.invoice_id ? !String(o.invoice_id).startsWith('LOCAL-') : invoicedOrderRefs.has(o.order_group_ref)) ? '#52c41a' : '#faad14', color: '#000' }}
                                                                >{o.order_group_ref}</span>
                                                                {isFirstInGroup && o.invoice_id && invoices.find(inv => inv.invoice_id === o.invoice_id) && (
                                                                    <div style={{ fontSize: '0.72rem', color: '#555', marginTop: 3 }} title="Invoice for this order">
                                                                        {invoices.find(inv => inv.invoice_id === o.invoice_id).invoice_number}
                                                                    </div>
                                                                )}</>
                                                                : <span className="order-group-connector">└─</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            <span className={`order-status-pill order-status-${o.status.replace(/\s+/g, '-').toLowerCase()}`}>
                                                                {o.status}
                                                            </span>
                                                        </td>
                                                        <td>{o.customer_name}</td>
                                                        <td>
                                                            <span style={{ fontWeight: 500 }}>{o.racquet_brand}</span>
                                                            <br />
                                                            <span style={{ color: '#666', fontSize: '0.82rem' }}>{o.racquet_model}</span>
                                                        </td>
                                                        <td>
                                                            {orderFilter === 'Collected'
                                                                ? (o.collected_date ? new Date(o.collected_date).toLocaleString() : '—')
                                                                : (o.collection_date ? new Date(o.collection_date).toLocaleDateString() : 'TBD')}
                                                        </td>
                                                        <td onClick={e => e.stopPropagation()}>
                                                            <Select
                                                                value={o.paid ? 'paid' : 'unpaid'}
                                                                onChange={val => handlePaidChange(o.order_id, val === 'paid')}
                                                                getPopupContainer={() => document.body}
                                                                size="small"
                                                                className={`paid-status-select ${o.paid ? 'paid-status-select-paid' : 'paid-status-select-unpaid'}`}
                                                                style={{
                                                                    width: 100,
                                                                    fontWeight: 600,
                                                                    ...(o.paid
                                                                        ? { '--ant-select-background-color': '#d4edda', '--ant-select-border-color': '#b7e0c3', '--ant-select-color': '#155724' }
                                                                        : { '--ant-select-background-color': '#f8d7da', '--ant-select-border-color': '#eeb4ba', '--ant-select-color': '#cf1322' }),
                                                                }}
                                                                options={[
                                                                    { value: 'paid', label: 'Paid' },
                                                                    { value: 'unpaid', label: 'Unpaid' },
                                                                ]}
                                                            />
                                                        </td>
                                                        <td>
                                                            {isFirstInGroup && (
                                                                <a
                                                                    href={buildWhatsAppUrl(groupOrders)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={e => e.stopPropagation()}
                                                                    style={{ color: '#25D366', fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                                >
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                                                                    {o.contact_number}
                                                                </a>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {canMarkOrderCollected(o) && (
                                                                <button
                                                                    type="button"
                                                                    className="order-collect-tick-btn"
                                                                    title="Mark as Collected"
                                                                    onClick={e => { e.stopPropagation(); handleOrderStatusChange(o.order_id, 'Collected'); }}
                                                                >
                                                                    <CheckCircleOutlined />
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                title="Print order slip"
                                                                onClick={e => { e.stopPropagation(); handlePrintOrder(buildOrderForm(o)); }}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', color: '#c41e3a' }}
                                                            >
                                                                <PrinterOutlined style={{ fontSize: 16 }} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {filteredOrders.length === 0 && (
                                        <div className="no-data">No stringing orders yet. Click "+ Add New" to create one.</div>
                                    )}
                                    {filteredOrders.length > 0 && (
                                        <div className="table-pagination">
                                            <span className="table-pagination-summary">
                                                Showing {paginatedOrders.items.length} of {paginatedOrders.totalItems} orders
                                            </span>
                                            <div className="table-pagination-controls">
                                                <button
                                                    type="button"
                                                    className="btn-cancel table-page-btn"
                                                    onClick={() => setOrderPage(prev => Math.max(1, prev - 1))}
                                                    disabled={paginatedOrders.currentPage === 1}
                                                >
                                                    Previous
                                                </button>
                                                <span className="table-page-indicator">
                                                    Page {paginatedOrders.currentPage} of {paginatedOrders.totalPages}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn-save table-page-btn"
                                                    onClick={() => setOrderPage(prev => Math.min(paginatedOrders.totalPages, prev + 1))}
                                                    disabled={paginatedOrders.currentPage === paginatedOrders.totalPages}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== NEW STRING ORDER WIZARD MODAL ========== */}
                    {showOrderModal && (
                        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
                            <div className="modal-content order-modal" onClick={e => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowOrderModal(false)}>×</button>

                                <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                                    {(() => {
                                        const branched = orderPhoneLookupStatus === 'not_found';
                                        const stepTitles = branched
                                            ? ['Phone', 'New Customer', 'Racquet Details', 'Collect & Confirm']
                                            : ['Phone', 'Racquet Details', 'Collect & Confirm'];
                                        const stepIndex = {
                                            phone: 0,
                                            newCustomer: 1,
                                            racquet: branched ? 2 : 1,
                                            confirm: branched ? 3 : 2,
                                        }[orderWizardStep];
                                        return (
                                            <Steps
                                                current={stepIndex}
                                                size="small"
                                                style={{ marginBottom: 28 }}
                                                items={stepTitles.map(title => ({ title }))}
                                            />
                                        );
                                    })()}

                                    {/* Step 1: Phone lookup */}
                                    {orderWizardStep === 'phone' && (
                                        <div className="order-carousel-page">
                                            <Form layout="vertical" requiredMark="">
                                                <Form.Item label="Customer Phone Number" required>
                                                    <PhoneInput
                                                        country="sg"
                                                        preferredCountries={['sg', 'my', 'id', 'ph', 'th', 'au', 'gb', 'us']}
                                                        enableSearch
                                                        searchPlaceholder="Search country..."
                                                        value={orderForm.phone}
                                                        onChange={val => setOrderForm(prev => ({ ...prev, phone: val }))}
                                                        containerClass="order-phone-container"
                                                        inputClass="order-phone-input"
                                                        buttonClass="order-phone-flag"
                                                    />
                                                </Form.Item>
                                            </Form>
                                            <div className="order-carousel-nav" style={{ marginTop: 24 }}>
                                                <span />
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    icon={<ArrowRightOutlined />}
                                                    iconPosition="end"
                                                    loading={orderPhoneLookupLoading}
                                                    onClick={handlePhoneLookupSubmit}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2: New customer name — only when the phone wasn't found */}
                                    {orderWizardStep === 'newCustomer' && (
                                        <div className="order-carousel-page">
                                            <Form layout="vertical" requiredMark="">
                                                <Form.Item label="Customer Name" required>
                                                    <Input
                                                        prefix={<UserOutlined style={{ color: '#bbb' }} />}
                                                        value={orderForm.custName}
                                                        onChange={e => setOrderForm(prev => ({ ...prev, custName: e.target.value }))}
                                                        size="large"
                                                        autoFocus
                                                    />
                                                </Form.Item>
                                            </Form>
                                            <div className="order-carousel-nav">
                                                <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => setOrderWizardStep('phone')}>
                                                    Back
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    icon={<ArrowRightOutlined />}
                                                    iconPosition="end"
                                                    loading={orderSaving}
                                                    onClick={handleCreateNewCustomerSubmit}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3: Racquet Details — brand buttons, model/string/tension, add + collapse */}
                                    {orderWizardStep === 'racquet' && (
                                        <div className="order-carousel-page">
                                            <div style={{ marginBottom: 16, color: '#888', fontSize: '0.85rem' }}>
                                                Customer: <strong style={{ color: '#333' }}>{orderForm.custName}</strong>
                                            </div>
                                            <div style={{ marginBottom: 8 }}>
                                                {orderForm.racquets.map((r, rIdx) => {
                                                    const effectiveBrand = r.racquetBrandSelect === 'Other' ? r.racquetBrandOther : r.racquetBrandSelect;
                                                    const effectiveModel = r.racquetBrandSelect === 'Other' ? r.racquetModelOther : r.racquetModel;
                                                    const racquetLabel = [effectiveBrand, effectiveModel].filter(Boolean).join(' ');
                                                    const summaryParts = [racquetLabel || `Racquet ${rIdx + 1}`];
                                                    if (r.string.bringOwnString || r.string.zohoItemName) summaryParts.push(stringSelectionLabel(r.string));
                                                    if (r.string.tension) summaryParts.push(`${r.string.tension}/${r.string.crossTension || r.string.tension}lbs`);

                                                    // Live stock contention: a string is unavailable to this racquet once
                                                    // (stock_on_hand - count already claimed by OTHER racquets in this order) < 1.
                                                    // Clearing/changing another racquet's selection frees it up again automatically,
                                                    // since claimCounts is recomputed from current orderForm state on every render.
                                                    // A hybrid racquet claims half a set of each of its 2 strings.
                                                    const claimCounts = {};
                                                    orderForm.racquets.forEach((other, i) => {
                                                        if (i === rIdx || other.string.bringOwnString) return;
                                                        const share = isHybridString(other.string) ? 0.5 : 1;
                                                        const id = other.string.zohoItemId;
                                                        if (id) claimCounts[id] = (claimCounts[id] || 0) + share;
                                                        if (isHybridString(other.string)) {
                                                            const cid = other.string.crossZohoItemId;
                                                            claimCounts[cid] = (claimCounts[cid] || 0) + share;
                                                        }
                                                    });
                                                    const inventoryItems = (zohoItems || []).filter(it => it.item_type === 'inventory');
                                                    const needed = r.string.hybrid ? 0.5 : 1;
                                                    const buildStringOptions = (selectedId, excludeId) => inventoryItems
                                                        .filter(it => it.item_id !== excludeId)
                                                        .map(it => {
                                                            const stock = it.stock_on_hand != null ? Number(it.stock_on_hand) : 0;
                                                            const claimedByOthers = claimCounts[it.item_id] || 0;
                                                            const remaining = stock - claimedByOthers;
                                                            const isSelected = it.item_id === selectedId;
                                                            const unavailable = remaining < needed && !isSelected;
                                                            let label = it.name;
                                                            if (stock < needed) label += ' (Out of stock)';
                                                            else if (unavailable) label += ' (In use by another racquet)';
                                                            return { value: it.item_id, label, disabled: unavailable };
                                                        });
                                                    const stringOptions = buildStringOptions(r.string.zohoItemId, r.string.hybrid ? r.string.crossZohoItemId : null);
                                                    const crossStringOptions = buildStringOptions(r.string.crossZohoItemId, r.string.zohoItemId);

                                                    return (
                                                        <div key={rIdx} className="string-card" style={{ marginBottom: 10 }}>
                                                            {r.collapsed ? (
                                                                <div className="string-card-summary" onClick={() => handleEditRacquet(rIdx)}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                        <span className="string-card-badge">#{rIdx + 1}</span>
                                                                        <span className="string-card-summary-text">
                                                                            <strong>{summaryParts.join(' — ')}</strong>
                                                                        </span>
                                                                    </div>
                                                                    <EditOutlined className="string-card-edit-icon" />
                                                                </div>
                                                            ) : (
                                                                <div className="string-card-body">
                                                                    <div className="string-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                            <span className="string-card-badge">Racquet #{rIdx + 1}</span>
                                                                            {rIdx > 0 && (
                                                                                <button type="button" className="btn-edit" style={{ padding: '2px 10px', fontSize: '0.78rem', margin: 0 }} onClick={() => handleDuplicateRacquet(rIdx)}>Duplicate</button>
                                                                            )}
                                                                        </div>
                                                                        {orderForm.racquets.length > 1 && (
                                                                            <button type="button" className="btn-delete" style={{ padding: '2px 10px', fontSize: '0.78rem' }} onClick={() => handleRemoveRacquet(rIdx)}>Remove</button>
                                                                        )}
                                                                    </div>
                                                                    <Form layout="vertical" requiredMark="">
                                                                        <Form.Item label="Racquet Brand" required style={{ marginBottom: 10 }}>
                                                                            <div className="brand-button-grid">
                                                                                {RACQUET_BRAND_LOGOS.map(b => (
                                                                                    <button
                                                                                        type="button"
                                                                                        key={b.name}
                                                                                        className={`brand-button${r.racquetBrandSelect === b.name ? ' brand-button-active' : ''}`}
                                                                                        onClick={() => handleRacquetChange(rIdx, 'racquetBrandSelect', b.name)}
                                                                                    >
                                                                                        <img src={b.logo} alt={b.name} onError={e => { e.target.style.display = 'none'; }} />
                                                                                        <span>{b.name}</span>
                                                                                    </button>
                                                                                ))}
                                                                                <button
                                                                                    type="button"
                                                                                    className={`brand-button brand-button-other${r.racquetBrandSelect === 'Other' ? ' brand-button-active' : ''}`}
                                                                                    onClick={() => handleRacquetChange(rIdx, 'racquetBrandSelect', 'Other')}
                                                                                >
                                                                                    <ShopOutlined style={{ fontSize: 22, color: '#999' }} />
                                                                                    <span>Other</span>
                                                                                </button>
                                                                            </div>
                                                                            {r.racquetBrandSelect === 'Other' && (
                                                                                <Input
                                                                                    style={{ marginTop: 10 }}
                                                                                    placeholder="Enter racquet brand"
                                                                                    prefix={<ShopOutlined style={{ color: '#bbb' }} />}
                                                                                    value={r.racquetBrandOther}
                                                                                    onChange={e => handleRacquetChange(rIdx, 'racquetBrandOther', e.target.value)}
                                                                                    size="large"
                                                                                    autoFocus
                                                                                />
                                                                            )}
                                                                        </Form.Item>
                                                                        <Form.Item label="Racquet Model" required style={{ marginBottom: 10 }}>
                                                                            {r.racquetBrandSelect === 'Other' ? (
                                                                                <Input
                                                                                    placeholder="Enter racquet model"
                                                                                    prefix={<TagOutlined style={{ color: '#bbb' }} />}
                                                                                    value={r.racquetModelOther}
                                                                                    onChange={e => handleRacquetChange(rIdx, 'racquetModelOther', e.target.value)}
                                                                                    size="large"
                                                                                />
                                                                            ) : (
                                                                                <AutoComplete
                                                                                    value={r.racquetModel}
                                                                                    onChange={val => handleRacquetChange(rIdx, 'racquetModel', val)}
                                                                                    options={(racquetModelsData[r.racquetBrandSelect] || []).map(m => ({ value: m }))}
                                                                                    filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                                                                                    size="large"
                                                                                    style={{ width: '100%' }}
                                                                                    notFoundContent={null}
                                                                                >
                                                                                    <Input prefix={<TagOutlined style={{ color: '#bbb' }} />} size="large" />
                                                                                </AutoComplete>
                                                                            )}
                                                                        </Form.Item>
                                                                        <Form.Item style={{ marginBottom: 10 }}>
                                                                            <Checkbox
                                                                                checked={r.string.bringOwnString}
                                                                                onChange={e => {
                                                                                    const checked = e.target.checked;
                                                                                    updateRacquet(rIdx, r2 => ({
                                                                                        ...r2,
                                                                                        string: checked ? {
                                                                                            ...r2.string,
                                                                                            bringOwnString: true,
                                                                                            _savedZohoItemId: r2.string.zohoItemId,
                                                                                            _savedZohoItemName: r2.string.zohoItemName,
                                                                                            zohoItemId: '',
                                                                                            zohoItemName: '',
                                                                                        } : {
                                                                                            ...r2.string,
                                                                                            bringOwnString: false,
                                                                                            zohoItemId: r2.string._savedZohoItemId || '',
                                                                                            zohoItemName: r2.string._savedZohoItemName || '',
                                                                                            ownStringType: '',
                                                                                        },
                                                                                    }));
                                                                                }}
                                                                            >
                                                                                Bring own string
                                                                            </Checkbox>
                                                                        </Form.Item>
                                                                        {r.string.bringOwnString ? (
                                                                            <Form.Item label="Type of String (optional)" style={{ marginBottom: 10 }}>
                                                                                <Input
                                                                                    placeholder="E.g. Yonex BG65"
                                                                                    prefix={<TagOutlined style={{ color: '#bbb' }} />}
                                                                                    value={r.string.ownStringType}
                                                                                    onChange={e => handleStringFieldChange(rIdx, 'ownStringType', e.target.value)}
                                                                                    size="large"
                                                                                />
                                                                            </Form.Item>
                                                                        ) : (
                                                                            <Form.Item label="String Brand/Model" required style={{ marginBottom: 10 }}>
                                                                                <Select
                                                                                    showSearch
                                                                                    allowClear
                                                                                    value={r.string.zohoItemId || undefined}
                                                                                    placeholder="Select a string"
                                                                                    optionFilterProp="label"
                                                                                    size="large"
                                                                                    style={{ width: '100%' }}
                                                                                    options={stringOptions}
                                                                                    onChange={(val) => {
                                                                                        const item = inventoryItems.find(it => it.item_id === val);
                                                                                        handleStringFieldChange(rIdx, 'zohoItemId', val || '');
                                                                                        handleStringFieldChange(rIdx, 'zohoItemName', item ? item.name : '');
                                                                                    }}
                                                                                    onClear={() => {
                                                                                        handleStringFieldChange(rIdx, 'zohoItemId', '');
                                                                                        handleStringFieldChange(rIdx, 'zohoItemName', '');
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                        )}
                                                                        {!r.string.bringOwnString && (
                                                                            <Form.Item style={{ marginBottom: 10 }}>
                                                                                <Checkbox
                                                                                    checked={!!r.string.hybrid}
                                                                                    onChange={e => {
                                                                                        const checked = e.target.checked;
                                                                                        updateRacquet(rIdx, r2 => ({
                                                                                            ...r2,
                                                                                            string: { ...r2.string, hybrid: checked, ...(checked ? {} : { crossZohoItemId: '', crossZohoItemName: '' }) },
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    Use 2 different strings (mains / crosses, half a set each)
                                                                                </Checkbox>
                                                                            </Form.Item>
                                                                        )}
                                                                        {!r.string.bringOwnString && r.string.hybrid && (
                                                                            <Form.Item label="2nd String Brand/Model (crosses)" required style={{ marginBottom: 10 }}>
                                                                                <Select
                                                                                    showSearch
                                                                                    allowClear
                                                                                    value={r.string.crossZohoItemId || undefined}
                                                                                    placeholder="Select the cross string"
                                                                                    optionFilterProp="label"
                                                                                    size="large"
                                                                                    style={{ width: '100%' }}
                                                                                    options={crossStringOptions}
                                                                                    onChange={(val) => {
                                                                                        const item = inventoryItems.find(it => it.item_id === val);
                                                                                        handleStringFieldChange(rIdx, 'crossZohoItemId', val || '');
                                                                                        handleStringFieldChange(rIdx, 'crossZohoItemName', item ? item.name : '');
                                                                                    }}
                                                                                    onClear={() => {
                                                                                        handleStringFieldChange(rIdx, 'crossZohoItemId', '');
                                                                                        handleStringFieldChange(rIdx, 'crossZohoItemName', '');
                                                                                    }}
                                                                                />
                                                                            </Form.Item>
                                                                        )}
                                                                        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                                                            <Form.Item label="Main Tension" required style={{ flex: 1, marginBottom: 0 }}>
                                                                                <Input
                                                                                    prefix={<DashboardOutlined style={{ color: '#bbb' }} />}
                                                                                    suffix={<span style={{ color: '#aaa', fontSize: 12 }}>lbs</span>}
                                                                                    value={r.string.tension}
                                                                                    onChange={e => handleMainTensionChange(rIdx, e.target.value)}
                                                                                    size="large"
                                                                                    inputMode="numeric"
                                                                                />
                                                                            </Form.Item>

                                                                            <Form.Item label="Cross Tension" required style={{ flex: 1, marginBottom: 0 }}>
                                                                                <Input
                                                                                    prefix={<DashboardOutlined style={{ color: '#bbb' }} />}
                                                                                    suffix={<span style={{ color: '#aaa', fontSize: 12 }}>lbs</span>}
                                                                                    value={r.string.crossTension}
                                                                                    onChange={e => handleStringFieldChange(rIdx, 'crossTension', e.target.value)}
                                                                                    size="large"
                                                                                    inputMode="numeric"
                                                                                />
                                                                            </Form.Item>
                                                                            <Form.Item label="Pre-stretch" style={{ flex: 1, marginBottom: 0 }}>
                                                                                <Select
                                                                                    value={r.string.preStretch || 'None'}
                                                                                    onChange={val => handleStringFieldChange(rIdx, 'preStretch', val)}
                                                                                    getPopupContainer={() => document.body}
                                                                                    size="large"
                                                                                    style={{ width: '100%' }}
                                                                                    options={[
                                                                                        { value: 'None', label: '0%' },
                                                                                        { value: '5', label: '5%' },
                                                                                        { value: '10', label: '10%' },
                                                                                        { value: '15', label: '15%' },
                                                                                        { value: '20', label: '20%' },
                                                                                    ]}
                                                                                />
                                                                            </Form.Item>
                                                                        </div>
                                                                        <Form.Item label="String Pattern" style={{ marginBottom: 0 }}>
                                                                            <Select
                                                                                value={r.string.stringPattern || undefined}
                                                                                onChange={val => handleStringFieldChange(rIdx, 'stringPattern', val || '')}
                                                                                getPopupContainer={() => document.body}
                                                                                size="large"
                                                                                style={{ width: '100%' }}
                                                                                allowClear
                                                                                showSearch
                                                                                placeholder="Select a pattern"
                                                                                options={stringPatterns.map(p => ({ value: p.name, label: p.name }))}
                                                                                notFoundContent="No patterns found — add one in Static Info"
                                                                                filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                                                            />
                                                                        </Form.Item>
                                                                        <Form.Item style={{ marginTop: 10, marginBottom: 0 }}>
                                                                            <Checkbox
                                                                                checked={!!r.expressService}
                                                                                onChange={e => updateRacquet(rIdx, rq => ({ ...rq, expressService: e.target.checked }))}
                                                                            >
                                                                                <span className="express-checkbox-label">Express Service</span>
                                                                            </Checkbox>
                                                                        </Form.Item>
                                                                    </Form>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                <Button
                                                    type="dashed"
                                                    icon={<PlusOutlined />}
                                                    onClick={handleAddRacquet}
                                                    style={{ width: '100%' }}
                                                >
                                                    Add Racquet
                                                </Button>
                                            </div>

                                            <div className="order-carousel-nav" style={{ marginTop: 24 }}>
                                                <Button
                                                    size="large"
                                                    icon={<ArrowLeftOutlined />}
                                                    onClick={() => setOrderWizardStep(orderPhoneLookupStatus === 'not_found' ? 'newCustomer' : 'phone')}
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    icon={<ArrowRightOutlined />}
                                                    iconPosition="end"
                                                    onClick={() => {
                                                        for (const r of orderForm.racquets) {
                                                            const brand = r.racquetBrandSelect === 'Other' ? r.racquetBrandOther : r.racquetBrandSelect;
                                                            const model = r.racquetBrandSelect === 'Other' ? r.racquetModelOther : r.racquetModel;
                                                            if (!brand.trim() || !model.trim()) {
                                                                setError('Please fill in brand and model for all racquets.');
                                                                return;
                                                            }
                                                            if (!r.string.bringOwnString && !r.string.zohoItemId) {
                                                                setError('Please select a string for all racquets.');
                                                                return;
                                                            }
                                                            if (!r.string.bringOwnString && r.string.hybrid && !r.string.crossZohoItemId) {
                                                                setError('Please select the 2nd string (crosses) or untick "Use 2 different strings".');
                                                                return;
                                                            }
                                                            if (!String(r.string.tension || '').trim() || !String(r.string.crossTension || '').trim()) {
                                                                setError('Please enter main and cross tension for all racquets.');
                                                                return;
                                                            }
                                                        }
                                                        setError('');
                                                        // ensure first racquet is open on the confirm step
                                                        setOrderForm(prev => ({
                                                            ...prev,
                                                            racquets: prev.racquets.map((r, i) => ({ ...r, collapsed: i !== 0 })),
                                                        }));
                                                        setOrderWizardStep('confirm');
                                                    }}
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 4: Collect date + confirm */}
                                    {orderWizardStep === 'confirm' && (
                                        <div className="order-carousel-page">
                                            <div style={{ marginBottom: 16 }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Order Summary</div>
                                                {orderForm.racquets.map((r, rIdx) => {
                                                    const brand = r.racquetBrandSelect === 'Other' ? r.racquetBrandOther : r.racquetBrandSelect;
                                                    const model = r.racquetBrandSelect === 'Other' ? r.racquetModelOther : r.racquetModel;
                                                    return (
                                                        <div key={rIdx} className="string-card-summary" style={{ borderRadius: 8, border: '1px solid #e8e8e8', marginBottom: 8, cursor: 'default' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                <span className="string-card-badge">#{rIdx + 1}</span>
                                                                <span className="string-card-summary-text">
                                                                    <strong>{[brand, model].filter(Boolean).join(' ')}</strong> — {stringSelectionLabel(r.string)}{r.string.tension ? ` · ${r.string.tension}/${r.string.crossTension || r.string.tension}lbs` : ''}{r.string.stringPattern ? ` · ${r.string.stringPattern}` : ''}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <Form layout="vertical" requiredMark="">
                                                <Form.Item label="Collect Date (optional)">
                                                    <Input
                                                        type="date"
                                                        min={todayDateStr()}
                                                        value={orderForm.expectedCollectionDate}
                                                        onChange={e => setOrderForm(prev => ({ ...prev, expectedCollectionDate: e.target.value }))}
                                                        size="large"
                                                        placeholder="TBD"
                                                    />
                                                </Form.Item>
                                                {(() => {
                                                    const prices = orderForm.racquets.map(r => orderRacquetPrice(r.string, zohoItems));
                                                    const total = Math.round(prices.reduce((sum, p) => sum + p.price, 0) * 100) / 100;
                                                    const discountVal = orderForm.discount !== '' ? Number(orderForm.discount) || 0 : 0;
                                                    const toPay = Math.max(0, Math.round((total - discountVal) * 100) / 100);
                                                    return (
                                                        <>
                                                            <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: '0.88rem' }}>
                                                                {orderForm.racquets.map((r, rIdx) => (
                                                                    <div key={rIdx} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
                                                                        <span style={{ color: '#555' }}>#{rIdx + 1} {prices[rIdx].label}</span>
                                                                        <span style={{ whiteSpace: 'nowrap', color: prices[rIdx].missing ? '#c41e3a' : '#333' }}>
                                                                            {prices[rIdx].missing ? 'No price set' : `SGD ${prices[rIdx].price.toFixed(2)}`}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                                                                <Form.Item label="Total Fees (SGD)" style={{ flex: 1, marginBottom: 0 }}>
                                                                    <Input value={total.toFixed(2)} size="large" disabled />
                                                                </Form.Item>
                                                                <Form.Item label="Discount (SGD)" style={{ flex: 1, marginBottom: 0 }}>
                                                                    <Input
                                                                        type="number"
                                                                        min="0"
                                                                        max={total}
                                                                        step="0.01"
                                                                        placeholder="0.00"
                                                                        value={orderForm.discount}
                                                                        onChange={e => setOrderForm(prev => ({ ...prev, discount: e.target.value }))}
                                                                        size="large"
                                                                    />
                                                                </Form.Item>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: '2rem' }}>
                                                                <div style={{ fontSize: '1rem' }}>
                                                                    Amount to pay: <strong>SGD {toPay.toFixed(2)}</strong>
                                                                    {discountVal > total + 0.001 && (
                                                                        <div style={{ color: '#c41e3a', fontSize: '0.82rem' }}>Discount can't be more than the total fees.</div>
                                                                    )}
                                                                </div>
                                                                <Checkbox
                                                                    className="paid-checkbox-large"
                                                                    checked={orderForm.paid}
                                                                    onChange={e => setOrderForm(prev => ({ ...prev, paid: e.target.checked }))}
                                                                >
                                                                    <span className="express-checkbox-label">Paid</span>
                                                                </Checkbox>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </Form>
                                            <div className="order-carousel-nav">
                                                <Button size="large" icon={<ArrowLeftOutlined />} onClick={() => setOrderWizardStep('racquet')}>
                                                    Back
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    size="large"
                                                    onClick={handleOrderConfirm}
                                                    loading={orderSaving}
                                                >
                                                    Confirm
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </ConfigProvider>
                            </div>
                        </div>
                    )}

                    {/* ========== ORDER DETAIL MODAL ========== */}
                    {showUpdateModal && (
                        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
                            <div className="modal-content update-order-modal" onClick={e => e.stopPropagation()}>
                                <button className="modal-close" onClick={() => setShowUpdateModal(false)}>×</button>
                                <h3 className="update-order-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    Order Details
                                    <span className="order-ref-badge" style={{ fontSize: '0.85rem' }}>{updateForm._order_ref}</span>
                                    <button
                                        type="button"
                                        title="Print order slip"
                                        onClick={() => handlePrintOrder()}
                                        style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, color: '#c41e3a', fontSize: '0.82rem', fontWeight: 500 }}
                                    >
                                        <PrinterOutlined style={{ fontSize: 16 }} />
                                    </button>
                                    {orderDetailPhotos.length > 0 && (
                                        <>
                                            <button
                                                type="button"
                                                title={`${orderDetailPhotos.length} photo${orderDetailPhotos.length > 1 ? 's' : ''}`}
                                                onClick={() => { setPhotoCarouselIndex(0); setPhotoCarouselVisible(true); }}
                                                style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, color: '#c41e3a', fontSize: '0.82rem', fontWeight: 500 }}
                                            >
                                                <span style={{ fontSize: 16 }}>🖼️</span>
                                                <span>{orderDetailPhotos.length}</span>
                                            </button>
                                            {/* Hidden Image.PreviewGroup — trigger via photoCarouselVisible */}
                                            <div style={{ display: 'none' }}>
                                                <Image.PreviewGroup
                                                    preview={{
                                                        visible: photoCarouselVisible,
                                                        current: photoCarouselIndex,
                                                        onChange: (cur) => setPhotoCarouselIndex(cur),
                                                        onVisibleChange: (vis) => setPhotoCarouselVisible(vis),
                                                    }}
                                                >
                                                    {orderDetailPhotos.map((p, i) => (
                                                        <Image key={i} src={p.photo_url} />
                                                    ))}
                                                </Image.PreviewGroup>
                                            </div>
                                        </>
                                    )}
                                </h3>

                                {/* Read-only info — order date */}
                                <div className="view-order-grid" style={{ marginBottom: 16 }}>
                                    <div className="view-order-row">
                                        <span className="view-label">Order Date</span>
                                        <span className="view-value">{updateForm._order_date ? new Date(updateForm._order_date).toLocaleDateString() : '—'}</span>
                                    </div>
                                    {updateForm._collected_date && (
                                        <div className="view-order-row">
                                            <span className="view-label">Collected Date</span>
                                            <span className="view-value">{new Date(updateForm._collected_date).toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div style={{ marginTop: 8 }}>
                                        <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                                            <Form layout="vertical" requiredMark="" style={{ marginBottom: 0 }}>
                                                <Form.Item label="Racquet Brand" style={{ marginBottom: 8 }}>
                                                    <Select
                                                        value={updateForm.racquet_brand}
                                                        onChange={val => setUpdateForm(prev => ({ ...prev, racquet_brand: val, racquet_brand_other: '', racquet_model: '' }))}
                                                        size="large"
                                                        style={{ width: '100%' }}
                                                        disabled={!updateModalEditing}
                                                        options={RACQUET_BRANDS.map(b => ({ value: b, label: b }))}
                                                        getPopupContainer={() => document.body}
                                                    />
                                                    {updateForm.racquet_brand === 'Others' && (
                                                        <Input
                                                            style={{ marginTop: 8 }}
                                                            prefix={<ShopOutlined style={{ color: '#bbb' }} />}
                                                            value={updateForm.racquet_brand_other}
                                                            onChange={e => setUpdateForm(prev => ({ ...prev, racquet_brand_other: e.target.value }))}
                                                            size="large"
                                                            disabled={!updateModalEditing}
                                                            placeholder="Enter brand name"
                                                        />
                                                    )}
                                                </Form.Item>
                                                <Form.Item label="Racquet Model" style={{ marginBottom: 0 }}>
                                                    <AutoComplete
                                                        value={updateForm.racquet_model}
                                                        onChange={val => setUpdateForm(prev => ({ ...prev, racquet_model: val }))}
                                                        options={(racquetModels[updateForm.racquet_brand] || []).map(m => ({ value: m }))}
                                                        filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                                                        size="large"
                                                        style={{ width: '100%' }}
                                                        disabled={!updateModalEditing}
                                                        notFoundContent={null}
                                                    >
                                                        <Input prefix={<TagOutlined style={{ color: '#bbb' }} />} size="large" placeholder="Enter or select model" />
                                                    </AutoComplete>
                                                </Form.Item>
                                            </Form>
                                        </ConfigProvider>
                                    </div>
                                </div>

                                <ConfigProvider theme={{ token: { colorPrimary: '#c41e3a', borderRadius: 8 } }}>
                                    {['Ready to Collect', 'Collected'].includes(updateForm.status) && (
                                        <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.82rem', color: '#ad6800' }}>
                                            ⚠️ <strong>Mandatory for this status:</strong> Tension{!updateForm.bring_own_string ? ', String Item' : ''}, String Pattern, String Machine, Stringer Name, DT
                                        </div>
                                    )}
                                    <Form layout="vertical" requiredMark="">
                                        <Form.Item label="Status">
                                            <Select
                                                value={updateForm.status}
                                                onChange={val => setUpdateForm(prev => ({ ...prev, status: val }))}
                                                getPopupContainer={() => document.body}
                                                size="large"
                                                style={{ width: '100%' }}
                                                disabled={!updateModalEditing}
                                                options={ORDER_STATUSES.map(s => ({ value: s, label: s }))}
                                            />
                                        </Form.Item>
                                        <Form.Item style={{ marginBottom: 12 }}>
                                            <Checkbox
                                                checked={updateForm.bring_own_string}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    setUpdateForm(prev => checked ? ({
                                                        ...prev,
                                                        bring_own_string: true,
                                                        _savedZohoItemId: prev.zoho_item_id,
                                                        _savedZohoItemName: prev.zoho_item_name,
                                                        zoho_item_id: '',
                                                        zoho_item_name: '',
                                                    }) : ({
                                                        ...prev,
                                                        bring_own_string: false,
                                                        zoho_item_id: prev._savedZohoItemId || '',
                                                        zoho_item_name: prev._savedZohoItemName || '',
                                                    }));
                                                }}
                                                disabled={!updateModalEditing}
                                            >
                                                <span className="express-checkbox-label">Bring own string</span>
                                            </Checkbox>
                                        </Form.Item>
                                        {updateForm.bring_own_string ? (
                                            <Form.Item label={<span>Type of String<span style={{ color: '#888', fontWeight: 400 }}> (optional)</span></span>} style={{ marginBottom: 12 }}>
                                                <Input
                                                    placeholder="E.g. Yonex BG65"
                                                    prefix={<TagOutlined style={{ color: '#bbb' }} />}
                                                    value={updateForm.zoho_item_name}
                                                    onChange={e => setUpdateForm(prev => ({ ...prev, zoho_item_name: e.target.value }))}
                                                    size="large"
                                                    disabled={!updateModalEditing}
                                                />
                                            </Form.Item>
                                        ) : (
                                            <Form.Item label={<span>String Item{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ marginBottom: 12 }}>
                                                <div style={{ position: 'relative' }}>
                                                    {updateItemPickerOpen && (
                                                        <div
                                                            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                                                            onClick={() => setUpdateItemPickerOpen(false)}
                                                        />
                                                    )}
                                                    <div
                                                        onClick={() => {
                                                            if (!updateModalEditing) return;
                                                            if (updateItemPickerOpen) {
                                                                setUpdateItemPickerOpen(false);
                                                            } else {
                                                                setUpdateItemPickerOpen(true);
                                                                setUpdateItemPickerSearch('');
                                                                setUpdateItemPickerExpandedGroups(new Set());
                                                            }
                                                        }}
                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d9d9d9', borderRadius: 8, padding: '9px 12px', cursor: updateModalEditing ? 'pointer' : 'not-allowed', background: updateModalEditing ? '#fff' : '#f5f5f5', minHeight: 40, fontSize: 14 }}
                                                    >
                                                        <TagOutlined style={{ color: '#bbb' }} />
                                                        <span style={{ flex: 1, color: updateForm.zoho_item_name ? '#000' : '#bbb' }}>
                                                            {updateForm.zoho_item_name || 'Select a string item...'}
                                                        </span>
                                                        {updateForm.zoho_item_name && updateModalEditing && (
                                                            <span
                                                                onClick={e => { e.stopPropagation(); setUpdateForm(prev => ({ ...prev, zoho_item_id: '', zoho_item_name: '' })); }}
                                                                style={{ color: '#aaa', fontSize: 12, cursor: 'pointer' }}
                                                            >✕</span>
                                                        )}
                                                        <span style={{ color: '#aaa', fontSize: 10 }}>{updateItemPickerOpen ? '▲' : '▼'}</span>
                                                    </div>
                                                    {updateItemPickerOpen && (() => {
                                                        const inventoryItems = zohoItems.filter(it => it.item_type === 'inventory');
                                                        const searchLower = updateItemPickerSearch.toLowerCase();
                                                        const filtered = searchLower ? inventoryItems.filter(it => it.name && it.name.toLowerCase().includes(searchLower)) : inventoryItems;
                                                        const groups = {};
                                                        filtered.forEach(it => {
                                                            const grp = it.name ? it.name.split(' ')[0] : 'Other';
                                                            if (!groups[grp]) groups[grp] = [];
                                                            groups[grp].push(it);
                                                        });
                                                        return (
                                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto', marginTop: 4 }}>
                                                                <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0' }}>
                                                                    <Input
                                                                        placeholder="Search items..."
                                                                        size="small"
                                                                        value={updateItemPickerSearch}
                                                                        onChange={e => setUpdateItemPickerSearch(e.target.value)}
                                                                        onClick={e => e.stopPropagation()}
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                {Object.keys(groups).sort().length === 0 && (
                                                                    <div style={{ padding: '12px 14px', color: '#aaa', fontSize: 13 }}>No items found</div>
                                                                )}
                                                                {Object.keys(groups).sort().map(grp => {
                                                                    const isExpanded = searchLower || updateItemPickerExpandedGroups.has(grp);
                                                                    return (
                                                                        <div key={grp}>
                                                                            <div
                                                                                onClick={e => {
                                                                                    e.stopPropagation();
                                                                                    if (searchLower) return;
                                                                                    setUpdateItemPickerExpandedGroups(prev => {
                                                                                        const next = new Set(prev);
                                                                                        next.has(grp) ? next.delete(grp) : next.add(grp);
                                                                                        return next;
                                                                                    });
                                                                                }}
                                                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', fontWeight: 700, fontSize: 13, color: '#444', background: '#fafafa', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', userSelect: 'none' }}
                                                                            >
                                                                                <span>{grp} <span style={{ color: '#aaa', fontWeight: 400 }}>({groups[grp].length})</span></span>
                                                                                <span style={{ fontSize: 10, color: '#aaa' }}>{isExpanded ? '▲' : '▼'}</span>
                                                                            </div>
                                                                            {isExpanded && groups[grp].map(it => (
                                                                                <div
                                                                                    key={it.item_id}
                                                                                    onClick={e => {
                                                                                        e.stopPropagation();
                                                                                        setUpdateForm(prev => ({ ...prev, zoho_item_id: it.item_id, zoho_item_name: it.name }));
                                                                                        setUpdateItemPickerOpen(false);
                                                                                    }}
                                                                                    style={{ padding: '8px 14px 8px 26px', fontSize: 13, cursor: 'pointer', color: updateForm.zoho_item_id === it.item_id ? '#1890ff' : '#333', background: updateForm.zoho_item_id === it.item_id ? '#e6f4ff' : 'transparent', borderBottom: '1px solid #f9f9f9' }}
                                                                                    onMouseEnter={e => { if (updateForm.zoho_item_id !== it.item_id) e.currentTarget.style.background = '#f5f5f5'; }}
                                                                                    onMouseLeave={e => { e.currentTarget.style.background = updateForm.zoho_item_id === it.item_id ? '#e6f4ff' : 'transparent'; }}
                                                                                >
                                                                                    {it.name}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </Form.Item>
                                        )}
                                        {!updateForm.bring_own_string && (updateForm.cross_zoho_item_id || updateModalEditing) && (
                                            <Form.Item label="2nd String (crosses, optional)" style={{ marginBottom: 12 }}>
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    size="large"
                                                    style={{ width: '100%' }}
                                                    placeholder="Same string for mains and crosses"
                                                    optionFilterProp="label"
                                                    getPopupContainer={() => document.body}
                                                    disabled={!updateModalEditing}
                                                    value={updateForm.cross_zoho_item_id || undefined}
                                                    options={(zohoItems || [])
                                                        .filter(it => it.item_type === 'inventory' && it.item_id !== updateForm.zoho_item_id)
                                                        .map(it => ({ value: it.item_id, label: it.name }))
                                                        .concat(updateForm.cross_zoho_item_id && !(zohoItems || []).some(it => it.item_id === updateForm.cross_zoho_item_id)
                                                            ? [{ value: updateForm.cross_zoho_item_id, label: updateForm.cross_string_brand || updateForm.cross_zoho_item_id }] : [])}
                                                    onChange={val => {
                                                        const it = (zohoItems || []).find(i => i.item_id === val);
                                                        setUpdateForm(prev => ({ ...prev, cross_zoho_item_id: val || '', cross_string_brand: it ? it.name : '' }));
                                                    }}
                                                />
                                            </Form.Item>
                                        )}
                                        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                                            <Form.Item label={<span>Main Tension{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ flex: 1, marginBottom: 0 }}>
                                                <Input
                                                    size="large"
                                                    value={updateForm.tension}
                                                    onChange={e => setUpdateForm(prev => {
                                                        const inSync = prev.cross_tension === '' || prev.cross_tension === prev.tension;
                                                        return { ...prev, tension: e.target.value, cross_tension: inSync ? e.target.value : prev.cross_tension };
                                                    })}
                                                    disabled={!updateModalEditing}
                                                />
                                            </Form.Item>
                                            <Form.Item label={<span>Cross Tension{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ flex: 1, marginBottom: 0 }}>
                                                <Input
                                                    size="large"
                                                    value={updateForm.cross_tension}
                                                    onChange={e => setUpdateForm(prev => ({ ...prev, cross_tension: e.target.value }))}
                                                    disabled={!updateModalEditing}
                                                />
                                            </Form.Item>
                                        </div>
                                        <Form.Item style={{ marginBottom: 12 }}>
                                            <Checkbox
                                                checked={updateForm.express_service}
                                                onChange={e => setUpdateForm(prev => ({ ...prev, express_service: e.target.checked }))}
                                                disabled={!updateModalEditing}
                                            >
                                                <span className="express-checkbox-label">Express Service</span>
                                            </Checkbox>
                                        </Form.Item>
                                        <Form.Item style={{ marginBottom: 12 }}>
                                            <Checkbox
                                                checked={updateForm.paid}
                                                onChange={e => setUpdateForm(prev => ({ ...prev, paid: e.target.checked }))}
                                                disabled={!updateModalEditing}
                                            >
                                                <span className="express-checkbox-label">Paid</span>
                                            </Checkbox>
                                        </Form.Item>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                            <Form.Item label="Pre-stretch" style={{ marginBottom: 0 }}>
                                                <Select
                                                    value={updateForm.pre_stretch || undefined}
                                                    onChange={val => setUpdateForm(prev => ({ ...prev, pre_stretch: val || '' }))}
                                                    getPopupContainer={() => document.body}
                                                    size="large"
                                                    style={{ width: '100%' }}
                                                    allowClear
                                                    disabled={!updateModalEditing}
                                                    options={[
                                                        { value: 'None', label: '0%' },
                                                        { value: '5', label: '5%' },
                                                        { value: '10', label: '10%' },
                                                        { value: '15', label: '15%' },
                                                        { value: '20', label: '20%' },
                                                    ]}
                                                />
                                            </Form.Item>
                                            <Form.Item label={<span>String Pattern{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ marginBottom: 0 }}>
                                                <Select
                                                    value={updateForm.string_pattern || undefined}
                                                    onChange={val => setUpdateForm(prev => ({ ...prev, string_pattern: val || '' }))}
                                                    getPopupContainer={() => document.body}
                                                    size="large"
                                                    style={{ width: '100%' }}
                                                    allowClear
                                                    showSearch
                                                    disabled={!updateModalEditing}
                                                    placeholder="Select a pattern"
                                                    options={stringPatterns.map(p => ({ value: p.name, label: p.name }))}
                                                    notFoundContent="No patterns found — add one in Static Info"
                                                    filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                                />
                                            </Form.Item>
                                        </div>
                                        <Form.Item label={<span>String Machine{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ marginBottom: 12 }}>
                                            <Select
                                                value={updateForm.string_machine || undefined}
                                                onChange={val => setUpdateForm(prev => ({ ...prev, string_machine: val || '' }))}
                                                getPopupContainer={() => document.body}
                                                size="large"
                                                style={{ width: '100%' }}
                                                allowClear
                                                showSearch
                                                disabled={!updateModalEditing}
                                                placeholder="Select a machine"
                                                options={stringMachines.map(m => ({ value: m.name, label: m.name }))}
                                                notFoundContent="No machines — add in Static Info"
                                            />
                                        </Form.Item>
                                        {updateForm.status !== 'Pending to String' && (
                                            <Form.Item label={<span>Stringer Name{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ marginBottom: 12 }}>
                                                <Select
                                                    value={updateForm.stringer_name || undefined}
                                                    onChange={val => setUpdateForm(prev => ({ ...prev, stringer_name: val || '' }))}
                                                    getPopupContainer={() => document.body}
                                                    size="large"
                                                    style={{ width: '100%' }}
                                                    allowClear
                                                    showSearch
                                                    disabled={!updateModalEditing}
                                                    placeholder="Select a stringer"
                                                    options={stringers.map(s => ({ value: s.name, label: s.name }))}
                                                    notFoundContent="No stringers found — add one in Stringer Details"
                                                />
                                            </Form.Item>
                                        )}
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                            <Form.Item label="Fees (SGD)" style={{ marginBottom: 0 }}>
                                                <Input
                                                    size="large"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={updateForm.fees}
                                                    onChange={e => setUpdateForm(prev => ({ ...prev, fees: e.target.value }))}
                                                    disabled={!updateModalEditing}
                                                />
                                            </Form.Item>
                                            <Form.Item label={<span>DT{['Ready to Collect', 'Collected'].includes(updateForm.status) && <span style={{ color: '#c41e3a' }}> *</span>}</span>} style={{ marginBottom: 0 }}>
                                                <Input
                                                    size="large"
                                                    value={updateForm.dt}
                                                    onChange={e => setUpdateForm(prev => ({ ...prev, dt: e.target.value }))}
                                                    disabled={!updateModalEditing}
                                                />
                                            </Form.Item>
                                        </div>
                                        <Form.Item label="Collection Date" style={{ marginBottom: 12 }}>
                                            <Input
                                                size="large"
                                                type="date"
                                                min={todayDateStr()}
                                                value={updateForm.collection_date}
                                                onChange={e => setUpdateForm(prev => ({ ...prev, collection_date: e.target.value }))}
                                                disabled={!updateModalEditing}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Remarks" style={{ marginBottom: 10 }}>
                                            <Input.TextArea
                                                size="large"
                                                rows={2}
                                                value={updateForm.remarks}
                                                onChange={e => setUpdateForm(prev => ({ ...prev, remarks: e.target.value }))}
                                                disabled={!updateModalEditing}
                                                placeholder="Optional notes about this order"
                                            />
                                        </Form.Item>
                                    </Form>

                                    {updateForm.status === 'Pending to String' && (
                                        <div className="assign-stringer-section">
                                            <div className="assign-stringer-label">Assign to Stringer</div>
                                            <Select
                                                value={updateForm.stringer_name || undefined}
                                                onChange={val => setUpdateForm(prev => ({ ...prev, stringer_name: val || '' }))}
                                                getPopupContainer={() => document.body}
                                                size="large"
                                                style={{ width: '100%' }}
                                                placeholder="Select a stringer"
                                                disabled={!updateModalEditing}
                                                options={stringers.map(s => ({ value: s.name, label: s.name }))}
                                                notFoundContent="No stringers found — add one in Stringer Details"
                                            />
                                        </div>
                                    )}

                                    <div className="update-order-actions">
                                        <Button danger size="large" onClick={handleDeleteFromModal} style={{ flex: 1 }}>
                                            Delete
                                        </Button>
                                        <Button type="primary" size="large" loading={updateSaving} onClick={handleUpdateSubmit} style={{ flex: 2 }}>
                                            Save Changes
                                        </Button>
                                    </div>

                                </ConfigProvider>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
